//home
import React, { useState, useEffect, useRef, useMemo } from 'react';
import Layout from "../../../Component/Layout";
import AsyncSelect from "react-select/async";
import { ToastContainer, toast, Zoom } from "react-toastify";
import Bd from "./Bd";
import CharacteristicsModal from '../../../Component/CharacteristicsModal ';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { ORDER_API, MASTER_API } from "../../../apiConfig";
import { useLocation, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import sbeeLogo from "../../../assets/sbeelogo.png";
// Tolerance range calc is owned by the Product Configuration master
// (it defines the Over/Under Tolerances characteristics + Tolerance
// Checkbox), so RFQ just imports the shared function from there instead
// of keeping its own separate copy of the formula.
import { calculateToleranceRange } from "../../../ProcurementManagement/ProductManagement/ProductConfiguration";
import { useFieldPermissions } from "../../../utils/useFieldPermissions";
import { authHeaders } from "../../../utils/authHeaders";
import MaskedValue from "../../../Component/MaskedValue";
import { FileText } from 'lucide-react';

// --- Payment terms are now managed via Product Configuration → Payment
//     Terms (see ProductMaster.js / configuration.service.js: "payment-terms"
//     type) instead of being hardcoded here. This component fetches them at
//     runtime — see the paymentTermsOptions state + useEffect below.
//     NOTE: QuotationView.js previously imported PAYMENT_TERMS_OPTIONS from
//     this file for the printed Terms & Conditions table — that needs to be
//     switched to fetch from the same API, or receive the list as a prop.

// --- Matches free-text payment terms (e.g. from a customer's business_partner
//     record) against the configured standard options. Used only to detect
//     whether the auto-filled value is one of the standard ones or a custom
//     free-text value coming from the customer record. ---
const matchPaymentTerm = (text, options = []) => {
    if (!text) return "";
    const trimmed = String(text).trim().toLowerCase();
    const match = options.find(opt => opt.trim().toLowerCase() === trimmed);
    return match || String(text).trim();
};

// 🆕 This screen's own Module / Process / Sub Process — must exactly match
// what was picked in the User Manual upload screen (case-insensitive,
// trimmed) for the manual to be found.
const USER_MANUAL_SCOPE = {
    moduleName: "Order Management",
    process: "Sales",
    subProcess: "Quotation"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

// --- Shared dropdown option/menu styling (matches Product Master / Enquiry dropdown look) ---
const dropdownOptionStyle = {
    control: (base) => ({
        ...base,
        minHeight: 32,
        height: 32,
        fontSize: '13px',
        borderColor: '#dee2e6',
        boxShadow: 'none',
        '&:hover': { borderColor: '#b3b3b3' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: '32px', display: 'flex', alignItems: 'center' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    menu: (base) => ({ ...base, fontSize: '13px', zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : 'white',
        color: state.isSelected ? 'white' : '#334155',
        cursor: 'pointer',
        fontSize: '13px'
    })
};

// Reads module-level permissions (can_view / can_create) for the given
// module_id from the logged-in user's stored permissions, the same array
// authController.js returns at login. Admins get an empty `permissions`
// array from the backend by design, so they're granted full access here
// rather than being locked out by an "empty permissions" default.
const getModulePermissions = (moduleId) => {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) return { canView: false, canCreate: false };
        if (user.role === "admin") return { canView: true, canCreate: true };

        const perm = (user.permissions || []).find((p) => p.module_id === moduleId);
        return {
            canView: perm?.can_view === 1,
            canCreate: perm?.can_create === 1
        };
    } catch (e) {
        console.error("Error reading user permissions:", e);
        return { canView: false, canCreate: false };
    }
};

function Home() {
    // Role-based field visibility for this module. When the logged-in
    // user's role is Display-only, isFieldVisible(field_id) tells us
    // whether to show the real value or a masked placeholder in view mode.
    const { isFieldVisible, isFieldEditable, isDisplayOnly, isReviewOnly, isAdmin, canApprove } = useFieldPermissions("SALES_QUOTATION");

    // Module-level access for this page. CREATE-type roles are add-only
    // (canView false, canCreate true) — they should see the create form
    // but not the existing-records list. DISPLAY-type roles are the
    // opposite (canView true, canCreate false) — they can browse/view but
    // the Create button and any mutating actions (delete) should be hidden.
    const { canView, canCreate } = useMemo(() => getModulePermissions("SALES_QUOTATION"), []);

    const [loading, setLoading] = useState(false);
    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const [manualFile, setManualFile] = useState(null);
    const [page, setPage] = useState(1);
    const [rfqSummary, setRfqSummary] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState("create");
    const [activeTab, setActiveTab] = useState("basic");
    const [salesData, setSalesData] = useState([]);
    const [selectedRfq, setSelectedRfq] = useState(null);

    // A Review-only role can't land on "basic" (it's not in their tab list) —
    // push them onto whichever review tab actually applies once we know the
    // record's status. Runs whenever the modal opens on a new record.
    useEffect(() => {
        if (!isReviewOnly || modalMode === 'create') return;
        const draftStatuses = ['Draft', 'Created', ''];
        const status = selectedRfq?.rfq_status || '';
        if (status.includes('Customer')) {
            setActiveTab('feasibility_customer');
        } else if (!draftStatuses.includes(status)) {
            setActiveTab('feasibility');
        }
    }, [isReviewOnly, modalMode, selectedRfq]);
    const [discounts, setDiscounts] = useState([]);
    // 🆕 Spl. Price — a new editable base price that sits beside Unit Price.
    // Starts out equal to Unit Price, but the user can override it per row.
    // Markup (%) and Discount (%) now compute off Spl. Price instead of the
    // raw Unit Price (which stays untouched as a reference value). Mirrors
    // the `discounts` array pattern: index-aligned with salesData, used only
    // in Create mode — Edit/View mode keeps its own value directly on
    // row.spl_price (same pattern row.discount/row.markup already use).
    const [splPrices, setSplPrices] = useState([]);
    // Text currently being typed in a row's Spl. Price cell ({ idx, text } | null).
    // The cell shows a CALCULATED value (base + markup - discount), so while the
    // user is typing we show exactly what they typed instead of the round-tripped
    // figure — otherwise decimals/trailing zeros get eaten mid-keystroke.
    const [splEdit, setSplEdit] = useState(null);
    useEffect(() => { setSplEdit(null); }, [showModal, modalMode]);
    const [submitComment, setSubmitComment] = useState("");
    const [salesFile, setSalesFile] = useState(null);
    //eslint-disable-next-line
    const [uploadMessage, setUploadMessage] = useState("");
    const [supportingFiles, setSupportingFiles] = useState([]);
    //eslint-disable-next-line
    const [isSalesFileUploaded, setIsSalesFileUploaded] = useState(false);
    const selectedCharsRef = useRef([]);
    const salesFileInputRef = useRef(null);
    const docFileInputRef = useRef(null);
    const [showExcelModal, setShowExcelModal] = useState(false);
    const [excelUploading, setExcelUploading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [rfqToDelete, setRfqToDelete] = useState(null);
    // RFQ Validity — "which RFQs expired" (Configuration → RFQ Validity sets
    // rfq_id -> validity days; quotation.service.js's getSummary() attaches
    // is_validity_expired to each row here based on that config). Surfaced
    // via the navbar bell's badge/dropdown, not on this page.
    const [unit1Location, setUnit1Location] = useState(null); // from Configuration > Location Master (Unit 1 / Regd. Office) — used for GTP "Name & Address of the manufacturer"
    const [docFiles, setDocFiles] = useState([]);
    const [gtpDocs, setGtpDocs] = useState([]);
    // Payment Terms options — now fetched from Product Configuration
    // (Payment Terms master) instead of being hardcoded here.
    const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
    const [showGtpPreview, setShowGtpPreview] = useState(null); // holds gtp doc object or null
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [selectedRefRfq, setSelectedRefRfq] = useState(null);   // Reference RFQ for create mode
    const [refRfqList, setRefRfqList] = useState([]);              // Dropdown list
    const [showProductModal, setShowProductModal] = useState(false);
    const [productDetails, setProductDetails] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedCharacteristics, setSelectedCharacteristics] = useState([]);
    const [modalIndex, setModalIndex] = useState(null);
    const [productList, setProductList] = useState([]);
    const [showQuotationModal, setShowQuotationModal] =
        useState(false);


    const [approvedDate, setApprovedDate] = useState("");  // ADD
    // --- GLOBAL FILTER STATES ---
    const [globalFilters, setGlobalFilters] = useState({
        construction: [], code: [], cableType: [], category: [],
        noOfCores: [], color: [], nominalCrossSection: [], referenceOfStandard: [], voltage: []
    });
    const [filteredProductList, setFilteredProductList] = useState(null);
    // --- FILTER DROPDOWN MASTER DATA ---
    const [filterMasterData, setFilterMasterData] = useState({
        constructions: [], codes: [], cableTypes: [], categories: [],
        cores: [], colours: [], nominalCrossSections: [], referenceOdStandards: [], voltages: []
    });
    // --- FORM STATE (Used in Modals) ---
    const [salesOrganization, setSalesOrganization] = useState("");
    const [customerSegment, setCustomerSegment] = useState("");
    const [dChannel, setDCHannel] = useState("");
    const [rfqId, setRfqId] = useState(""); // Form RFQ ID
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // --- FILTER STATE (Used for Main Table) ---
    const [filterSalesOrg, setFilterSalesOrg] = useState("");
    const [filterCustomerSegment, setFilterCustomerSegment] = useState("");
    const [filterDChannel, setFilterDChannel] = useState("");
    const [filterRfqId, setFilterRfqId] = useState(""); // Filter RFQ ID
    const [filterCustomer, setFilterCustomer] = useState(null);
    const [filterStatus, setFilterStatus] = useState("");
    const [filterCreationDateFrom, setFilterCreationDateFrom] = useState(null);
    const [filterCreationDateTo, setFilterCreationDateTo] = useState(null);
    // Set when arriving here from the navbar bell — shows only the
    // validity-expired RFQs (the ones already highlighted red in the
    // table) with every other filter cleared/ignored.
    const [showExpiredOnly, setShowExpiredOnly] = useState(false);
    const [customerSegmentList, setCustomerSegmentList] = useState([]);
    const [actionType, setActionType] = useState("");
    const [salesOrgList, setSalesOrgList] = useState([]);
    const [channelList, setChannelList] = useState([]);
    const [changeLogs, setChangeLogs] = useState([]);
    const [paymentTerms, setPaymentTerms] = useState("");
    const [currency, setCurrency] = useState("INR");
    const [markup, setMarkup] = useState("");
    const [customerInspection, setCustomerInspection] = useState("No");
    const [validityNumber, setValidityNumber] = useState("");
    const [validityUnit, setValidityUnit] = useState("days");
    const [validityDate, setValidityDate] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [datasheetParagraph, setDatasheetParagraph] = useState("");
    const [inquiryDropdownList, setInquiryDropdownList] = useState([]);
    const [selectedInquiryId, setSelectedInquiryId] = useState(null);
    // User-typed "Quotation Rev No." — free-text field, editable in create
    // and edit mode at any time. Separate from the system's auto rev_no.
    const [quotationRevNo, setQuotationRevNo] = useState("");
    const [createdBy, setCreatedBy] = useState("");
    const [createdDate, setCreatedDate] = useState("");
    const [approvedBy, setApprovedBy] = useState("");

    // 🆕 EXPORT — mirrors enquiry.js's isExportEnquiry flow. The selected
    // Enquiry ID's own enquiry_type drives this (Quotation has no Enquiry
    // Type field of its own); when it's "Export" we show + auto-fill the
    // Customer Country field from what was captured on that Enquiry.
    const [quotationEnquiryType, setQuotationEnquiryType] = useState("");
    const isExportQuotation = String(quotationEnquiryType || "").trim().toLowerCase() === "export";
    const [customerCountry, setCustomerCountry] = useState("");
    const [exchangeRate, setExchangeRate] = useState("");
    const [countryOptions, setCountryOptions] = useState([]);

    // ── Stop scroll-wheel from changing number inputs ──
    // Browsers increment/decrement a focused <input type="number"> when the
    // user scrolls over it — Validity, Markup (%), Discount (%), Qty, Unit
    // Price, etc. all have this problem. Blurring the field the instant a
    // wheel event fires (while it's focused) stops the value from changing,
    // without needing to touch every individual input.
    useEffect(() => {
        const loadUnit1Location = async () => {
            try {
                const res = await fetch(`${MASTER_API}/location-master`, { headers: authHeaders() });
                const data = await res.json();
                const locations = Array.isArray(data) ? data : [];
                const unit1 = locations.find((loc) => (loc.location_label || "").toLowerCase().includes("1")) || null;
                setUnit1Location(unit1);
            } catch (err) {
                console.error("LOCATION MASTER FETCH ERROR", err);
                setUnit1Location(null);
            }
        };
        loadUnit1Location();
    }, []);

    // "Name & Address of the manufacturer" for the GTP datasheet — always Unit 1
    // (Regd. Office), sourced from Configuration > Location Master. Falls back
    // to the static text only if Location Master hasn't been set up yet.
    const manufacturerName = unit1Location?.company_name || "SBEE Cables (India) Limited";
    const manufacturerAddress = unit1Location
        ? `${manufacturerName}, ${unit1Location.address_line1}${unit1Location.address_line2 ? ", " + unit1Location.address_line2 : ""}, ${unit1Location.city}${unit1Location.pin_code ? " - " + unit1Location.pin_code : ""}, ${unit1Location.state}`
        : "SBEE Cables (India) Limited, Plot No. -282 & 283, 2nd Phase KIADB Industrial Area, Harohalli Village, Kanakapura Taluk, Ramanagar District";

    useEffect(() => {
        const handleWheel = (e) => {
            const active = document.activeElement;
            if (active && active.tagName === 'INPUT' && active.type === 'number') {
                active.blur();
            }
        };
        document.addEventListener('wheel', handleWheel, { passive: true });
        return () => document.removeEventListener('wheel', handleWheel);
    }, []);

    // ── Live markup-on-unit-price calculation ──
    // Markup is now applied on Unit Price (producing a new, marked-up unit price)
    // instead of on Net/Discounted Price. Every downstream figure (Total Price,
    // Discounted Price, Net Price, GST, Grand Total) is derived from that new
    // unit price. Recalculated live via useEffect whenever markup, the line
    // items, or discounts change.
    const [liveRowCalcs, setLiveRowCalcs] = useState([]);
    // 🆕 taxByType/taxColumns replace the fixed cgst/sgst/igst totals — see
    // the useEffect below, which mirrors SalesOrder.js's dynamic tax column
    // resolution (union of every tax type actually configured across all
    // lines, CGST/SGST/IGST pinned first for a normal domestic quotation).
    const [liveTotals, setLiveTotals] = useState({
        gross: 0, discounted: 0, net: 0, taxByType: {}, taxColumns: [], totalGst: 0, grand: 0
    });

    const location = useLocation();
    const navigate = useNavigate();

    // Helper to safely format API data into {label, value} for react-select
    const getFilterOptions = (list) => {
        if (!list || !Array.isArray(list)) return [];
        return list.map(item => {
            if (typeof item === 'string') return { value: item, label: item };
            const val = item.name || item.code || item.cable_type || item.category || item.core || item.colour || item.value || "";
            return { value: String(val).trim(), label: String(val).trim() };
        }).filter(opt => opt.value !== "");
    };

    useEffect(() => {
        fetchSummary();
        fetchProductList();
        fetchMasterData();
        // Enquiry ID list: full list loaded in handleOpenCreate so it can be
        // picked directly; re-scoped to a customer's enquiries via
        // handleCustomerChange once a Customer is selected.
        fetchPaymentTermsOptions();
    }, []);

    const fetchPaymentTermsOptions = async () => {
        try {
            const res = await fetch(`${MASTER_API}/configuration/dynamic/payment-terms`, { headers: authHeaders() });
            if (!res.ok) return;
            const data = await res.json();
            setPaymentTermsOptions((data || []).map(row => row.name).filter(Boolean));
        } catch (err) {
            console.error("Failed to fetch payment terms options:", err);
        }
    };

    useEffect(() => {
        selectedCharsRef.current = selectedCharacteristics;
    }, [selectedCharacteristics]);

    // ── Live recalculation: Markup on Unit Price ──
    // Whenever markup, the line items, or discounts change, recompute a
    // marked-up unit price per row (unitPrice + markup%) and derive Total
    // Price, Discounted Price, Net Price, GST, and Grand Total from that new
    // unit price. This mirrors the calculation previously done inline during
    // render, but centralizes it so the "apply markup on unit price" rule is
    // defined in exactly one place and stays live as any dependency changes.
    useEffect(() => {
        const tableData = modalMode === 'create' ? salesData : (selectedRfq?.productDetails || []);
        const markupNum = parseFloat(markup || 0);

        const rows = tableData.map((row, idx) => {
            const quantity = parseFloat(row.quantity || row.Quantity || 0);

            // Markup (%) can now be overridden per product row. Each row
            // uses its OWN markup value if one has been typed in, and
            // falls back to the header Markup (%) field otherwise. Changing
            // the header field re-broadcasts its value into every row (see
            // the header Markup (%) onChange handler), so this fallback
            // mainly matters for the brief moment before that broadcast
            // lands / for rows that have never had their markup touched.
            const rowMarkupNum = (row.markup !== undefined && row.markup !== null && row.markup !== "")
                ? (parseFloat(row.markup) || 0)
                : markupNum;

            // 🆕 Spl. Price is now the base Markup (%) applies to — Unit
            // Price stays a plain, untouched reference value.
            let newUnitPrice;
            let splBase;
            if (modalMode === 'create') {
                // Fresh rows from the sales Excel — unit_price/Standard_Price is
                // the raw reference price. Spl. Price starts out equal to it,
                // but the user can override it per row via the splPrices array
                // (mirrors the `discounts` array pattern).
                const rawUnitPrice = parseFloat(row.unit_price || row.Standard_Price || 0);
                const splStr = splPrices[idx];
                splBase = (splStr !== undefined && splStr !== null && splStr !== "")
                    ? (parseFloat(splStr) || 0)
                    : rawUnitPrice;
                newUnitPrice = splBase + (splBase * rowMarkupNum) / 100;
            } else {
                // Existing RFQ — row.spl_price is the saved base price this
                // row's markup applies to (persisted directly, so it never
                // needs to be reverse-engineered from a marked-up figure).
                //
                // A row saved before Spl. Price existed has no row.spl_price
                // yet — for that legacy case only, fall back to reversing the
                // markup baked into row.unit_price (the FINAL, marked-up
                // price loaded from the DB) using the frozen
                // original_unit_price / original_markup baseline (see
                // withOriginalPricing above), the same reversal this
                // calculation used to always do for Unit Price.
                const rowSplPrice = row.spl_price;
                if (rowSplPrice !== undefined && rowSplPrice !== null && rowSplPrice !== "") {
                    splBase = parseFloat(rowSplPrice) || 0;
                } else {
                    const baselineUnitPrice = parseFloat(
                        row.original_unit_price !== undefined && row.original_unit_price !== null && row.original_unit_price !== ""
                            ? row.original_unit_price
                            : row.unit_price || 0
                    );
                    const baselineMarkup = parseFloat(
                        row.original_markup !== undefined && row.original_markup !== null && row.original_markup !== ""
                            ? row.original_markup
                            : 0
                    );
                    splBase = baselineUnitPrice > 0 ? baselineUnitPrice / (1 + baselineMarkup / 100) : baselineUnitPrice;
                }
                newUnitPrice = splBase + (splBase * rowMarkupNum) / 100;
            }

            // Total Price is now based on the new unit price
            const grossPrice = newUnitPrice * quantity;

            const discStr = modalMode === 'create' ? (discounts[idx] ?? "") : row.discount;
            const discountVal = parseFloat(discStr) || 0;

            const discountedPriceRow = grossPrice - (discountVal * grossPrice) / 100;
            // Markup is already baked into the unit price, so Net Price is
            // simply the discounted price (no extra markup step here anymore).
            const displayNetPrice = discountedPriceRow;

            // 🆕 FULLY DYNAMIC per-row tax breakdown — one { type: amount }
            // entry for every tax type actually configured for THIS line's
            // HSN (via row.taxes: [{type, percent, value}], see
            // quotation.service.js's getGstByHsn / getRFQById /
            // getLineItemById), instead of an assumed CGST/SGST/IGST split.
            // A row saved before quotation_tax existed (no row.taxes yet)
            // falls back to synthesizing from its legacy cgst/sgst/igst
            // amount columns so old data still renders.
            const backendNet = parseFloat(row.net_price_after_discount || 0);
            const rowTaxes = (Array.isArray(row.taxes) && row.taxes.length)
                ? row.taxes
                : (row.cgst_amount !== undefined && row.cgst_amount !== null && row.cgst_amount !== ''
                    ? [
                        { type: "CGST", percent: parseFloat(row.cgst) || 0, value: parseFloat(row.cgst_amount) || 0 },
                        { type: "SGST", percent: parseFloat(row.sgst) || 0, value: parseFloat(row.sgst_amount) || 0 },
                        { type: "IGST", percent: parseFloat(row.igst) || 0, value: parseFloat(row.igst_amount) || 0 },
                    ]
                    : []
                );

            const taxBreakdown = {};
            rowTaxes.forEach((t) => {
                const type = String(t.type || "").trim();
                if (!type) return;
                const pct = parseFloat(t.percent) || 0;
                // Rate implied by the last-saved amount (held constant through
                // the live preview, same idea as the old cgstRate/sgstRate),
                // falling back to the configured percent directly for a
                // brand-new row that hasn't been saved yet (backendNet is 0).
                const rate = backendNet > 0 ? ((parseFloat(t.value) || 0) / backendNet) : (pct / 100);
                taxBreakdown[type] = (taxBreakdown[type] || 0) + (displayNetPrice * rate);
            });

            const totalGstRow = Object.values(taxBreakdown).reduce((s, v) => s + v, 0);
            const grandTotalRow = displayNetPrice + totalGstRow;

            // Spl. Price shown in the grid = the price ONE unit actually ends up at
            // after Markup (%) and Discount (%): base + markup, minus discount.
            // (Net Price = this x Qty.) The saved spl_price stays the pre-markup base.
            const finalUnitPrice = newUnitPrice - (newUnitPrice * discountVal) / 100;

            return {
                newUnitPrice,
                finalUnitPrice,
                rowMarkupNum,
                discountVal,
                splBase,
                grossPrice,
                discountedPriceRow,
                displayNetPrice,
                taxBreakdown,
                totalGstRow,
                grandTotalRow
            };
        });

        // 🆕 Union of every tax type actually configured across all lines —
        // decides how many/which tax columns the grid draws. CGST/SGST/IGST
        // pinned first (so a normal domestic quotation still looks exactly
        // like it always did); anything else (Export duties, Cess, etc.) is
        // appended in the order it's first seen. Mirrors SalesOrder.js.
        const PRIORITY_TAX_ORDER = ["CGST", "SGST", "IGST"];
        const taxColumnSet = new Set();
        rows.forEach((r) => {
            Object.keys(r.taxBreakdown || {}).forEach((t) => taxColumnSet.add(t));
        });
        const dynamicTaxColumns = [
            ...PRIORITY_TAX_ORDER.filter(t => taxColumnSet.has(t)),
            ...[...taxColumnSet].filter(t => !PRIORITY_TAX_ORDER.includes(t))
        ];
        // Nothing resolved anywhere yet (brand-new quotation, no HSN
        // matched) — show the historical CGST/SGST/IGST headings as the
        // empty default rather than a tax section with zero columns.
        const effectiveTaxColumns = dynamicTaxColumns.length ? dynamicTaxColumns : PRIORITY_TAX_ORDER;

        // Once the RFQ is in a customer-approved view stage, only approved
        // rows are shown in the table (see the Product tab render below) —
        // the TOTAL row needs to sum the same subset, or it'll disagree
        // with what's actually visible.
        const isCustomerApprovedStage = modalMode === 'view'
            && /Approved by Customer/i.test(selectedRfq?.rfq_status || '');
        const rowsForTotals = isCustomerApprovedStage
            ? rows.filter((_, i) => (tableData[i]?.customer_approval_status || '').trim().toLowerCase() === 'approved')
            : rows;

        const newTotals = rowsForTotals.reduce((acc, r) => {
            const taxByType = { ...acc.taxByType };
            effectiveTaxColumns.forEach((t) => {
                taxByType[t] = (taxByType[t] || 0) + (r.taxBreakdown[t] || 0);
            });
            return {
                gross: acc.gross + r.grossPrice,
                discounted: acc.discounted + r.discountedPriceRow,
                net: acc.net + r.displayNetPrice,
                taxByType,
                totalGst: acc.totalGst + r.totalGstRow,
                grand: acc.grand + r.grandTotalRow
            };
        }, { gross: 0, discounted: 0, net: 0, taxByType: {}, totalGst: 0, grand: 0 });

        setLiveRowCalcs(rows);
        setLiveTotals({ ...newTotals, taxColumns: effectiveTaxColumns });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markup, salesData, selectedRfq, discounts, splPrices, modalMode]);

    // ── Discount Review rows ──
    // A line item needs Discount Review whenever its Discount (%) exceeds
    // its EFFECTIVE Markup (%) — the row's own Markup (%) if it has been
    // overridden, otherwise the header Markup (%) field.
    const discountReviewRows = useMemo(() => {
        const tableData = modalMode === 'create' ? salesData : (selectedRfq?.productDetails || []);
        const markupNum = parseFloat(markup || 0);

        return tableData
            .map((row, idx) => {
                const rowMarkupNum = (row.markup !== undefined && row.markup !== null && row.markup !== "")
                    ? (parseFloat(row.markup) || 0)
                    : markupNum;

                const discStr = modalMode === 'create' ? (discounts[idx] ?? "") : row.discount;
                const discountVal = parseFloat(discStr) || 0;

                return { row, idx, discountVal, rowMarkupNum };
            })
            .filter(({ discountVal, rowMarkupNum }) => discountVal > rowMarkupNum);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markup, salesData, selectedRfq, discounts, modalMode]);

    // Discount Review only becomes relevant once the RFQ has actually been
    // submitted — before that (create mode, or still Draft/Created) there's
    // no submitted line data to review yet. Also admin-only.
    const draftStatuses = ['Draft', 'Created', ''];
    const isRfqSubmitted = modalMode !== 'create' && !draftStatuses.includes(selectedRfq?.rfq_status || '');
    const isDiscountReviewVisible = canApprove && isRfqSubmitted && discountReviewRows.length > 0;

    // If the underlying conditions change out from under an open Discount
    // Review tab (e.g. the flagged rows get cleared elsewhere), fall back
    // to the Basic tab rather than leaving the user on a tab with no
    // button and no content.
    useEffect(() => {
        if (activeTab === "discount_review" && !isDiscountReviewVisible) {
            setActiveTab("basic");
        }
    }, [activeTab, isDiscountReviewVisible]);

    useEffect(() => {
        const applyAutoRange = (rows, isCreate) => {
            let changed = false;
            const next = rows.map(row => {
                const chars = row.Characteristics || row.characteristics || [];
                if (!chars.length) return row;

                const fallbackQty = parseFloat(row.quantity || row.Quantity) || 0;
                const { rangeText, newQty } = computeRangeAndQty(chars, fallbackQty);
                const currentRange = row.range || "";
                const qtyShouldUpdate = newQty > 0 && Math.abs(newQty - fallbackQty) > 0.001;

                if (rangeText === currentRange && !qtyShouldUpdate) return row;

                changed = true;
                const updatedRow = { ...row, range: rangeText };
                if (qtyShouldUpdate) {
                    if (isCreate) {
                        updatedRow.Quantity = newQty;
                        updatedRow.quantity = newQty;
                    } else {
                        updatedRow.quantity = parseFloat(newQty.toFixed(2));
                    }
                }
                return updatedRow;
            });
            return changed ? next : null;
        };

        if (modalMode === 'create') {
            const next = applyAutoRange(salesData, true);
            if (next) setSalesData(next);
        } else if (selectedRfq?.productDetails) {
            const next = applyAutoRange(selectedRfq.productDetails, false);
            if (next) setSelectedRfq(prev => ({ ...prev, productDetails: next }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salesData, selectedRfq, modalMode]);

    // Re-fetch the cascading filter dropdown data any time a filter
    // selection changes (including on mount, when globalFilters is the
    // default all-empty object) — same pattern as Enquiry, so every field
    // stays aware of every other field's current selection.
    useEffect(() => {
        fetchProductFilterOptions(globalFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalFilters]);

    // --- Turn the currently-selected globalFilters into a query string ---
    // Identical helper to Enquiry's buildFilterQuery.
    const buildFilterQuery = (filters) => {
        const params = new URLSearchParams();
        Object.entries(filters || {}).forEach(([key, selectedOpts]) => {
            if (Array.isArray(selectedOpts) && selectedOpts.length > 0) {
                const values = selectedOpts.map(o => (o && o.value !== undefined) ? o.value : o);
                params.set(key, values.join(","));
            }
        });
        return params.toString();
    };

    // Construction / Code / Color / etc. — calls the SAME backend endpoint
    // logic as Enquiry (quotation's /product-filters route just passes
    // through to enquiry.service.getProductFilterOptions). No separate
    // filter implementation for Quotation.
    const fetchProductFilterOptions = async (filters = globalFilters) => {
        try {
            const qs = buildFilterQuery(filters);
            const res = await fetch(`${ORDER_API}/quotation/product-filters${qs ? `?${qs}` : ""}`, { headers: authHeaders() });
            if (!res.ok) return;
            const data = await res.json();
            setFilterMasterData(data);
        } catch (err) {
            console.error("Error fetching product filter options:", err);
        }
    };

    // Enquiry ID can be picked directly (no Customer required first): with
    // no customerCode this loads the full Enquiry ID list. Passing a
    // customer's partner_code re-scopes the list to just that customer's
    // enquiries (used once a Customer has been chosen, so the two stay
    // in sync either direction).
    const fetchInquiryList = async (customerCode) => {
        try {
            const qs = customerCode ? `?customer=${encodeURIComponent(customerCode)}` : "";
            const res = await fetch(`${ORDER_API}/quotation/inquiry-list${qs}`, { headers: authHeaders() });
            if (res.ok) {
                const data = await res.json();
                setInquiryDropdownList(data);
            }
        } catch (err) { console.error(err); }
    };

    // ── Reset everything that Reference RFQ / Enquiry ID auto-fill ──
    // Both handleRefRfqSelect and handleInquirySelect populate the same set
    // of header fields + product rows. Previously, clearing the dropdown
    // (clicking the "x") just returned early and left all that copied data
    // sitting in the form — so Sales Org, Customer, Payment Terms, Validity,
    // Currency, and the whole product table stayed populated even after the
    // reference was unselected. This puts the Create form back to the same
    // blank state handleOpenCreate starts it in.
    const resetCreateFormFields = () => {
        setSalesOrganization("");
        setCustomerSegment("");
        setDCHannel("");
        setMarkup("");
        setCustomerInspection("No");
        setPaymentTerms("");
        setCurrency("INR");
        setValidityNumber("");
        setValidityUnit("days");
        setValidityDate(null);
        setSelectedCustomer(null);
        setSalesData([]);
        setDiscounts([]);
        setIsSalesFileUploaded(false);
        // 🆕 EXPORT — clear Customer Country / enquiry-type tracking along
        // with everything else this reset already covers.
        setQuotationEnquiryType("");
        setCustomerCountry("");
        setExchangeRate("");
        // Enquiry ID list is customer-scoped — with no customer selected
        // anymore, reload the full unscoped list.
        fetchInquiryList();
    };

    const handleInquirySelect = async (selectedOption) => {
        setSelectedInquiryId(selectedOption);
        if (!selectedOption) {
            // Only reset if nothing is still driving these fields via a
            // Reference RFQ selection — if one is active, leave its data alone.
            if (!selectedRefRfq) {
                resetCreateFormFields();
            }
            return;
        }

        try {
            const res = await fetch(`${ORDER_API}/quotation/fetch-inquiry-for-rfq/${selectedOption.value}`, { headers: authHeaders() });
            const data = await res.json();

            if (res.ok) {
                if (data.header.salesOrganization) setSalesOrganization(data.header.salesOrganization);
                if (data.header.customerSegment) setCustomerSegment(data.header.customerSegment);
                if (data.header.dChannel) setDCHannel(data.header.dChannel);
                // FIX: populate currency from enquiry header
                if (data.header.currency) setCurrency(data.header.currency);

                // 🆕 EXPORT — if the selected Enquiry's type is "Export",
                // auto-select Customer Country with the value captured on
                // that Enquiry (Currency above already carries the export
                // currency through, same as Enquiry does for Export lines).
                setQuotationEnquiryType(data.header.enquiryType || "");
                const isExportInquiry = String(data.header.enquiryType || "").trim().toLowerCase() === "export";
                setCustomerCountry(isExportInquiry ? (data.header.customerCountry || "") : "");
                setExchangeRate(isExportInquiry ? (data.header.exchangeRate || "") : "");
                // Payment terms already resolved from business_partner on the backend —
                // set it directly and do NOT call handleCustomerChange (which would overwrite it).
                if (data.header.paymentTerms) setPaymentTerms(matchPaymentTerm(data.header.paymentTerms, paymentTermsOptions));

                if (data.header.customer) {
                    const custOption = { label: data.header.customer, value: data.header.customer };
                    setSelectedCustomer(custOption);
                    // Note: intentionally NOT calling handleCustomerChange here so that
                    // the payment terms fetched from business_partner above are preserved.
                    // Still re-scope the Enquiry ID dropdown to this customer so it
                    // stays in sync with the Customer field.
                    fetchInquiryList(data.header.customer);
                }

                if (data.validProducts.length > 0) {
                    // FIX: map Currency field from each product row so it is available in salesData
                    const mappedProducts = data.validProducts.map(p => ({
                        ...p,
                        currency: p.Currency || data.header.currency || "",
                        Currency: p.Currency || data.header.currency || "",
                    }));
                    setSalesData(mappedProducts);

                    const mappedDiscounts = mappedProducts.map(p => p.Discount || 0);
                    setDiscounts(mappedDiscounts);
                    // Spl. Price starts equal to Unit Price for freshly loaded
                    // Enquiry products — left blank so the live-calc fallback
                    // (splPrices[idx] ?? rawUnitPrice) picks it up automatically.
                    setSplPrices(mappedProducts.map(() => ''));

                    toast.success(`Enquiry loaded. ${mappedProducts.length} valid products mapped.`);
                } else {
                    toast.warning("Enquiry loaded, but no matching master products found.");
                }
            }
        } catch (err) {
            toast.error("Failed to load Enquiry Data");
        }
    };

    // ================= REFERENCE RFQ POPULATE =================
    const handleRefRfqSelect = async (selectedOption) => {
        setSelectedRefRfq(selectedOption);
        if (!selectedOption) {
            resetCreateFormFields();
            setSelectedInquiryId(null);
            return;
        }
        try {
            const res = await fetch(`${ORDER_API}/quotation/${selectedOption.value}`, { headers: authHeaders() });
            const data = await res.json();
            if (!res.ok) { toast.error("Failed to load reference RFQ"); return; }

            // Populate all header fields
            setSalesOrganization(data.sales_organization || "");
            setCustomerSegment(data.customer_segment || "");
            setDCHannel(data.distribution_channel || "");
            // Same header_markup fix as handleOpenView — see comment there.
            setMarkup((data.header_markup !== undefined && data.header_markup !== null && data.header_markup !== "")
                ? data.header_markup
                : (data.markup || ""));
            setCustomerInspection(data.customer_inspection || "No");

            if (data.payment_terms) {
                setPaymentTerms(matchPaymentTerm(data.payment_terms, paymentTermsOptions));
            } else {
                setPaymentTerms("");
            }
            if (data.validity_terms) {
                const match = data.validity_terms.match(/^(\d+)\s*(.*)$/);
                setValidityNumber(match ? match[1] : data.validity_terms);
                setValidityUnit(match && match[2] ? match[2].toLowerCase() : "days");
            } else {
                setValidityNumber(""); setValidityUnit("days");
            }
            if (data.validity_date) {
                // Read the calendar date straight off the string instead of
                // round-tripping through `new Date()` — that's what was
                // causing the date to shift back a day in some timezones.
                const clean = String(data.validity_date).substring(0, 10);
                const [yyyy, mm, dd] = clean.split('-');
                setValidityDate((yyyy && mm && dd) ? `${yyyy}-${mm}-${dd}` : null);
            } else {
                setValidityDate(null);
            }

            // Populate customer — set directly without calling handleCustomerChange so that
            // payment_terms already set from rfq.payment_terms above is not overwritten.
            if (data.customer) {
                const custOpt = { label: data.customer, value: data.customer };
                setSelectedCustomer(custOpt);
                fetchInquiryList(data.customer);
            }

            // Helper: safely parse a date without timezone shift (IST fix).
            // Never construct a `new Date()` from the value — just read the
            // calendar digits directly off the string.
            const safeDateLocal = (val) => {
                if (!val) return '';
                if (val instanceof Date) {
                    if (isNaN(val.getTime())) return '';
                    const yyyy = val.getFullYear();
                    const mm = String(val.getMonth() + 1).padStart(2, '0');
                    const dd = String(val.getDate()).padStart(2, '0');
                    return `${yyyy}-${mm}-${dd}`;
                }
                const clean = String(val).substring(0, 10);
                return /^\d{4}-\d{2}-\d{2}$/.test(clean) ? clean : '';
            };

            // Populate products — map from view format to create format
            if (data.productDetails && data.productDetails.length > 0) {
                const mappedProducts = data.productDetails.map((p, i) => {
                    // p.unit_price coming from a saved RFQ is the FINAL
                    // (already marked-up) price — same as the DB comment in
                    // the live-calc effect explains. Create mode's live
                    // markup calc, however, treats Standard_Price/unit_price
                    // as the RAW pre-markup price and adds Markup(%) on top
                    // of it. Left as-is, that meant every RFQ created "from"
                    // a previous one re-applied markup on an already
                    // marked-up price — and doing that again from the new
                    // RFQ compounded it further each time. Reverse this
                    // row's markup here so we hand Create mode the base
                    // price, not the marked-up one.
                    const lineMarkup = parseFloat(
                        (p.markup !== undefined && p.markup !== null && p.markup !== "")
                            ? p.markup
                            : (data.header_markup || data.markup || 0)
                    ) || 0;
                    const finalUnitPrice = Number(p.unit_price || 0);
                    const baseUnitPrice = lineMarkup
                        ? finalUnitPrice / (1 + lineMarkup / 100)
                        : finalUnitPrice;

                    // Spl. Price: if the source RFQ was saved under the new
                    // Spl. Price system, carry its base price forward as-is.
                    // Otherwise (older RFQ, no spl_price yet) fall back to
                    // the same reversed base price used for Standard_Price/
                    // unit_price above.
                    const baseSplPrice = (p.spl_price !== undefined && p.spl_price !== null && p.spl_price !== "")
                        ? Number(p.spl_price)
                        : baseUnitPrice;

                    return {
                        Item: i + 1,
                        product_code: p.product_code || '',
                        "Product Code": p.product_code || '',
                        Description: p.description || '',
                        UOM: p.uom || '',
                        Standard_Price: baseUnitPrice,
                        unit_price: baseUnitPrice,
                        spl_price: baseSplPrice,
                        Product_Cost: Number(p.unit_cost || 0),
                        unit_cost: Number(p.unit_cost || 0),
                        Product_Class: p.product_class || '',
                        Quantity: Number(p.quantity || 0),
                        quantity: Number(p.quantity || 0),
                        Requirement_Day: safeDateLocal(p.requirement_date),
                        requirement_date: safeDateLocal(p.requirement_date),
                        Min_Order_Qty: p.minimum_order_quantity ?? '',
                        minimum_order_quantity: p.minimum_order_quantity ?? '',
                        Discount: Number(p.discount || 0),
                        Currency: p.currency || p.Currency || data.currency || '',
                        currency: p.currency || p.Currency || data.currency || '',
                        Characteristics: p.characteristics || [],
                        characteristics: p.characteristics || [],
                        // Markup (%) is header-only now — the new quotation's
                        // header Markup (%) field (set above from
                        // data.header_markup) governs every line, so no
                        // per-row value is carried forward here.
                        markup: "",
                    };
                });
                setSalesData(mappedProducts);
                setDiscounts(mappedProducts.map(p => p.Discount || 0));
                setSplPrices(mappedProducts.map(p => p.spl_price ?? ''));
                setIsSalesFileUploaded(true);
            } else {
                setSalesData([]); setDiscounts([]); setSplPrices([]); setIsSalesFileUploaded(false);
            }

            toast.success(`Reference RFQ ${selectedOption.value} loaded!`);
        } catch (err) {
            console.error("handleRefRfqSelect error:", err);
            toast.error("Failed to load reference RFQ data");
        }
    };

    const fetchSummary = async () => {
        try {
            const res = await fetch(`${ORDER_API}/quotation/summary`, { headers: authHeaders() });
            const data = await res.json();
            const arr = Array.isArray(data) ? data : data.data || [];
            setRfqSummary(arr);
            // Build reference RFQ dropdown list from summary (unique rfq_ids)
            const seen = new Set();
            const refList = arr
                .filter(r => { if (seen.has(r.rfq_id)) return false; seen.add(r.rfq_id); return true; })
                .map(r => ({ value: r.rfq_id, label: `${r.rfq_id} — ${r.customer || ''}` }));
            setRefRfqList(refList);
            // Any RFQ whose configured validity (Configuration → RFQ Validity)
            // has passed is now surfaced via the navbar bell's badge/dropdown
            // instead of an auto-popping modal on this page.
        } catch (err) {
            console.error("Failed to load RFQ summary:", err);
        }
    };

    const handleDeleteRFQ = (rfqId) => {
        setRfqToDelete(rfqId);
        setShowDeleteConfirm(true);
    };

    const confirmDeleteRFQ = async () => {
        const rfqId = rfqToDelete;
        setShowDeleteConfirm(false);
        if (!rfqId) return;

        try {
            const res = await fetch(`${ORDER_API}/quotation/delete/${rfqId}`,{ headers: authHeaders(), method: "PUT" });
            if (!res.ok) throw new Error("Failed to delete");
            toast.success("RFQ deleted successfully");
            fetchSummary();
        } catch (err) {
            toast.error("Failed to delete RFQ");
        } finally {
            setRfqToDelete(null);
        }
    };

    const fetchMasterData = async () => {
        try {
            const resOrg = await fetch(`${MASTER_API}/configuration/sales-organization`, { headers: authHeaders() });
            const resSeg = await fetch(`${MASTER_API}/configuration/dynamic/customer-segment`, { headers: authHeaders() });
            const resChn = await fetch(`${MASTER_API}/configuration/distribution-channel`, { headers: authHeaders() });

            if (resOrg.ok) setSalesOrgList(await resOrg.json());
            if (resSeg.ok) setCustomerSegmentList(await resSeg.json());
            if (resChn.ok) setChannelList(await resChn.json());

            // 🆕 Country master (for Export Enquiry's Customer Country field) —
            // same config source as enquiry.js's Export enquiry field.
            fetch(`${MASTER_API}/configuration/country`, { headers: authHeaders() })
                .then(r => r.json())
                .then(d => {
                    const raw = d.data || d || [];
                    setCountryOptions(Array.isArray(raw) ? raw : []);
                })
                .catch(err => console.error("Error fetching country list:", err));

            // NOTE: Category/Cable Type/Code/Color/Cores/Cross Section/Std Ref/
            // Voltage/Construction dropdown data is intentionally NOT fetched
            // here anymore. It used to come from the generic, unscoped
            // /configuration/* endpoints (all product categories in the
            // system — Bobbin, Boxes, Compound, etc.), and since those fetches
            // resolved after fetchProductFilterOptions() on mount, they
            // clobbered the correct Finished-Goods-scoped filterMasterData
            // with the unscoped one. That data now comes exclusively from
            // fetchProductFilterOptions() (/quotation/product-filters),
            // which is already scoped to Finished Goods.
        } catch (err) {
            console.error("Error fetching master data:", err);
        }
    };

    const getCurrentUser = () => {
        // Try full user object first (login stores: { email, role, token, ... })
        const userObj = localStorage.getItem("user");
        if (userObj) {
            try {
                const parsedUser = JSON.parse(userObj);
                // Prefer the full name (first + last) so Created By / Updated By
                // show a real name; fall back to the combined name, then user id,
                // then email for accounts with no name saved yet.
                const fullName = `${parsedUser.firstName || ''} ${parsedUser.lastName || ''}`.trim();
                if (fullName) return fullName;
                if (parsedUser.name && parsedUser.name.trim()) return parsedUser.name.trim();
                if (parsedUser.userId) return parsedUser.userId;
                if (parsedUser.email) return parsedUser.email;
            } catch (e) { console.error("Error parsing user data:", e); }
        }
        // Also check if token is stored separately and email is stored flat
        const storedUserId = localStorage.getItem("userId");
        if (storedUserId && storedUserId !== "undefined" && storedUserId !== "null") return storedUserId;
        const email = localStorage.getItem("email");
        if (email && email !== "undefined" && email !== "null") return email;
        const storedName = localStorage.getItem("userName");
        if (storedName && storedName !== "undefined" && storedName !== "null") return storedName;
        return "User";
    };

    const dropdownOptions = useMemo(() => {
        const getUniqueIds = () => [...new Set(rfqSummary.map(item => item.rfq_id).filter(Boolean))];
        const getUniqueStatuses = () => [...new Set(rfqSummary.map(item => item.rfq_status).filter(Boolean))];

        return {
            salesOrgs: Array.isArray(salesOrgList) ? salesOrgList : [],
            customerSegments: Array.isArray(customerSegmentList) ? customerSegmentList : [],
            channels: Array.isArray(channelList) ? channelList : [],
            rfqIds: getUniqueIds(),
            statuses: getUniqueStatuses()
        };
    }, [rfqSummary, salesOrgList, customerSegmentList, channelList]);

    // Newest-first ordering: primarily by created_at/creation_date timestamp;
    // if that's missing or ties, fall back to comparing rfq_id strings, since
    // RFQ IDs embed a yyyymmddhhmmss-style timestamp and sort correctly as text.
    const getRfqTime = (item) => {
        const raw = item.created_at || item.creation_date;
        const t = raw ? new Date(raw).getTime() : NaN;
        return isNaN(t) ? 0 : t;
    };

    // "YYYY-MM-DD" form for range comparisons — kept separate from
    // formatDateLocal() above, which returns "DD-MM-YYYY" for display.
    const toIsoDateStr = (dateInput) => {
        if (!dateInput) return "";
        if (dateInput instanceof Date) {
            if (isNaN(dateInput.getTime())) return "";
            const year = dateInput.getFullYear();
            const month = String(dateInput.getMonth() + 1).padStart(2, '0');
            const day = String(dateInput.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
        return String(dateInput).substring(0, 10);
    };

    const filteredData = useMemo(() => {
        const fromStr = filterCreationDateFrom ? toIsoDateStr(filterCreationDateFrom) : "";
        const toStr = filterCreationDateTo ? toIsoDateStr(filterCreationDateTo) : "";

        return rfqSummary
            .filter((item) => {
                const itemDateStr = item.created_at ? toIsoDateStr(item.created_at) : "";
                return (
                    (!showExpiredOnly || item.is_validity_expired) &&
                    (filterSalesOrg === "" || item.sales_organization === filterSalesOrg) &&
                    (filterCustomerSegment === "" || item.customer_segment === filterCustomerSegment) &&
                    (filterDChannel === "" || item.distribution_channel === filterDChannel) &&
                    (filterCustomer === null || item.customer === filterCustomer.label || (item.customer && item.customer.includes(filterCustomer.label))) &&
                    (filterRfqId === "" || item.rfq_id.toString().includes(filterRfqId)) &&
                    (filterStatus === "" || item.rfq_status === filterStatus) &&
                    (fromStr === "" || (itemDateStr && itemDateStr >= fromStr)) &&
                    (toStr === "" || (itemDateStr && itemDateStr <= toStr))
                );
            })
            .sort((a, b) => {
                const diff = getRfqTime(b) - getRfqTime(a);
                if (diff !== 0) return diff;
                return (b.rfq_id || '').toString().localeCompare((a.rfq_id || '').toString());
            });
    }, [rfqSummary, showExpiredOnly, filterSalesOrg, filterCustomerSegment, filterDChannel, filterCustomer, filterRfqId, filterStatus, filterCreationDateFrom, filterCreationDateTo]);

    const limit = 10;
    const totalPages = Math.max(1, Math.ceil(filteredData.length / limit));

    const paginatedData = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredData.slice(start, start + limit);
    }, [filteredData, page]);

    // Distinct RFQs flagged expired by the backend are surfaced via the
    // navbar bell (see Navbar.js), not computed on this page.

    useEffect(() => {
        setPage(1);
    }, [filterSalesOrg, filterCustomerSegment, filterDChannel, filterCustomer, filterRfqId, filterStatus, filterCreationDateFrom, filterCreationDateTo]);

    const fetchProductList = async () => {
        try {
            const res = await fetch(`${ORDER_API}/quotation/fetch-product-list`, { headers: authHeaders() });
            const data = await res.json();

            const productArray = Array.isArray(data)
                ? data
                : data.products || data.data || [];

            const formatted = productArray.map(item => ({
                value: item.product_code || item.Product_Code || item.value || '',
                label: (() => {
                    const code = item.product_code || item.Product_Code || item.value || '';
                    const desc = item.description || item.Description || '';
                    return desc ? `${code} - ${desc}` : code;
                })(),
                description: item.description || item.Description || '',
                uom: item.uom || item.UOM || '',
                product_class: item.product_class || item.Product_Class || '',
                // --- FILTER FIELDS ---
                construction: item.construction || item.Construction || '',
                code: item.code || item.Code || '',
                cableType: item.cableType || item.cable_type || item.CableType || item.Cable_Type || '',
                category: item.productCategory || item.product_category || item.category || item.Category || '',
                noOfCores: item.noOfCores || item.no_of_cores || item.NoOfCores || item.No_Of_Cores || item.cores || item.Cores || '',
                color: item.color || item.Color || item.colour || item.Colour || '',
                nominalCrossSection: item.nominalCrossSection || item.nominal_cross_section || item.NominalCrossSection || item.Nominal_Cross_Section || '',
                referenceOfStandard: item.referenceOfStandard || item.reference_of_standard || item.ReferenceOfStandard || item.Reference_Of_Standard || item.referenceOdStandard || '',
                voltage: item.voltage || item.Voltage || '',
            }));

            setProductList(formatted);

        } catch (error) {
            console.error("Error fetching product list", error);
        }
    };

    const loadCustomerOptions = async (inputValue) => {
        try {
            const res = await fetch(`${ORDER_API}/quotation/customers`, { headers: authHeaders() })
            const data = await res.json();

            const options = data.map(c => ({
                value: c.partner_code,
                label: `${c.partner_code} - ${c.vendor_name}`,
            }));

            if (!inputValue) return options;

            const search = inputValue.toLowerCase();

            return options
                .filter(opt => opt.label.toLowerCase().includes(search))
                .sort((a, b) => {
                    const labelA = a.label.toLowerCase();
                    const labelB = b.label.toLowerCase();
                    const nameA = labelA.split(' - ')[1] || "";
                    const nameB = labelB.split(' - ')[1] || "";
                    const aStarts = labelA.startsWith(search) || nameA.startsWith(search);
                    const bStarts = labelB.startsWith(search) || nameB.startsWith(search);

                    if (aStarts && !bStarts) return -1;
                    if (!aStarts && bStarts) return 1;
                    return 0;
                });
        } catch (err) {
            return [];
        }
    };

    const handleCustomerChange = async (selectedOption) => {
        setSelectedCustomer(selectedOption);

        // Enquiry ID is linked to Customer: whenever the customer changes,
        // re-scope the Enquiry ID dropdown to that customer's enquiries and
        // drop any previously selected Enquiry ID (it likely belonged to a
        // different customer).
        setSelectedInquiryId(null);
        fetchInquiryList(selectedOption ? selectedOption.value : null);

        if (selectedOption) {
            try {
                const res = await fetch(`${ORDER_API}/quotation/customer-details/${selectedOption.value}`, { headers: authHeaders() });
                const data = await res.json();

                // Fetch payment terms from terms_and_conditions column — only
                // auto-select it if it matches one of the 12 standard options
                // exactly, so the dropdown actually shows the selection.
                const terms = data.terms_and_conditions || data.term_and_conditions || data.payment_terms || "";
                setPaymentTerms(matchPaymentTerm(terms, paymentTermsOptions));
            } catch (err) {
                console.error("Error fetching customer details:", err);
                setPaymentTerms("");
            }
        } else {
            setPaymentTerms("");
        }
    };


    const clearGlobalFilters = () => {
        setGlobalFilters({
            construction: [], code: [], cableType: [], category: [],
            noOfCores: [], color: [], nominalCrossSection: [], referenceOfStandard: [], voltage: []
        });
        setFilteredProductList(null);
    };
    // ----------------------------

    const handleOpenCreate = () => {
        setModalMode("create");
        setSalesData([]);
        setRfqId("");
        setSelectedCustomer(null);
        setSalesOrganization("");
        setCustomerSegment("");
        setDCHannel("");
        setSalesFile(null);
        setUploadMessage("");
        setSupportingFiles([]);
        setDocFiles([]);
        // gtpDocs is only ever populated by handleOpenView (from whichever
        // quotation was last opened) — it was never cleared here, so a stale
        // GTP datasheet from a previously-viewed quotation kept showing up
        // in the Docs tab of a brand-new Create form.
        setGtpDocs([]);
        setIsSalesFileUploaded(false);
        setActiveTab("basic");
        setShowModal(true);
        setPaymentTerms("");
        setCurrency("INR");
        setMarkup("");
        setCustomerInspection("No");
        setValidityNumber("");
        setValidityUnit("days");
        setValidityDate(null);
        setSelectedInquiryId(null);
        setQuotationRevNo("");
        // Load the full Enquiry ID list up front (not customer-scoped) so the
        // dropdown is usable directly on open — the user can pick Customer
        // first OR Enquiry ID first. Picking an Enquiry ID auto-fills the
        // Customer (see handleInquirySelect) and re-scopes this list to it.
        fetchInquiryList();
        setSelectedRefRfq(null);
        setApprovedBy("");
        setApprovedDate("");  // ADD
        // Use getCurrentUser() — NOT raw localStorage.getItem() calls. Those were
        // pulling whichever key existed first even if its value was the literal
        // string "undefined" (e.g. from an earlier `localStorage.setItem("userName", undefined)`
        // elsewhere), which is truthy and so passed straight through the || chain.
        // getCurrentUser() explicitly checks for and rejects "undefined"/"null" strings.
        setCreatedBy(getCurrentUser());
        setCreatedDate(formatDateLocal(new Date()));
        setApprovedBy("");
    };

    // const handleDiscountChange = (index, newValue) => {
    //     setDiscounts(prevDiscounts => {
    //         const updatedDiscounts = [...prevDiscounts];
    //         updatedDiscounts[index] = newValue;
    //         return updatedDiscounts;
    //     });
    // };

    // const liveTotal = useMemo(() => {
    //     return salesData.reduce((acc, item, index) => {
    //         const currentDiscount = Number(discounts[index]) || 0;

    //         // Adjust this math to your specific billing logic (e.g., percentage vs flat rate)
    //         const itemTotal = (item.price * item.quantity) - currentDiscount;

    //         return acc + itemTotal;
    //     }, 0);
    // }, [salesData, discounts]); // React recalculates immediately when either dependency changes

    // Freezes a baseline (original_unit_price / original_markup) onto every
    // row the moment RFQ data comes back from the server. This baseline is
    // what the live Markup(%) recalculation (see the useEffect above) uses
    // to re-derive Unit Price / Discounted Price / Net Price as the user
    // types — it must NOT be re-derived from row.unit_price/row.markup
    // after edits, since those fields get overwritten on every keystroke.
    const withOriginalPricing = (data) => {
        if (!data || !Array.isArray(data.productDetails)) return data;
        return {
            ...data,
            productDetails: data.productDetails.map(row => ({
                ...row,
                original_unit_price: parseFloat(row.unit_price || 0),
                // The markup that actually produced the currently stored
                // unit_price: this row's own legacy override if the DB still
                // has one (older data, from before Markup (%) became
                // header-only), otherwise the header_markup snapshot taken
                // the last time this row was saved. Needed to correctly
                // reverse out the old markup below — using 0 here would
                // treat an already-marked-up price as if it had never been
                // marked up, stacking the new header markup on top instead
                // of replacing the old one.
                original_markup: (row.markup !== null && row.markup !== undefined && row.markup !== '')
                    ? parseFloat(row.markup)
                    : parseFloat(row.header_markup || 0),
            }))
        };
    };

    // GTP datasheets should always be visible for whatever products are
    // currently on the quotation — Create, Edit, or View — with one
    // exception: once a specific product line has been rejected (internally
    // or by the customer), its GTP sheet is dropped. Centralized here so
    // every place products can change (add row, change product code, remove
    // row, load an existing RFQ) stays in sync via the useEffect below,
    // instead of each handler having to remember to refetch manually.
    const gtpEligibleCodes = (products) => {
        return Array.from(
            new Set(
                (products || [])
                    .filter(p => p.internal_action !== 'Rejected' && p.customer_approval_status !== 'Rejected')
                    .map(p => (p.product_code || p.productCode || p["Product Code"] || "").trim())
                    .filter(Boolean)
            )
        );
    };

    const refreshGtpDocs = async (products) => {
        const codes = gtpEligibleCodes(products);
        if (codes.length === 0) { setGtpDocs([]); return; }
        try {
            const gtpResults = await Promise.all(
                codes.map(async (code) => {
                    try {
                        const res = await fetch(`${MASTER_API}/gtp-configuration/by-product-code/${encodeURIComponent(code)}`, { headers: authHeaders() });
                        if (!res.ok) { console.warn(`GTP fetch failed for ${code}: HTTP ${res.status}`); return null; }
                        const gtp = await res.json();
                        if (!gtp || !gtp.gtpNumber) return null;
                        return gtp;
                    } catch {
                        return null;
                    }
                })
            );
            setGtpDocs(gtpResults.filter(Boolean));
        } catch (_) {
            setGtpDocs([]);
        }
    };

    // Keeps gtpDocs synced with whichever product list is actually live for
    // the current mode. This replaces the old one-shot fetch that only ran
    // in handleOpenView — that version left stale entries behind whenever
    // Create was opened afterward, or a product was added/removed/rejected
    // without the page fully reloading.
    useEffect(() => {
        if (!showModal) return;
        if (modalMode === 'create') {
            refreshGtpDocs(salesData);
        } else if ((modalMode === 'edit' || modalMode === 'view') && selectedRfq?.productDetails) {
            refreshGtpDocs(selectedRfq.productDetails);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal, modalMode, salesData, selectedRfq?.productDetails]);

    const handleOpenView = async (id) => {
        try {
            const res = await fetch(`${ORDER_API}/quotation/${id}`, { headers: authHeaders() });
            const data = await res.json();

            setSelectedRfq(withOriginalPricing(data));
            setRfqId(data.rfq_id);
            setSelectedInquiryId(data.linked_inquiry_id ? { label: data.linked_inquiry_id, value: data.linked_inquiry_id } : null);
            setQuotationRevNo(data.quotation_rev_no || "");
            fetchLogs(id);
            setSalesOrganization(data.sales_organization || "");
            setCustomerSegment(data.customer_segment || "");
            setDCHannel(data.distribution_channel || "");
            setSelectedCustomer({ label: data.customer, value: data.customer });
            setCreatedBy(data.created_by || "");
            setCreatedDate(
                data.creation_date
                    ? formatDateLocal(data.creation_date)
                    : data.created_at
                        ? formatDateLocal(data.created_at)
                        : ""
            );
            setApprovedBy(data.internal_action_by || data.approved_by || "");
            setApprovedDate(
                data.approved_at
                    ? formatDateLocal(data.approved_at)
                    : ""
            );


            if (data.productDetails) {
                const mappedData = data.productDetails.map(item => ({
                    ...item,
                    "Standard_Price": item.unit_price,
                    "Product_Cost": item.unit_cost,
                    "Product Code": item.product_code,
                    "Requirement_Day": item.requirement_date,
                    "requirement_date": item.requirement_date,   // ✅ ADD THIS LINE
                    "Min_Order_Qty": item.minimum_order_quantity ?? '',
                    "minimum_order_quantity": item.minimum_order_quantity ?? '',
                    // Backend returns 0 (not blank) when a row was saved without
                    // its own markup. Normalize that to "" so the live-calc
                    // fallback (row.markup === "" -> use header Markup (%))
                    // actually kicks in instead of getting stuck at 0 forever.
                    "markup": (item.markup === null || item.markup === undefined || item.markup === '' || parseFloat(item.markup) === 0) ? "" : item.markup
                }));

                setSalesData(mappedData);
                const currentDiscounts = data.productDetails.map(p => p.discount || 0);
                setDiscounts(currentDiscounts);
                setSplPrices(data.productDetails.map(p => p.spl_price ?? ''));
            }

            if (data.payment_terms) {
                setPaymentTerms(data.payment_terms);
            } else {
                setPaymentTerms("");
            }
            setCurrency(data.currency || data.Currency || "INR");
            // Header Markup (%) must come from header_markup (the true,
            // persisted header default), not data.markup — data.markup is
            // just the FIRST product row's own effective markup, which can
            // legitimately differ from the header default whenever that
            // row carries its own per-row override. Reading it directly
            // used to make the header box silently show that row's value
            // instead of the real header setting.
            setMarkup((data.header_markup !== undefined && data.header_markup !== null && data.header_markup !== "")
                ? data.header_markup
                : (data.markup || ""));
            setCustomerInspection(data.customer_inspection || "No");
            if (data.validity_terms) {
                const match = data.validity_terms.match(/^(\d+)\s*(.*)$/);
                setValidityNumber(match ? match[1] : data.validity_terms);
                setValidityUnit(match && match[2] ? match[2].toLowerCase() : "days");
            } else {
                setValidityNumber("");
                setValidityUnit("days");
            }
            if (data.validity_date) {
                // Read the calendar date straight off the string instead of
                // round-tripping through `new Date()` — that's what was
                // causing the date to shift back a day in some timezones.
                const clean = String(data.validity_date).substring(0, 10);
                const [yyyy, mm, dd] = clean.split('-');
                setValidityDate((yyyy && mm && dd) ? `${yyyy}-${mm}-${dd}` : null);
            } else {
                setValidityDate(null);
            }

            // Fetch uploaded docs for this RFQ
            try {
                const docsRes = await fetch(`${ORDER_API}/quotation/list-docs/${id}`, { headers: authHeaders() });
                const docsData = docsRes.ok ? await docsRes.json() : { files: [] };
                setDocFiles((docsData.files || []).map(f => ({
                    name: f.originalname,
                    filename: f.filename,
                    googleFileId: f.googleFileId,
                    size: f.size,
                    type: f.mimetype || "",
                    uploadedAt: f.uploadedAt || "",
                    rfqId: id
                })));
            } catch (_) { setDocFiles([]); }

            // GTP docs are now kept in sync automatically by the
            // gtpEligibleCodes/refreshGtpDocs useEffect above, which fires as
            // soon as selectedRfq.productDetails is set below — no need to
            // fetch them separately here.

            setModalMode("view");
            setActiveTab("basic");
            setShowModal(true);
        } catch (error) {
            console.error("Error fetching RFQ details:", error);
            toast.error("Failed to load RFQ data");
        }
    };

    const fetchLogs = async (id) => {
        try {
            const res = await fetch(`${ORDER_API}/quotation/logs/${id}`, { headers: authHeaders() });
            if (res.ok) {
                const data = await res.json();
                setChangeLogs(data);
            }
        } catch (err) {
            console.error("Error fetching logs:", err);
            toast.error("Failed to load history");
        }
    };

    const handleSwitchToEdit = () => {
        // Defense in depth — button is already hidden for a converted
        // quotation, but block here too in case this is ever reached some
        // other way. Real enforcement is server-side in updateRFQ.
        if (selectedRfq?.is_converted_to_so) {
            toast.info(`Already converted to Sales Order ${selectedRfq?.converted_so_id}. Editing is not allowed.`, { autoClose: 3000, hideProgressBar: true });
            return;
        }
        setModalMode("edit");
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setModalMode("create");
        // Also clear on close so the next modal open (Create or View) never
        // inherits GTP docs from whatever was open before.
        setGtpDocs([]);

        // Check if we were sent here from another screen
        if (location.state?.returnTo) {
            navigate(location.state.returnTo);
        }
    };

    useEffect(() => {
        if (location.state?.openSoId) {
            handleOpenView(location.state.openSoId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state?.openSoId]);

    // Arrived here from the navbar bell — clear every filter and switch
    // the table into "expired validity only" mode. Keyed off location.key
    // (unique per navigation) rather than the showExpiredOnly value itself,
    // since the bell always passes the same literal `true` — depending on
    // the value meant this only fired the very first time, not on repeat
    // bell clicks once showExpiredOnly had already been true/reset before.
    useEffect(() => {
        if (location.state?.showExpiredOnly) {
            setShowExpiredOnly(true);
            setFilterSalesOrg("");
            setFilterCustomerSegment("");
            setFilterDChannel("");
            setFilterCustomer(null);
            setFilterRfqId("");
            setFilterStatus("");
            setFilterCreationDateFrom(null);
            setFilterCreationDateTo(null);
            // Consume the navigation flag so it doesn't live on in browser
            // history — otherwise a plain refresh replays this same state
            // and re-applies the expired-only filter forever, with no way
            // to land back on a normal, unfiltered page load.
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    // Pulls the latest RFQ (including each line item's internal_action /
    // internal_action_comment, customer_approval_comment, etc.) from the
    // server and merges it into selectedRfq + change logs, WITHOUT closing
    // the modal or navigating away. Shared by the view-mode refetch effect
    // below and by Bd.js's onDecisionSubmitted callback (Approve/Reject/
    // Query in the Review tabs) — that used to just call
    // window.location.reload(), which dumped the user back to the list
    // and hid the very comment/reason they'd just entered.
    const refreshSelectedRfq = async () => {
        if (!rfqId) return;
        try {
            const res = await fetch(`${ORDER_API}/quotation/${rfqId}`, { headers: authHeaders() });
            if (res.ok) {
                const data = await res.json();
                if (data) setSelectedRfq(withOriginalPricing(data));
            }
        } catch (err) {
            console.error("Failed to refresh RFQ status:", err);
        }
        fetchLogs(rfqId);
    };

    // Re-fetch RFQ from server whenever modal returns to view mode
    // so selectedRfq always reflects the real backend status (no hardcoding)
    useEffect(() => {
        if (modalMode === 'view' && rfqId) {
            refreshSelectedRfq();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalMode]);

    const handleSalesFileSave = async () => {
        if (!salesFile) { toast.error("Please select a sales file."); return; }
        const formData = new FormData();
        formData.append("file", salesFile);
        setExcelUploading(true);

        try {
            const res = await fetch(`${ORDER_API}/quotation/parse-sales`,{ headers: authHeaders(), method: "POST", body: formData });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();

            const normalized = (data.data || []).map(row => {
                const code = row["product_code"] || row["Product Code"] || row["productCode"] || "";

                const chars = (row.Characteristics || []).map(c => {
                    if (c.characteristic === "Factor" && row["Factor"] !== undefined && row["Factor"] !== "") {
                        return { ...c, value: String(row["Factor"]) };
                    }
                    if (c.characteristic === "Length" && row["Length"] !== undefined && row["Length"] !== "") {
                        return { ...c, value: String(row["Length"]) };
                    }
                    return c;
                });

                return {
                    ...row,
                    product_code: code,
                    Characteristics: chars
                };
            });

            setUploadMessage("File parsed. Check Product tab.");
            setSalesData(normalized);
            setIsSalesFileUploaded(true);
            toast.success("File uploaded successfully!");
            setShowExcelModal(false);
            setSalesFile(null);
        } catch (err) {
            toast.error("Failed to parse file");
        } finally { setExcelUploading(false); }
    };

    const handleDownloadTemplate = () => {
        const a = document.createElement("a");
        a.href = "/assets/files/rfq_template.xlsx";
        a.download = "rfq_template.xlsx";
        a.click();
    };

    // --- Quotation document upload size limit (50 KB per file) ---
const MAX_QUOTATION_DOC_SIZE_BYTES = 50 * 1024;

const filterFilesWithinSizeLimit = (files) => {
    const okFiles = [];
    const rejected = [];
    files.forEach(f => {
        if (f.size > MAX_QUOTATION_DOC_SIZE_BYTES) {
            rejected.push(f.name);
        } else {
            okFiles.push(f);
        }
    });
    if (rejected.length) {
        toast.error(
            `${rejected.length} file(s) exceed the 50 KB upload limit and were skipped: ${rejected.join(', ')}`
        );
    }
    return okFiles;
};

const getFileIcon = (name = '', type = '') => {
    const ext = (name.split('.').pop() || '').toLowerCase();
    if (['pdf'].includes(ext)) return '📄';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return '🖼️';
    if (['zip', 'rar', '7z'].includes(ext)) return '🗜️';
    return '📎';
};

    // Req. Date is mandatory on every product line item — enquiry, quotation
    // and sales order all enforce this the same way. Row shape differs between
    // create mode (Requirement_Day) and edit mode (requirement_date), so check
    // both keys.
    const validateReqDatesFilled = (rows) => {
        const missingRows = rows
            .map((r, i) => ({ ok: !!(r.Requirement_Day || r.requirement_date), item: i + 1 }))
            .filter(r => !r.ok);
        if (missingRows.length > 0) {
            toast.error(
                missingRows.length === rows.length
                    ? "Req. Date is mandatory for all line items."
                    : `Req. Date is mandatory. Missing for item(s): ${missingRows.map(r => r.item).join(", ")}.`,
                { transition: Zoom }
            );
            return false;
        }
        return true;
    };

    const handleCreateRFQId = async () => {

        if (!salesOrganization) {
            toast.error("RFQ ID cannot be generated: Sales Organization is not selected.", { transition: Zoom });
            return;
        }

        if (!customerSegment) {
            toast.error("RFQ ID cannot be generated: Customer Segment is not selected.", { transition: Zoom });
            return;
        }

        if (!dChannel) {
            toast.error("RFQ ID cannot be generated: Distribution Channel is not selected.", { transition: Zoom });
            return;
        }

        if (!selectedCustomer) {
            toast.error("RFQ ID cannot be generated: Customer is not selected.", { transition: Zoom });
            return;
        }

        if (!validityNumber || !String(validityNumber).trim()) {
            toast.error("RFQ ID cannot be generated: Validity is required.", { transition: Zoom });
            return;
        }

        if (!paymentTerms || !String(paymentTerms).trim()) {
            toast.error("RFQ ID cannot be generated: Payment Terms is required.", { transition: Zoom });
            return;
        }

        if (!salesData || salesData.length === 0) {
            toast.error("You must add at least one product before generating an RFQ ID.", { transition: Zoom });
            return;
        }

        // ================= VALIDATION =================

        for (let i = 0; i < salesData.length; i++) {

            const row = salesData[i];
            const rowNum = i + 1;

            if (!row.Requirement_Day) {
                toast.error(`Row ${rowNum}: Requirement Date is missing.`, { transition: Zoom });
                return;
            }

            const chars = row.Characteristics || row.characteristics || [];

            const qtyBasedChars = chars.filter(c => {
                if (!c.calculation_type) return false;
                return c.calculation_type
                    .split(",")
                    .map(s => s.trim())
                    .includes("Quantity Based Calculation");
            });

            // Only validate qty-based fields if any are configured for this product
            if (qtyBasedChars.length > 0) {
                for (const c of qtyBasedChars) {
                    if (!parseFloat(c.value) || parseFloat(c.value) <= 0) {
                        toast.error(`Row ${rowNum}: "${c.characteristic}" is missing or zero.`, { transition: Zoom });
                        return;
                    }
                }
            }

        }

        // Only the overall Total Price must be > 0. Individual lines may have a
        // 0/blank Unit Price; the quotation is blocked only if the TOTAL is 0.
        if (!(parseFloat(liveTotals?.net) > 0)) {
            toast.error("RFQ ID cannot be generated: Total Price must be greater than 0.", { transition: Zoom });
            return;
        }

        // ================= CREATE RFQ =================
        // The RFQ ID is no longer built here. quotation.createDraft
        // generates it server-side (prefix comes from Configuration →
        // ID Creation, module "RFQ") inside its DB transaction, and
        // returns it in the response below — that's what actually
        // guarantees uniqueness, not a pre-fetch from the client.

        setLoading(true);

        try {

            // ================= PAYLOAD =================

            const payloadData = salesData.map((row, index) => ({

                ...row,

                customer: selectedCustomer?.label || "",

                Discount: discounts[index] || 0,

                Quantity: row.Quantity || 0,

                Unit_Price: row.Standard_Price || 0,

                // 🆕 Spl. Price — the base Markup (%) actually applies to.
                // Falls back to Unit Price if the user never touched it.
                SplPrice: (splPrices[index] !== undefined && splPrices[index] !== null && splPrices[index] !== "")
                    ? splPrices[index]
                    : (row.Standard_Price || 0),

                Unit_Cost: row.Product_Cost || 0,

                Product_Class: row.Product_Class || "",

                // ✅ IMPORTANT — map Requirement_Day to requirement_date
                requirement_date: row.Requirement_Day || row.requirement_date || null,

                minimum_order_quantity: row.Min_Order_Qty || row.minimum_order_quantity || null,

                // Each row now carries its own Markup (%) (defaulting to
                // the header value unless the user overrode it for this
                // row). The backend falls back to the header Markup (%)
                // field for any row that comes through blank.
                markup: (row.markup !== undefined && row.markup !== null) ? row.markup : "",

                // Over/Under Tolerances Range — calculated in handleSaveCharModal
                range: row.range || "",

                Characteristics: row.Characteristics || []
            }));

            console.log("PAYLOAD DATA =", payloadData);
            console.log("FIRST ROW requirement_date =", payloadData[0]?.requirement_date);  // ✅ DEBUG

            // ================= FORMDATA =================

            const formData = new FormData();

            const combinedValidity = validityNumber ? `${validityNumber} ${validityUnit}` : "";

            formData.append("payment_terms", paymentTerms || "");
            formData.append("currency", currency || "");
            formData.append("markup", markup || 0);
            formData.append("customer_inspection", customerInspection);
            formData.append("validity_terms", combinedValidity);
            formData.append("data", JSON.stringify(payloadData));
            formData.append("salesOrganization", salesOrganization);
            formData.append("customerSegment", customerSegment);
            formData.append("dChannel", dChannel);
            formData.append("customer", selectedCustomer?.label || "");
            formData.append("createdBy", getCurrentUser());
            formData.append("linkedInquiryId", selectedInquiryId ? selectedInquiryId.value : "");
            formData.append("quotationRevNo", quotationRevNo || "");

            // 🆕 Domestic vs Export + customer country — inherited from the
            // linked enquiry (quotationEnquiryType/customerCountry state,
            // set in the enquiry-fetch effect) but was never actually sent
            // to createDraft before, so every quotation persisted as
            // 'Domestic' with a null country regardless of the enquiry's
            // real type. Backend reads either orderType or quotationType.
            formData.append("orderType", quotationEnquiryType || "Domestic");
            formData.append("customerCountry", customerCountry || "");

            if (salesFile) {
                formData.append("salesFile", salesFile);
            }

            supportingFiles.forEach(file => {
                formData.append("supportingFiles", file);
            });

            // ================= API =================

            const res = await fetch(`${ORDER_API}/quotation/create-draft`,{ headers: authHeaders(), method: "POST",
                body: formData });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(errorText || "Failed to Create");
            }

            // ================= SUCCESS =================
            // generatedId now comes from the server's response — it's the
            // id that was actually committed to the rfq table.

            const result = await res.json();
            const generatedId = result?.rfqId;

            if (!generatedId) {
                throw new Error("Server did not return an RFQ ID.");
            }

            setRfqId(generatedId);

            // ✅ FIX — preserve requirement_date when updating salesData
            const updated = salesData.map(row => ({
                ...row,
                rfqId: generatedId,
                customer: selectedCustomer?.label || "",
                requirement_date: row.Requirement_Day || row.requirement_date || null,  // ✅ preserve
                minimum_order_quantity: row.Min_Order_Qty || row.minimum_order_quantity || null,
                markup: ''
            }));

            setSalesData(updated);

            setUploadMessage(`RFQ ID Generated: ${generatedId}`);

            toast.success(`RFQ ID ${generatedId} generated successfully!`, { transition: Zoom });

            fetchSummary();
            fetchLogs(generatedId);

        } catch (error) {
            console.error("Error in creating :", error);
            toast.error("Failed to generate RFQ ID and Creation. Please try again.", { transition: Zoom });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitNewRFQ = async () => {

        // ✅ Use rfqId state directly (works for both Create→Submit and Draft→Submit)
        const currentRfqId = rfqId || selectedRfq?.rfq_id;

        if (!currentRfqId) {
            toast.error("Please create the RFQ first before submitting.");
            return;
        }

        if (!validityNumber || !String(validityNumber).trim()) {
            toast.error("Validity is required.");
            return;
        }

        if (!paymentTerms || !String(paymentTerms).trim()) {
            toast.error("Payment Terms is required.");
            return;
        }

        setIsSubmitting(true);

        try {
            const res = await fetch(`${ORDER_API}/quotation/submit-status`,{
                method: "POST",
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    rfqId: currentRfqId,   // ✅ Fixed
                    comment: submitComment,
                    datasheetParagraph: datasheetParagraph,
                    updatedBy: getCurrentUser()
                })
            });

            const data = await res.json();

            if (!res.ok) throw new Error(data.message || "Submission failed");

            if (data.zipFile) {
                const fileUrl = `http://35.154.147.43:5000/uploads/${data.zipFile}`;
                const link = document.createElement('a');
                link.href = fileUrl;
                link.setAttribute('download', data.zipFile);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            toast.success(`RFQ ${currentRfqId} submitted for approval!`);
            setShowCommentModal(false);
            setSubmitComment("");
            setDatasheetParagraph("");
            handleCloseModal();
            fetchSummary();
            fetchLogs(currentRfqId);   // ✅ Fixed

        } catch (err) {
            toast.error("Failed to submit RFQ.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddRowCreate = () => {

        setSalesData(prev => [

            ...prev,

            {

                Item: prev.length + 1,

                product_code: '',

                Product_Class: '',

                Description: '',

                UOM: "",

                Standard_Price: '',

                Quantity: '',

                Product_Cost: '',

                Requirement_Day: '',
                Min_Order_Qty: '',
                minimum_order_quantity: '',

                Characteristics: []
            }
        ]);

        setDiscounts(prev => [...prev, '']);
        setSplPrices(prev => [...prev, '']);
    };

    const handleAddRowEdit = () => {
        // id is intentionally omitted — the backend uses absence of id to INSERT a new row
        const newRow = {
            product_code: '',
            description: '',
            product_class: '',
            uom: '',
            unit_cost: 0,
            unit_price: 0,
            spl_price: 0,
            quantity: 0,
            discount: 0,
            requirement_date: '',
            Requirement_Day: '',
            minimum_order_quantity: '',
            Min_Order_Qty: '',
            characteristics: [],
            Characteristics: []
        };
        setSelectedRfq(prev => ({ ...prev, productDetails: [...prev.productDetails, newRow] }));
    };

    const handleRemoveRow = (idx) => {
        if (modalMode === 'create') {
            setSalesData(prev => prev.filter((_, i) => i !== idx));
            setDiscounts(prev => prev.filter((_, i) => i !== idx));
            setSplPrices(prev => prev.filter((_, i) => i !== idx));
        } else if (modalMode === 'edit') {
            setSelectedRfq(prev => ({
                ...prev,
                productDetails: prev.productDetails.filter((_, i) => i !== idx)
            }));
        }
    };

    const handleProductCodeChangeCreate = async (code, index) => {

        if (!code) return;

        try {

            const res = await fetch(`${ORDER_API}/quotation/fetch-product-details?productCode=${code}`, { headers: authHeaders() });

            const response = await res.json();

            console.log("FULL API RESPONSE =", response);

            let p = null;

            // Handle different response structures
            if (Array.isArray(response)) {

                p = response[0];

            } else if (Array.isArray(response?.data)) {

                p = response.data[0];

            } else if (response?.data?.data) {

                p = Array.isArray(response.data.data)
                    ? response.data.data[0]
                    : response.data.data;

            } else if (response?.data) {

                p = response.data;
            }

            console.log("PRODUCT =", p);

            if (!p) {

                toast.error("Product details not found");

                return;
            }

            const productChars = p.Characteristics || p.characteristics || [];
            const { rangeText, newQty } = computeRangeAndQty(productChars, 0);

            setSalesData(prev => {

                const updated = [...prev];

                updated[index] = {

                    ...updated[index],

                    // Product Code
                    product_code: code,

                    // Description
                    Description:
                        p.Description ||
                        p.description ||
                        "",

                    description:
                        p.description ||
                        p.Description ||
                        "",

                    // Product Class
                    Product_Class:
                        p.Product_Class ||
                        p.product_class ||
                        "",

                    product_class:
                        p.product_class ||
                        p.Product_Class ||
                        "",

                    // UOM
                    UOM:
                        p.UOM ||
                        p.uom ||
                        "",

                    uom:
                        p.uom ||
                        p.UOM ||
                        "",

                    // Standard Price
                    Standard_Price:
                        Number(
                            p.Standard_Price ||
                            p.standard_price ||
                            0
                        ),

                    standard_price:
                        Number(
                            p.standard_price ||
                            p.Standard_Price ||
                            0
                        ),

                    
                    Quantity: newQty > 0 ? newQty : 0,

                    quantity: newQty > 0 ? newQty : 0,

                    range: rangeText,

                    // Product Cost
                    Product_Cost:
                        Number(
                            p.Product_Cost ||
                            p.product_cost ||
                            0
                        ),

                    unit_price:
                        Number(
                            p.standard_price ||
                            p.Standard_Price ||
                            0
                        ),

                    // Requirement Date
                    Requirement_Day:
                        updated[index]?.Requirement_Day || "",

                    // Minimum Order Quantity — auto-filled from the Product Master's MOQ
                    Min_Order_Qty:
                        p.Min_Order_Qty ??
                        p.minimum_order_quantity ??
                        p.moq ??
                        updated[index]?.Min_Order_Qty ??
                        "",

                    // Currency
                    Currency:
                        p.Currency ||
                        p.currency ||
                        "",

                    currency:
                        p.currency ||
                        p.Currency ||
                        "",

                    // Characteristics
                    Characteristics:
                        p.Characteristics ||
                        p.characteristics ||
                        []
                };

                return updated;
            });

            // Reset this row's Spl. Price to the newly picked product's
            // price, same as Standard_Price/unit_price above — the user can
            // still override it afterwards.
            setSplPrices(prev => {
                const updated = [...prev];
                updated[index] = Number(p.standard_price || p.Standard_Price || 0);
                return updated;
            });

        } catch (err) {

            console.error("PRODUCT FETCH ERROR =", err);

            toast.error("Failed to load product");
        }
    };

    // Discount vs Markup check — a line item's Discount (%) should never
    // exceed its effective Markup (%) (the row's own Markup if set, else the
    // header Markup (%) field). Called right after a discount/markup edit so
    // the warning shows up immediately, the same rule the Discount Review
    // tab (see discountReviewRows above) applies after submission.
    const checkDiscountVsMarkup = (productCode, discountVal, rowMarkupVal) => {
        const discNum = parseFloat(discountVal) || 0;
        const markupNum = (rowMarkupVal !== undefined && rowMarkupVal !== null && rowMarkupVal !== "")
            ? (parseFloat(rowMarkupVal) || 0)
            : (parseFloat(markup) || 0);
        if (discNum > markupNum) {
            toast.warning(`Discount (${discNum}%) exceeds Markup (${markupNum}%) for product code ${productCode || "this row"}`);
        }
    };

    // Header Markup (%) changed by the user — broadcast it into EVERY
    // product row's own markup field, overwriting any per-row overrides
    // that were set earlier. Per-row edits only survive until the next
    // time the header field itself is changed.
    const handleHeaderMarkupChange = (value) => {
        setMarkup(value);

        if (modalMode === 'create') {
            setSalesData(prev => prev.map(row => ({ ...row, markup: value })));
        } else {
            setSelectedRfq(prev => {
                if (!prev?.productDetails) return prev;
                return {
                    ...prev,
                    productDetails: prev.productDetails.map(row => ({ ...row, markup: value }))
                };
            });
        }
    };

    const handleFieldChangeEdit = async (index, field, value) => {

        if (field === 'product_code' && value && String(value).trim()) {

            // ── Fetch product details first, then apply everything in one atomic setState ──
            try {
                const res = await fetch(`${ORDER_API}/quotation/fetch-product-details?productCode=${value}`, { headers: authHeaders() });
                const data = await res.json();

                const p = Array.isArray(data?.data)
                    ? data.data[0]
                    : data?.data;

                // Use functional setState to always work on the latest state (avoids stale closure)
                setSelectedRfq(prev => {
                    const updatedDetails = prev.productDetails.map((row, i) => {
                        if (i !== index) return row;
                        const base = { ...row, product_code: value };
                        if (res.ok && p) {
                            const productChars = p.Characteristics || p.characteristics || [];
                            const { rangeText, newQty } = computeRangeAndQty(
                                productChars,
                                parseFloat(row.quantity) || 0
                            );
                            const finalQty = newQty > 0 ? newQty : (parseFloat(row.quantity) || 0);
                            return {
                                ...base,
                                description: p.Description || "",
                                uom: p.UOM || "",
                                unit_cost: p.Product_Cost || p.product_cost || 0,
                                unit_price: p.Standard_Price || 0,
                                // Reset the live-markup baseline for this newly
                                // picked product so Markup(%) recalculates off
                                // its fresh price, not a stale one.
                                original_unit_price: p.Standard_Price || 0,
                                original_markup: 0,
                                // Spl. Price resets to the newly picked product's
                                // price too — same as Unit Price above.
                                spl_price: p.Standard_Price || 0,
                                product_class: p.Product_Class || "",
                                // Minimum Order Quantity — auto-filled from the Product Master's MOQ
                                Min_Order_Qty:
                                    p.Min_Order_Qty ??
                                    p.minimum_order_quantity ??
                                    p.moq ??
                                    "",
                                // Store under BOTH keys so handleOpenCharModal always finds them
                                characteristics: productChars,
                                Characteristics: productChars,
                                // Over/Under Tolerance Range — auto-calculated from the
                                // product master's default characteristics as soon as
                                // the product is picked, instead of only after manually
                                // opening + saving the Fast Entry (▶) modal.
                                range: rangeText,
                                quantity: finalQty,
                                total_price: finalQty * (p.Standard_Price || 0)
                            };
                        }
                        return base;
                    });
                    return { ...prev, productDetails: updatedDetails };
                });

            } catch (e) {
                console.error("handleFieldChangeEdit product fetch error:", e);
                // Still update the product_code field even if fetch fails
                setSelectedRfq(prev => {
                    const updatedDetails = prev.productDetails.map((row, i) =>
                        i === index ? { ...row, product_code: value } : row
                    );
                    return { ...prev, productDetails: updatedDetails };
                });
            }
            return; // handled above
        }

        // ── All other field changes ──
        setSelectedRfq(prev => {
            const updatedDetails = prev.productDetails.map((row, i) => {
                if (i !== index) return row;
                const updated = { ...row, [field]: value };

                if (field === 'requirement_date') {
                    updated.Requirement_Day = value;
                }

                if (field === 'minimum_order_quantity') {
                    updated.Min_Order_Qty = value;
                }

                if (field === 'unit_price') {
                    const qty = parseFloat(updated.quantity) || 0;
                    const price = parseFloat(value) || 0;
                    updated.total_price = qty * price;
                }

                return updated;
            });
            return { ...prev, productDetails: updatedDetails };
        });
    };

    // "Type of Packing" is saved per product line item (like "No. of Drum/Coil"
    // / "Drum Length"), via the same generic characteristics table keyed by
    // rfq_id + item_no. This injects a "Type of Packing" entry right after the
    // Drum/Coil length characteristic if it isn't already on the row, so it
    // shows up (and gets saved) through the same Fast Entry / characteristics
    // modal — mirrors the Sales Order Fast Entry behavior.
    const TYPE_OF_PACKING_LABEL = "Type of Packing";
    const withTypeOfPacking = (charList, fallbackValue = "") => {
        const list = [...(charList || [])];
        const alreadyPresent = list.some(
            (c) => (c.characteristic || "").trim().toLowerCase() === "type of packing"
        );
        if (alreadyPresent) return list;

        const drumCoilLengthIdx = list.findIndex((c) => {
            const name = (c.characteristic || "").toLowerCase();
            return name.includes("length") && (name.includes("drum") || name.includes("coil"));
        });

        const topEntry = {
            characteristic: TYPE_OF_PACKING_LABEL,
            value: fallbackValue || "",
            calculation_type: "",
        };

        if (drumCoilLengthIdx === -1) {
            list.push(topEntry);
        } else {
            list.splice(drumCoilLengthIdx + 1, 0, topEntry);
        }
        return list;
    };

    const handleOpenCharModal = async (index, productData, isCreateMode) => {
        setModalIndex(index);

        // ================= VIEW MODE =================
        if (modalMode === 'view') {
            const savedChars = (productData.Characteristics || productData.characteristics || []).map(c => ({
                ...c,
                tolerance_applied: c.tolerance_applied === true || c.tolerance_applied === 1 || c.tolerance_applied === "1"
            }));

            // Saved item characteristics only ever carry {characteristic, value,
            // tolerance_applied} — they never stored tolerance_checkbox (the
            // per-class "show a tolerance checkbox" flag from Product
            // Configuration). Without it the checkbox never renders in view
            // mode even when it was ticked. Fetch the master list (same as
            // create/edit) just to pull tolerance_checkbox across.
            const code = productData.product_code || productData["Product Code"];
            if (!code) {
                setSelectedCharacteristics(withTypeOfPacking(savedChars, productData.type_of_packing || ""));
                setModalVisible(true);
                return;
            }

            try {
                const res = await fetch(`${ORDER_API}/quotation/fetch-product-details?productCode=${code}`, { headers: authHeaders() });
                const data = await res.json();
                const p = Array.isArray(data?.data) ? data.data[0] : data?.data;
                const masterChars = p?.Characteristics || p?.characteristics || [];
                const masterByName = {};
                masterChars.forEach(mc => { masterByName[mc.characteristic] = mc; });

                const merged = savedChars.map(c => ({
                    ...c,
                    tolerance_checkbox: masterByName[c.characteristic]?.tolerance_checkbox ?? c.tolerance_checkbox
                }));

                setSelectedCharacteristics(withTypeOfPacking(merged, productData.type_of_packing || ""));
            } catch (masterErr) {
                console.error("View-mode master fetch error (tolerance_checkbox unavailable):", masterErr);
                setSelectedCharacteristics(withTypeOfPacking(savedChars, productData.type_of_packing || ""));
            }

            setModalVisible(true);
            return;
        }

        // ================= CREATE / EDIT — ALWAYS FETCH MASTER METADATA =================
        // Why: an item's already-saved characteristics (loaded from the DB
        // `characteristics` table when editing an existing RFQ, or carried over
        // from a linked enquiry) only ever contain {characteristic, value} —
        // that table never stores calculation_type. Without calculation_type,
        // handleSaveCharModal has no way to know which characteristics drive
        // the quantity formula (e.g. "No. of Drums" × "Drum Length/Coil Length"),
        // so qty silently never recalculates. Fix: always fetch the product
        // master list (which DOES carry calculation_type) and overlay whatever
        // value is already saved/entered on top of it — same fix as enquiry.js.
        const existingChars = productData.Characteristics || productData.characteristics || [];
        const code = productData.product_code || productData["Product Code"];

        if (!code) {
            // No product code to look up master data for — fall back to whatever
            // was already on the item (qty-based calc just won't run without
            // calculation_type, same as before this fix).
            setSelectedCharacteristics(withTypeOfPacking(existingChars, productData.type_of_packing || ""));
            setModalVisible(true);
            return;
        }

        try {
            const res = await fetch(`${ORDER_API}/quotation/fetch-product-details?productCode=${code}`, { headers: authHeaders() });
            const data = await res.json();
            const p = Array.isArray(data?.data) ? data.data[0] : data?.data;
            const masterChars = p?.Characteristics || p?.characteristics || [];

            // Product Master metadata lookup, keyed by trimmed/lowercased name.
            // IMPORTANT: below we map over the SAVED rows (existingChars), not
            // over masterChars — mapping over masterChars (one entry per
            // characteristic name) would collapse any Drum/Coil "+ Add" groups
            // down to a single row, since a by-name lookup like
            // existingValueByName[mc.characteristic] only ever keeps the LAST
            // group's value. That was the bug: converting an Enquiry with two
            // Drum/Coil groups (e.g. 8×100 and 2×200) into a Quotation, or
            // reopening Edit on such an item, silently kept only the last
            // group. Mapping over the saved rows instead preserves every
            // group, however many there are — mirrors enquiry.js's
            // handleOpenCharModal ("Do not group by characteristic name —
            // this preserves duplicate Drum/Coil groups.").
            const masterByName = {};
            masterChars.forEach(mc => {
                const key = (mc.characteristic || "").trim().toLowerCase();
                masterByName[key] = mc;
            });

            const normalizedExistingChars = existingChars.map(c => ({
                ...c,
                // Normalize whatever shape came back from the DB (true/1/"1")
                // into a real boolean — same fix as enquiry.js's
                // normalizeCharacteristics.
                tolerance_applied: c.tolerance_applied === true || c.tolerance_applied === 1 || c.tolerance_applied === "1"
            }));

            const merged = normalizedExistingChars.map(saved => {
                const key = (saved.characteristic || "").trim().toLowerCase();
                const master = masterByName[key];

                let value = saved.value;
                if (value === undefined || value === null || value === "") {
                    value = master?.value || "";
                    // Pre-fill Length/Factor-style fields from parsed-excel or
                    // enquiry-carried values so the modal isn't blank.
                    if (/drum.length|coil.length/i.test(saved.characteristic) && (productData.length || productData.Length)) {
                        value = String(productData.length || productData.Length);
                    } else if (saved.characteristic === "No. of Drums" && (productData.factor || productData.Factor)) {
                        value = String(productData.factor || productData.Factor);
                    }
                }

                return {
                    ...(master || {}),
                    ...saved,
                    value,
                    // Keep this row's own saved tolerance checkbox state —
                    // each Drum/Coil group carries its own, independent of
                    // any other group on the same item.
                    tolerance_applied: saved.tolerance_applied,
                    calculation_type: saved.calculation_type ?? master?.calculation_type ?? "",
                    tolerance_checkbox: saved.tolerance_checkbox ?? master?.tolerance_checkbox
                }; // keeps calculation_type from master
            });

            // Brand-new row with no saved characteristics at all yet — fall
            // back to the master list itself (single default group) so the
            // modal still shows the full set of fields to fill in.
            const finalMerged = merged.length > 0
                ? merged
                : masterChars.map(mc => {
                    let value = mc.value || "";
                    if (/drum.length|coil.length/i.test(mc.characteristic) && (productData.length || productData.Length)) {
                        value = String(productData.length || productData.Length);
                    } else if (mc.characteristic === "No. of Drums" && (productData.factor || productData.Factor)) {
                        value = String(productData.factor || productData.Factor);
                    }
                    return {
                        ...mc,
                        value,
                        tolerance_applied: mc.tolerance_applied === true || mc.tolerance_applied === 1 || mc.tolerance_applied === "1"
                    };
                });

            // Persist the merged (calculation_type-carrying) characteristics back
            // into state so they're available next time the modal opens, and so
            // the qty-based filter in handleSaveCharModal always has what it needs.
            if (isCreateMode) {
                setSalesData(prev => {
                    const updated = [...prev];
                    updated[index] = {
                        ...updated[index],
                        Characteristics: finalMerged,
                        characteristics: finalMerged,
                        description: updated[index].description || p?.Description || "",
                        uom: updated[index].uom || p?.UOM || "",
                        unit_cost: updated[index].unit_cost || p?.Product_Cost || 0,
                        product_class: updated[index].product_class || p?.Product_Class || "",
                    };
                    return updated;
                });
            } else {
                setSelectedRfq(prev => {
                    const updatedDetails = prev.productDetails.map((row, i) => {
                        if (i !== index) return row;
                        return {
                            ...row,
                            characteristics: finalMerged,
                            Characteristics: finalMerged,
                            description: row.description || p?.Description || "",
                            uom: row.uom || p?.UOM || "",
                            unit_cost: row.unit_cost || p?.Product_Cost || 0,
                            product_class: row.product_class || p?.Product_Class || "",
                        };
                    });
                    return { ...prev, productDetails: updatedDetails };
                });
            }

            setSelectedCharacteristics(
                withTypeOfPacking(
                    finalMerged,
                    normalizedExistingChars.find(c => (c.characteristic || "").trim() === TYPE_OF_PACKING_LABEL)?.value
                        || productData.type_of_packing || ""
                )
            );
            setModalVisible(true);

        } catch (e) {
            console.error("handleOpenCharModal fetch error:", e);
            // Master fetch failed — fall back to whatever was already on the
            // item so the modal still opens (qty-based calc just won't run
            // without calculation_type, same as before this fix).
            setSelectedCharacteristics(withTypeOfPacking(existingChars, productData.type_of_packing || ""));
            setModalVisible(true);
        }
    };


    // Over/Under Tolerances checkboxes are linked — checking either one
    // checks both, since Range is only ever calculated using both together.
    // Mirrors enquiry.js's handleToggleTolerance exactly.
    const handleToggleTolerance = (index) => {
        const target = selectedCharacteristics[index];
        const name = (target?.characteristic || '').trim().toLowerCase();
        if (name !== 'over tolerance' && name !== 'under tolerance') return;

        const newChecked = !target.tolerance_applied;
        const u = selectedCharacteristics.map((c) => {
            const cName = (c.characteristic || '').trim().toLowerCase();
            if (cName === 'over tolerance' || cName === 'under tolerance') {
                return { ...c, tolerance_applied: newChecked };
            }
            return c;
        });
        setSelectedCharacteristics(u);
    };

    // "+ Add" button in the Characteristics (Fast Entry) modal — appends a
    // fresh duplicate Drum/Coil GROUP (No. of Drum/Coil, Length of the
    // Drum/Coil, Over Tolerance, Under Tolerance, Type of Packing, ...) so a
    // single line item can carry more than one drum/coil grouping (e.g. 8
    // drums of length 100 AND 2 drums of length 200 on the same item).
    // Clones the first occurrence of EVERY characteristic currently on the
    // row, whatever it's named — this can never silently drop a field, and
    // computeRangeAndQty (above) already knows how to group repeated
    // occurrences by name and combine them. Mirrors enquiry.js's
    // handleAddDrumGroup exactly.
    const handleAddDrumGroup = () => {
        setSelectedCharacteristics(prev => {
            const seen = new Set();
            const baseEntries = [];
            prev.forEach((c) => {
                const key = (c.characteristic || "").trim().toLowerCase();
                if (!seen.has(key)) {
                    seen.add(key);
                    baseEntries.push(c);
                }
            });
            if (baseEntries.length === 0) return prev;
            const clones = baseEntries.map(t => ({
                ...t,
                value: "",
                tolerance_applied: false
            }));
            return [...prev, ...clones];
        });
    };

    // "Delete" icon in the Characteristics (Fast Entry) modal — removes one
    // Drum/Coil GROUP (the group at groupIdx: every characteristic's
    // groupIdx-th occurrence, same "occurrence index = group index"
    // convention handleAddDrumGroup / computeRangeAndQty already rely on).
    // Mirrors handleAddDrumGroup's grouping logic in reverse.
    const handleRemoveDrumGroup = (groupIdx) => {
        setSelectedCharacteristics(prev => {
            const byName = {};
            const order = [];
            prev.forEach((c) => {
                const key = (c.characteristic || "").trim().toLowerCase();
                if (!byName[key]) {
                    byName[key] = [];
                    order.push(key);
                }
                byName[key].push(c);
            });

            // Never delete the last remaining group — a line item must
            // always keep at least one Drum/Coil group.
            const groupCount = Math.max(0, ...order.map(k => byName[k].length));
            if (groupCount <= 1) {
                toast.info("At least one Drum/Coil group is required.", { autoClose: 2000, hideProgressBar: true });
                return prev;
            }

            order.forEach((key) => {
                if (byName[key][groupIdx] !== undefined) {
                    byName[key].splice(groupIdx, 1);
                }
            });

            const rebuilt = [];
            order.forEach((key) => {
                byName[key].forEach((c) => rebuilt.push(c));
            });
            return rebuilt;
        });
    };

    // If no tolerance % was actually entered, calculateToleranceRange still
    // returns a "min - max" string where min === max (e.g. "25 - 25").
    // Collapse that down to a single value ("25") instead of showing a fake
    // range. Same fix as Sales Order (SalesOrder.js) — keep both in sync.
    const collapseIfSameRange = (text) => {
        if (!text) return text;
        const parts = String(text).split('-').map(s => s.trim());
        if (parts.length === 2 && parts[0] !== '' && parts[1] !== '') {
            const min = parseFloat(parts[0]);
            const max = parseFloat(parts[1]);
            if (!isNaN(min) && !isNaN(max) && min === max) {
                return parts[0];
            }
        }
        return text;
    };

    // Shared Range (+ quantity-based-calc Qty) calculator. Same formula that
    // used to live only inside handleSaveCharModal, extracted so it can also
    // run automatically as soon as characteristics are known (product
    // selection, RFQ load) instead of requiring the user to manually open
    // the Fast Entry (▶) modal and save it before Range shows up.
    const computeRangeAndQty = (chars, fallbackQty = 0) => {
        const list = chars || [];

        // ---- Qty ----
        // A "+ Add" click (handleAddDrumGroup) appends a full duplicate set of
        // Drum/Coil characteristics with the SAME characteristic names as the
        // originals, so a name like "No. of Drum/Coil" can now appear more
        // than once in `list` — one occurrence per group the user added.
        // Group qty-based characteristics by name, in the order they appear,
        // so the 1st occurrence of every qty-based name forms "group 0", the
        // 2nd occurrence forms "group 1", etc. Each group's values are
        // multiplied together (e.g. No. of Drum/Coil × Length of the
        // Drum/Coil for that group), and the groups' products are then
        // summed — e.g. two groups of (8, 100) and (2, 200) give
        // (8*100) + (2*200) = 1200, instead of flattening everything into
        // one product. Mirrors enquiry.js's handleSaveCharModal.
        //
        // A qty-based characteristic that only appears once (no duplicate
        // group for it) is broadcast — its single value is reused as that
        // characteristic's factor in every group's product.
        const qtyByName = {};
        list.forEach(c => {
            if (!c.calculation_type) return;
            const types = c.calculation_type.split(",").map(s => s.trim());
            if (!types.includes("Quantity Based Calculation")) return;
            const key = (c.characteristic || "").trim().toLowerCase();
            if (!qtyByName[key]) qtyByName[key] = [];
            qtyByName[key].push(c.value);
        });

        const qtyNames = Object.keys(qtyByName);
        let newQty = 0;
        if (qtyNames.length > 0) {
            const groupCount = Math.max(...qtyNames.map(k => qtyByName[k].length));
            const groupProducts = [];
            for (let g = 0; g < groupCount; g++) {
                let groupProduct = 1;
                let groupHasBlankFactor = false;
                qtyNames.forEach(key => {
                    const values = qtyByName[key];
                    // Broadcast a single-occurrence characteristic's value
                    // across every group; otherwise use that group's own
                    // occurrence.
                    const raw = values.length > g ? values[g] : values[0];
                    const val = parseFloat(raw) || 0;
                    if (!val) groupHasBlankFactor = true;
                    groupProduct *= val;
                });
                // If a factor within THIS group is 0/blank, only that
                // group's contribution is voided — it no longer zeroes out
                // the whole total the way one flat product used to.
                groupProducts.push(groupHasBlankFactor ? 0 : groupProduct);
            }
            newQty = groupProducts.reduce((sum, p) => sum + p, 0);
        }

        // ---- Over/Under Tolerance Range ----
        // Shared formula (see calculateToleranceRange in ProductConfiguration.js):
        // Checked: base = Length, tolerance % applied against Length itself.
        // Unchecked: base = No. of Drum/Coil * Length, tolerance % applied against Qty.
        //
        // Each "+ Add" click appends another Drum/Coil GROUP (row) with its
        // own Length / No. of Drum / Over / Under Tolerance values — so any
        // of these can appear more than once in `list`. Group each of these
        // four characteristics by occurrence (same "nth occurrence = group
        // g" pairing used for Qty above), compute a min/max range per group,
        // then combine across groups — overall min = lowest of all groups'
        // mins, overall max = highest of all groups' maxes.
        const byName = (matcher) => list.filter(c => matcher((c.characteristic || '').trim().toLowerCase()));
        const overEntries = byName(n => n === 'over tolerance');
        const underEntries = byName(n => n === 'under tolerance');
        const lengthEntries = byName(n => n.includes('length') && (n.includes('drum') || n.includes('coil')));
        const noOfDrumEntries = byName(n => /no\.?\s*of\s*(drum|coil)/.test(n));

        const isToleranceOn = (c) => c?.tolerance_applied === true || c?.tolerance_applied === 1 || c?.tolerance_applied === "1";

        let rangeText = '';

        if (lengthEntries.length > 0 || noOfDrumEntries.length > 0) {
            // Drum/Coil-style product — group-aware range calc (supports any
            // number of "+ Add"-ed groups, including just one).
            const groupRowCount = Math.max(
                1,
                overEntries.length, underEntries.length, lengthEntries.length, noOfDrumEntries.length
            );

            let overallMin = null;
            let overallMax = null;
            let validGroupCount = 0;
            for (let g = 0; g < groupRowCount; g++) {
                const overEntry = overEntries.length > g ? overEntries[g] : overEntries[0];
                const underEntry = underEntries.length > g ? underEntries[g] : underEntries[0];
                const lengthEntry = lengthEntries.length > g ? lengthEntries[g] : lengthEntries[0];
                const noOfDrumEntry = noOfDrumEntries.length > g ? noOfDrumEntries[g] : noOfDrumEntries[0];

                const rowToleranceApplied = isToleranceOn(overEntry) || isToleranceOn(underEntry);
                const rowLengthVal = parseFloat(lengthEntry?.value) || 0;
                const rowNoOfDrumVal = parseFloat(noOfDrumEntry?.value) || 0;
                const rowOverPct = parseFloat(overEntry?.value) || 0;
                const rowUnderPct = parseFloat(underEntry?.value) || 0;
                // This group's own qty contribution — same base the Qty
                // calc above uses per group when summing newQty.
                const rowQty = rowNoOfDrumVal * rowLengthVal;
                if (!rowLengthVal && !rowQty) continue;

                const { rangeMin, rangeMax } = calculateToleranceRange({
                    toleranceApplied: rowToleranceApplied,
                    length: rowLengthVal,
                    noOfDrum: rowNoOfDrumVal,
                    qty: rowQty,
                    overPercent: rowOverPct,
                    underPercent: rowUnderPct
                });
                if (rangeMin === null || rangeMax === null) continue;

                validGroupCount++;
                if (overallMin === null || rangeMin < overallMin) overallMin = rangeMin;
                if (overallMax === null || rangeMax > overallMax) overallMax = rangeMax;
            }

            // More than one Drum/Coil group ("+ Add"-ed) on this item — a
            // single combined min-max Range here would misleadingly read as
            // one continuous span (e.g. "97 - 208") when the groups actually
            // have their own separate ranges (97-103, 192-208). Point to the
            // Fast Entry (▶) table instead, which already lists each group's
            // own Range.
            if (validGroupCount > 1) {
                rangeText = "Refer Table";
            } else if (overallMin !== null) {
                rangeText = `${overallMin} - ${overallMax}`;
            }
        } else {
            // Not a Drum/Coil-style product (no Length / No. of Drum
            // characteristics at all) — keep the original flat formula,
            // tolerance % applied against overall Qty.
            const overChar = overEntries[0];
            const underChar = underEntries[0];
            const toleranceApplied = isToleranceOn(overChar) || isToleranceOn(underChar);
            const baseQty = newQty > 0 ? newQty : (parseFloat(fallbackQty) || 0);
            const overPct = parseFloat(overChar?.value) || 0;
            const underPct = parseFloat(underChar?.value) || 0;

            const { rangeText: flatRangeText } = calculateToleranceRange({
                toleranceApplied,
                length: 0,
                noOfDrum: 0,
                qty: baseQty,
                overPercent: overPct,
                underPercent: underPct
            });
            rangeText = flatRangeText;
        }

        return { rangeText: collapseIfSameRange(rangeText), newQty };
    };

    const handleSaveCharModal = (newChars) => {
        const existingRow = modalMode === 'create'
            ? salesData[modalIndex]
            : selectedRfq?.productDetails?.[modalIndex];
        const fallbackQty = parseFloat(existingRow?.quantity || existingRow?.Quantity) || 0;
        const { rangeText, newQty } = computeRangeAndQty(newChars, fallbackQty);

        if (modalMode === 'create') {
            setSalesData(prev => prev.map((row, i) => {
                if (i !== modalIndex) return row;
                const updatedRow = {
                    ...row,
                    Characteristics: newChars,
                    characteristics: newChars,
                    range: rangeText,
                };
                if (newQty > 0) {
                    updatedRow.Quantity = newQty;
                    updatedRow.quantity = newQty;
                    const unitPrice = parseFloat(row.Standard_Price || row.unit_price || 0);
                    updatedRow.Total_Price = newQty * unitPrice;
                }
                return updatedRow;
            }));
        } else {
            setSelectedRfq(prev => {
                const updated = prev.productDetails.map((row, i) => {
                    if (i !== modalIndex) return row;
                    const updatedRow = {
                        ...row,
                        characteristics: newChars,
                        Characteristics: newChars,
                        range: rangeText,
                    };
                    if (newQty > 0) {
                        updatedRow.quantity = parseFloat(newQty.toFixed(2));
                        const price = parseFloat(row.unit_price) || 0;
                        updatedRow.total_price = newQty * price;
                    }
                    return updatedRow;
                });
                return { ...prev, productDetails: updated };
            });
        }
        setModalVisible(false);
    };

    const handleViewProductDetails = async (id) => {
        try {
            const res = await fetch(`${ORDER_API}/quotation/line-item/${id}`, { headers: authHeaders() });

            if (res.ok) {
                const data = await res.json();
                const cgstAmount = Number(data.cgst_amount || 0);
                const sgstAmount = Number(data.sgst_amount || 0);
                const igstAmount = Number(data.igst_amount || 0); // <-- Extracted IGST here
                const netPrice = Number(data.net_price_after_discount || 0);
                const unitPrice = Number(data.unit_price || 0);
                const quantity = Number(data.quantity || 0);

                // 🆕 Dynamic tax breakdown — data.taxes comes straight from
                // quotation_tax via getLineItemById (see quotation.service.js).
                // A line saved before quotation_tax existed has no taxes[]
                // yet, so fall back to synthesizing from the legacy
                // cgst/sgst/igst amount columns.
                const dynamicTaxes = (Array.isArray(data.taxes) && data.taxes.length)
                    ? data.taxes
                    : [
                        { type: "CGST", percent: parseFloat(data.cgst) || 0, value: cgstAmount },
                        { type: "SGST", percent: parseFloat(data.sgst) || 0, value: sgstAmount },
                        { type: "IGST", percent: parseFloat(data.igst) || 0, value: igstAmount },
                    ].filter(t => t.percent || t.value);

                const totalGST = dynamicTaxes.reduce((sum, t) => sum + (parseFloat(t.value) || 0), 0);

                setProductDetails({
                    ...data,
                    hsnCode: data.hsn_code,
                    netPrice: netPrice,
                    taxes: dynamicTaxes,
                    cgstAmount: cgstAmount,
                    sgstAmount: sgstAmount,
                    igstAmount: igstAmount, // <-- Added to state here
                    totalGST: totalGST,
                    finalPriceAfterGST: netPrice + totalGST,
                    totalPrice: unitPrice * quantity
                });

                setShowProductModal(true);
            } else {
                toast.error("Product details not found");
            }
        } catch (e) {
            console.error("Fetch Error:", e);
            toast.error("Error fetching details");
        }
    };

    const executeUpdateRFQ = async (commentToSubmit = null) => {
        if (!validateReqDatesFilled(selectedRfq.productDetails)) return;

        if (!validityNumber || !String(validityNumber).trim()) {
            toast.error("Validity is required.");
            return;
        }

        if (!paymentTerms || !String(paymentTerms).trim()) {
            toast.error("Payment Terms is required.");
            return;
        }

        // Only the overall Total Price must be > 0 (individual lines may be 0).
        if (!(parseFloat(liveTotals?.net) > 0)) {
            toast.error("Total Price must be greater than 0 before saving.");
            return;
        }

        try {
            const body = {
                productDetails: selectedRfq.productDetails.map(p => ({
                    ...p,
                    requirement_date: p.requirement_date || p.Requirement_Day || null,
                    minimum_order_quantity: p.minimum_order_quantity || p.Min_Order_Qty || null,
                    // Ensure both keys are present so backend INSERT/UPDATE both work
                    characteristics: p.characteristics || p.Characteristics || [],
                    Characteristics: p.Characteristics || p.characteristics || [],
                    // Ensure currency is sent per-row (used by INSERT for new rows)
                    currency: p.currency || p.Currency || currency || "",
                    // Ensure quantity is a number
                    quantity: parseFloat(p.quantity || p.Quantity || 0),
                    unit_price: parseFloat(p.unit_price || p.Standard_Price || 0),
                    // 🆕 Spl. Price — only send it if the row actually has one
                    // (either loaded from the DB or explicitly edited this
                    // session). A legacy row that never got one leaves this
                    // out entirely so the backend can fall back to reversing
                    // the markup already baked into unit_price, same as before.
                    ...(p.spl_price !== undefined && p.spl_price !== null && p.spl_price !== ""
                        ? { spl_price: parseFloat(p.spl_price) }
                        : {}),
                    unit_cost: parseFloat(p.unit_cost || p.Product_Cost || 0),
                    discount: parseFloat(p.discount || p.Discount || 0),
                    product_class: p.product_class || p.Product_Class || "",
                    description: p.description || p.Description || "",
                    uom: p.uom || p.UOM || "",
                    // Over/Under Tolerances Range — calculated in handleSaveCharModal
                    range: p.range || "",
                    // Each row's own Markup (%) — defaults to the header
                    // value unless the user overrode it for this row. The
                    // backend falls back to the header Markup (%) field for
                    // any row that comes through blank.
                    markup: (p.markup !== undefined && p.markup !== null) ? p.markup : "",
                })),
                comment: commentToSubmit,
                updatedBy: getCurrentUser(),
                paymentTerms: paymentTerms || null,
                currency: currency || "",
                markup: markup || 0,
                customerInspection: customerInspection,
                ...(validityNumber ? { validityTerms: `${validityNumber} ${validityUnit}` } : {}),
                ...(validityDate ? { validityDate: validityDate } : {}),
                linkedInquiryId: selectedInquiryId ? selectedInquiryId.value : null,
                quotationRevNo: quotationRevNo || "",
            };

            const response = await fetch(`${ORDER_API}/quotation/update/${rfqId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(body)
            });

            if (response.ok) {
                toast.success("Changes saved successfully!");
                // Re-fetch the RFQ so new rows get their DB-assigned IDs and calculated fields
                const refreshed = await fetch(`${ORDER_API}/quotation/${rfqId}`, { headers: authHeaders() });
                if (refreshed.ok) {
                    const data = await refreshed.json();
                    setSelectedRfq(withOriginalPricing(data));
                    // --- Sync UI states immediately with the newly calculated backend data ---
                    if (data.validity_date) {
                        const clean = String(data.validity_date).substring(0, 10);
                        const [yyyy, mm, dd] = clean.split('-');
                        setValidityDate((yyyy && mm && dd) ? `${yyyy}-${mm}-${dd}` : null);
                    } else {
                        setValidityDate(null);
                    }

                    if (data.validity_terms) {
                        const match = data.validity_terms.match(/^(\d+)\s*(.*)$/);
                        setValidityNumber(match ? match[1] : data.validity_terms);
                        setValidityUnit(match && match[2] ? match[2].toLowerCase() : "days");
                    }
                    // -----------------------------------------------------------------------
                    if (data.productDetails) {
                        const mappedData = data.productDetails.map(item => ({
                            ...item,
                            Standard_Price: item.unit_price,
                            Product_Cost: item.unit_cost,
                            "Product Code": item.product_code,
                            Requirement_Day: item.requirement_date,
                            requirement_date: item.requirement_date,
                            Min_Order_Qty: item.minimum_order_quantity ?? '',
                            minimum_order_quantity: item.minimum_order_quantity ?? '',
                            characteristics: item.characteristics || [],
                            Characteristics: item.characteristics || [],
                            // Keep this row's own persisted Markup (%) (falls
                            // back to the header Markup (%) via the live-calc
                            // effect if it comes back blank).
                            markup: (item.markup === null || item.markup === undefined) ? "" : item.markup,
                        }));
                        setSelectedRfq(prev => ({ ...prev, productDetails: mappedData }));
                    }
                }
                setModalMode("view");
                fetchSummary();
                fetchLogs(rfqId);
            } else {
                const err = await response.json();
                toast.error(err.message || "Failed to save changes");
            }
        } catch (error) {
            console.error("Save error:", error);
            toast.error("Internal Server Error");
        }
    };

    const handleSaveEdit = () => {
        if (!validityNumber || !String(validityNumber).trim()) {
            toast.error("Validity is required.");
            return;
        }
        if (!paymentTerms || !String(paymentTerms).trim()) {
            toast.error("Payment Terms is required.");
            return;
        }
        if (selectedRfq?.rfq_status === 'Rejected') {
            setActionType("RESUBMIT_EDIT");
            setSubmitComment("");
            setShowCommentModal(true);
        } else {
            executeUpdateRFQ();
        }
    };

    const handleOpenSubmitToCustomer = () => {
        setActionType("SUBMIT_TO_CUSTOMER");
        setSubmitComment("");
        setShowCommentModal(true);
    };

    const handleCommentSubmit = async () => {
        if (actionType === "SUBMIT_TO_CUSTOMER") {
            try {
                const res = await fetch(`${ORDER_API}/quotation/submit-to-customer`,{
                    method: "POST",
                    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        rfqId: selectedRfq.rfq_id,
                        comment: submitComment,
                        updatedBy: getCurrentUser()
                    })
                });
                if (res.ok) {

                    toast.success(
                        "Submitted to Customer successfully!"
                    );

                    setTimeout(() => {

                        toast.success(
                            "Quotation Template Generated Successfully"
                        );

                    }, 500);

                    setShowCommentModal(false);

                    handleCloseModal();

                    fetchSummary();

                    fetchLogs(rfqId);

                } else {
                    toast.error("Failed to submit.");
                }
            } catch (err) {
                toast.error("Error submitting to customer.");
            }
        } else {
            if (modalMode === 'edit' && selectedRfq?.rfq_status === 'Rejected') {
                executeUpdateRFQ(submitComment);
            } else {
                handleSubmitNewRFQ();
            }
            setShowCommentModal(false);
        }
    };

  /* GTP datasheet filenames in Quotation always use the fixed "GN35" prefix
     (instead of whatever gtpNumber the master GTP Configuration record has),
     followed by the product code and then the product description. */
  const gtpFileName = (gtp) => `GN35_${gtp.productCode || ""}_${gtp.productDescription || ""}.pdf`;

  /* The GTP doc shown/printed here is always the SALES doc, never the full
     datasheet — i.e. only characteristics whose GTP Type includes "Sales"
     are included, regardless of what's ticked for Master Doc. gtpType can
     arrive either as a comma-separated string ("Sales,Master") or an array
     of strings/option objects, so normalize before checking. */
  const isSalesCharacteristic = (item) => {
      const raw = item.gtpType ?? item.gtp_type ?? "";
      const types = Array.isArray(raw)
          ? raw.map((t) => (typeof t === "string" ? t : t?.value || t?.label || ""))
          : String(raw).split(",");
      return types.map((t) => String(t).trim()).includes("Sales");
  };

  /* ── GTP Datasheet PDF builder — matches GTPConfiguration format exactly ──
     preparedBy/approvedBy come from the QUOTATION (created_by / approved_by),
     not from the GTP master record — GTP config has no meaningful signer of
     its own, and the old fallback to gtp.checkedBy was showing a hardcoded
     "Admin" on every datasheet. Approved By is left blank until the
     quotation is actually approved (no fallback). ── */
const buildGtpDatasheetPdf = (gtp, { preparedBy = "", approvedBy = "" } = {}) => {
    const doc       = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin    = 40;
    const contentW  = pageWidth - margin * 2;

    // jsPDF's built-in "helvetica" font only supports WinAnsi/Latin-1
    // characters. Symbols like Ω (ohm) fall outside that set and render as
    // garbled bytes — this only affects the PDF; the on-screen View uses
    // the browser's own font rendering, which handles full Unicode fine.
    // Swap known problem characters for ASCII-safe equivalents before any
    // text goes into the PDF.
    const pdfSafeText = (val) => {
        if (val === null || val === undefined) return "";
        return String(val)
            .replace(/Ω/g, "Ohm")
            .replace(/[µμ]/g, "u")
            .replace(/°/g, " deg ");
    };

    /* ── HEADER ── */
    const headerTopY = 28;
    const logoH = 40, logoW = 60;
    try { doc.addImage(sbeeLogo, "PNG", margin, headerTopY, logoW, logoH); } catch (e) { }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("SBEE CABLES INDIA LTD", pageWidth / 2, headerTopY + 15, { align: "center" });

    doc.setFontSize(10);
    doc.text(
        `GUARANTEED TECHNICAL PARTICULARS FOR ${(gtp.productType || "").toUpperCase()}`,
        pageWidth / 2, headerTopY + 30, { align: "center" }
    );

    /* ── PRODUCT CODE / DESCRIPTION BAR ── */
    const barTopY = headerTopY + logoH + 12;
    const barH    = 20;

    doc.setDrawColor(51, 51, 51);
    doc.setLineWidth(0.75);
    doc.rect(margin, barTopY, contentW, barH);

    doc.setFontSize(9);
    const ty = barTopY + barH / 2 + 3.5;

    // Measure each segment first (font changes affect getTextWidth), so we
    // can center the whole line within the bar instead of starting at a
    // fixed left offset.
    doc.setFont("helvetica", "bold");
    const seg1 = "Product Code:";
    const w1 = doc.getTextWidth(seg1);

    doc.setFont("helvetica", "normal");
    const seg2 = ` ${gtp.productCode || "-"}`;
    const w2 = doc.getTextWidth(seg2);

    doc.setFont("helvetica", "bold");
    const seg3 = "   Description:";
    const w3 = doc.getTextWidth(seg3);

    doc.setFont("helvetica", "normal");
    const seg4 = ` ${pdfSafeText(gtp.productDescription) || "-"}`;
    const w4 = doc.getTextWidth(seg4);

    const totalW = w1 + w2 + w3 + w4;
    let tx = margin + (contentW - totalW) / 2;

    doc.setFont("helvetica", "bold");
    doc.text(seg1, tx, ty);
    tx += w1;

    doc.setFont("helvetica", "normal");
    doc.text(seg2, tx, ty);
    tx += w2;

    doc.setFont("helvetica", "bold");
    doc.text(seg3, tx, ty);
    tx += w3;

    doc.setFont("helvetica", "normal");
    doc.text(seg4, tx, ty);

    /* ── TABLE ── */
    const tableStartY = barTopY + barH;
    const col0W = 45, col2W = 65, col1W = 185;
    const col3W = contentW - col0W - col1W - col2W;

    const tableBody = [];

    // GENERAL section
    tableBody.push([
        { content: "1", styles: { fontStyle: "bold", halign: "center" } },
        { content: "GENERAL", colSpan: 3, styles: { fontStyle: "bold" } },
    ]);
    tableBody.push(["1.1", "Name & Address of the manufacturer", "",
        manufacturerAddress
    ]);
    tableBody.push(["1.2", "Trade mark / Brand Name", "", ""]);
    const trademarkBodyRowIndex = 2;

    // Dynamic characteristics — only rows that actually have a specified
    // value are printed; blanks are dropped instead of showing an empty cell.
    let lastHeader = null, sectionCounter = 1, subCounter = 0;
    const gtpCharsWithValues = (gtp.characteristics || []).filter(
        (item) => item.standardValue !== undefined && item.standardValue !== null && String(item.standardValue).trim() !== "" && isSalesCharacteristic(item)
    );
    gtpCharsWithValues.forEach((item) => {
        const headerName = item.processName || "";
        if (headerName !== lastHeader) {
            sectionCounter += 1; subCounter = 0;
            tableBody.push([
                { content: String(sectionCounter), styles: { fontStyle: "bold", halign: "center" } },
                { content: pdfSafeText(headerName).toUpperCase(), colSpan: 3, styles: { fontStyle: "bold" } },
            ]);
            lastHeader = headerName;
        }
        subCounter += 1;
        tableBody.push([
            `${sectionCounter}.${subCounter}`,
            pdfSafeText(item.characteristic),
            pdfSafeText(item.uom),
            pdfSafeText(item.standardValue),
        ]);
    });

    autoTable(doc, {
        startY: tableStartY,
        head: [[
            { content: "S.No",             styles: { halign: "center" } },
            { content: "Parameters",       styles: { halign: "left"   } },
            { content: "UOM",              styles: { halign: "center" } },
            { content: "Specified Values", styles: { halign: "center" } },
        ]],
        body: tableBody,
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 5, lineColor: [51, 51, 51], lineWidth: 0.75, textColor: 30 },
        headStyles: { fillColor: [220, 220, 220], textColor: 20, fontStyle: "bold", lineColor: [51, 51, 51], lineWidth: 0.75 },
        columnStyles: {
            0: { cellWidth: col0W, halign: "center" },
            1: { cellWidth: col1W, halign: "left"   },
            2: { cellWidth: col2W, halign: "center" },
            3: { cellWidth: col3W, halign: "center" },
        },
        margin: { left: margin, right: margin },
        didDrawCell: (data) => {
            if (
                data.section === "body" &&
                data.row.index === trademarkBodyRowIndex &&
                data.column.index === 3
            ) {
                try {
                    const imgW = 48, imgH = 19;
                    const imgX = data.cell.x + (data.cell.width  - imgW) / 2;
                    const imgY = data.cell.y + (data.cell.height - imgH) / 2;
                    doc.addImage(sbeeLogo, "PNG", imgX, imgY, imgW, imgH);
                } catch (e) {
                    doc.setFontSize(9);
                    doc.text("SBEE", data.cell.x + data.cell.width / 2,
                        data.cell.y + data.cell.height / 2 + 3, { align: "center" });
                }
            }
        },
    });

    /* ── FOOTER ── */
    const finalY     = doc.lastAutoTable.finalY + 50;
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerY    = Math.min(finalY, pageHeight - 50);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Prepared By:", margin, footerY);
    doc.text("Approved By:", pageWidth - margin - 130, footerY);

    doc.setDrawColor(51, 51, 51);
    doc.setLineWidth(0.75);
    doc.line(margin + 75, footerY, margin + 220, footerY);
    doc.line(pageWidth - margin - 55, footerY, pageWidth - margin, footerY);

    if (preparedBy) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(preparedBy, margin + 80, footerY - 2);
    }

    // Approved By only shows once the quotation has actually been approved —
    // no fallback to Prepared By.
    if (approvedBy) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(approvedBy, pageWidth - margin - 50, footerY - 2);
    }

    return doc;
};

    const formatCurrency = (value) => {
        const val = Number(value);
        if (isNaN(val)) return "0";
        return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Safely format any date (Date object, "YYYY-MM-DD", or ISO timestamp string)
    // as "DD-MM-YYYY" for display. Strings are never passed through `new Date()`
    // for the display path — we read the calendar digits straight off the string —
    // so IST/UTC conversion can never shift the day backward.
    const formatDateLocal = (dateInput) => {
        if (!dateInput) return "";
        let year, month, day;
        if (dateInput instanceof Date) {
            if (isNaN(dateInput.getTime())) return "";
            year = dateInput.getFullYear();
            month = String(dateInput.getMonth() + 1).padStart(2, '0');
            day = String(dateInput.getDate()).padStart(2, '0');
        } else {
            const clean = String(dateInput).substring(0, 10); // "YYYY-MM-DD"
            [year, month, day] = clean.split("-");
            if (!year || !month || !day) return "";
        }
        return `${day}-${month}-${year}`;
    };

    // Fixed dd/mm/yyyy hh:mm:ss formatter, always in Indian Standard Time
    // (Asia/Kolkata), regardless of the viewer's device timezone/locale or the
    // UTC value sent by the server. Used for the Change Log "Date & Time".
    const formatChangeLogDateTime = (dateInput) => {
        if (!dateInput) return "-";
        const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(d.getTime())) return "-";
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false
        }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
        const hh = parts.hour === '24' ? '00' : parts.hour;
        return `${parts.day}/${parts.month}/${parts.year} ${hh}:${parts.minute}:${parts.second}`;
    };



    return (
        <Layout>
            <style>{`
                .no-spinner::-webkit-outer-spin-button,
                .no-spinner::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .no-spinner {
                    -moz-appearance: textfield;
                }
            `}</style>
                        <div style={styles.pageTitleBar}>
                <div>
                    <div style={styles.pageTitle}>Quotation</div>
                    <div style={styles.pageSubtitle}>Manage and view your data here</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {canCreate && (
                    <button className="btn btn-dark" style={styles.primaryBtn} onClick={handleOpenCreate}>
                        + Create RFQ
                    </button>
                    )}
                    {/* --- User Manual Icon Button --- */}
                    <button
                        onClick={() => {
                            setManualExists(null);
                            setManualFile(null);
                            setShowManual(true);
                            fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                                .then((res) => res.json())
                                .then((data) => {
                                    const norm = (v) => String(v || "").trim().toLowerCase();
                                    const match = (data?.data || []).find((m) =>
                                        norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                                        norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                                        norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                                    );
                                    if (match && match.drive_link) {
                                        setManualFile(match);
                                        setManualExists(true);
                                    } else {
                                        setManualExists(false);
                                    }
                                })
                                .catch(() => setManualExists(false));
                        }}
                        title="View User Manual"
                        style={{
                            background: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "10px",
                            padding: "10px 14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            color: "#3b82f6",
                            fontSize: "14px",
                            fontWeight: "600",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
                    >
                        <FileText size={18} />
                        User Manual
                    </button>
                </div>
            </div>

            {/* --- User Manual Popup Modal --- */}
            {showManual && (
                <div
                    onClick={() => setShowManual(false)}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(15, 23, 42, 0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1100,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#ffffff",
                            borderRadius: "12px",
                            width: "90vw",
                            height: "97vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "14px 20px",
                                borderBottom: "1px solid #e2e8f0",
                            }}
                        >
                            <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                                User Manual
                            </h4>
                            <button
                                onClick={() => setShowManual(false)}
                                style={{
                                    border: "none",
                                    background: "#f1f5f9",
                                    borderRadius: "8px",
                                    width: "32px",
                                    height: "32px",
                                    cursor: "pointer",
                                    fontSize: "18px",
                                    color: "#475569",
                                    lineHeight: 1,
                                }}
                                title="Close"
                            >
                                ×
                            </button>
                        </div>

                                                {manualExists === null && (
                            <div style={{
                                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                color: "#94a3b8", fontSize: "14px"
                            }}>
                                Checking for user manual...
                            </div>
                        )}

                        {manualExists === false && (
                            <div style={{
                                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                            }}>
                                <FileText size={40} color="#cbd5e1" />
                                <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                                    User Manual Not Available
                                </div>
                                <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                                    The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                                </div>
                            </div>
                        )}

                        {manualExists === true && manualFile && (() => {
                            const MANUAL_SCALE = 1.25; // 1.0 = normal, 1.25 = 25% bigger, 1.5 = 50% bigger
                            return (
                                <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                                    <iframe
                                        src={manualFile.drive_link.replace("/view", "/preview")}
                                        title={manualFile.file_name || "User Manual"}
                                        allow="autoplay"
                                        style={{
                                            border: "none",
                                            width: `${100 / MANUAL_SCALE}%`,
                                            height: `${100 / MANUAL_SCALE}%`,
                                            transform: `scale(${MANUAL_SCALE})`,
                                            transformOrigin: "top left",
                                        }}
                                    />
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* --- FILTER CARD + LIST: hidden for CREATE-only (add-only) roles,
                 which have can_view = 0 and no visibility into existing records --- */}
            {canView && (
            <>
            {showExpiredOnly && (
                <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
                    padding: "10px 16px", marginBottom: 16, color: "#991b1b", fontWeight: 500
                }}>
                    <span>Showing RFQs with expired validity only</span>
                    <button
                        onClick={() => setShowExpiredOnly(false)}
                        style={{
                            background: "transparent", border: "1px solid #991b1b", color: "#991b1b",
                            borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: 13
                        }}
                    >
                        Clear
                    </button>
                </div>
            )}

            {!showExpiredOnly && (
            <div style={styles.filterCard}>
                <div className="row g-4">
                    <div className="col-md-2">
                        <label style={styles.filterLabel}>Sales Organization</label>
                        <Select
                            options={dropdownOptions.salesOrgs.map(opt => ({ value: opt.code || opt.name || opt, label: opt.code || opt.name || opt }))}
                            value={filterSalesOrg ? { value: filterSalesOrg, label: filterSalesOrg } : null}
                            onChange={opt => { setShowExpiredOnly(false); setFilterSalesOrg(opt ? opt.value : ""); }}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2">
                        <label style={styles.filterLabel}>Customer Segment</label>
                        <Select
                            options={dropdownOptions.customerSegments.map(opt => ({ value: opt.name || opt.customer_segment || opt, label: opt.name || opt.customer_segment || opt }))}
                            value={filterCustomerSegment ? { value: filterCustomerSegment, label: filterCustomerSegment } : null}
                            onChange={opt => { setShowExpiredOnly(false); setFilterCustomerSegment(opt ? opt.value : ""); }}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2">
                        <label style={styles.filterLabel}>Distribution Channel</label>
                        <Select
                            options={dropdownOptions.channels.map(opt => ({ value: opt.code || opt.name || opt, label: opt.code || opt.name || opt }))}
                            value={filterDChannel ? { value: filterDChannel, label: filterDChannel } : null}
                            onChange={opt => { setShowExpiredOnly(false); setFilterDChannel(opt ? opt.value : ""); }}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2">
                        <label style={styles.filterLabel}>RFQ ID</label>
                        <Select
                            options={dropdownOptions.rfqIds.map(opt => ({ value: opt, label: opt }))}
                            value={filterRfqId ? { value: filterRfqId, label: filterRfqId } : null}
                            onChange={opt => { setShowExpiredOnly(false); setFilterRfqId(opt ? opt.value : ""); }}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2">
                        <label style={styles.filterLabel}>Customer</label>
                        <AsyncSelect
                            cacheOptions
                            loadOptions={loadCustomerOptions}
                            defaultOptions
                            value={filterCustomer}
                            onChange={opt => { setShowExpiredOnly(false); setFilterCustomer(opt); }}
                            placeholder="Select Customer"
                            isClearable={true}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2">
                        <label style={styles.filterLabel}>Status</label>
                        <Select
                            options={dropdownOptions.statuses.map(opt => ({ value: opt, label: opt }))}
                            value={filterStatus ? { value: filterStatus, label: filterStatus } : null}
                            onChange={opt => { setShowExpiredOnly(false); setFilterStatus(opt ? opt.value : ""); }}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2">
                        <label style={styles.filterLabel}>Creation Date From</label>
                        <DatePicker
                            className="form-control form-control-sm"
                            wrapperClassName="w-100"
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/mm/yyyy"
                            selected={filterCreationDateFrom}
                            onChange={(date) => { setShowExpiredOnly(false); setFilterCreationDateFrom(date); }}
                            isClearable
                            maxDate={filterCreationDateTo || null}
                        />
                    </div>
                    <div className="col-md-2">
                        <label style={styles.filterLabel}>Creation Date To</label>
                        <DatePicker
                            className="form-control form-control-sm"
                            wrapperClassName="w-100"
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/mm/yyyy"
                            selected={filterCreationDateTo}
                            onChange={(date) => { setShowExpiredOnly(false); setFilterCreationDateTo(date); }}
                            isClearable
                            minDate={filterCreationDateFrom || null}
                        />
                    </div>
                </div>
            </div>
            )}

            <div style={styles.tableContainer}>
                {loading && <div style={{ padding: 20 }}>Loading…</div>}
                <table className="table" style={styles.erpTable}>
                    <thead>
                        <tr>
                            <th style={styles.erpTh}>RFQ ID</th>
                            <th style={styles.erpTh}>Customer</th>
                            <th style={styles.erpTh}>Sales Organization</th>
                            <th style={styles.erpTh}>Customer Segment</th>
                            <th style={styles.erpTh}>Distribution Channel</th>
                            <th style={styles.erpTh}>Product Count</th>
                            <th style={{ ...styles.erpTh, textAlign: 'right' }}>Order Value</th>
                            <th style={styles.erpTh}>Creation Date</th>
                            <th style={styles.erpTh}>Status</th>
                            <th style={styles.erpTh}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length ? paginatedData.map(item => {
                            // Bootstrap's .table class paints background-color on each
                            // <td>, not the <tr> — so setting it only on <tr> gets
                            // painted over. Merge it into every cell's style instead.
                            const rowCellStyle = item.is_validity_expired
                                ? { ...styles.erpTd, backgroundColor: '#fde8e8' }
                                : styles.erpTd;
                            return (
                            <tr key={item.rfq_id}>
                                <td style={rowCellStyle}>
                                    <span
                                        onClick={() => handleOpenView(item.rfq_id)}
                                        style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: '2px' }}
                                    >
                                        {item.rfq_id}
                                    </span>
                                </td>
                                <td style={rowCellStyle}>{item.customer}</td>
                                <td style={rowCellStyle}>{item.sales_organization}</td>
                                <td style={rowCellStyle}>{item.customer_segment || '—'}</td>
                                <td style={rowCellStyle}>{item.distribution_channel || '—'}</td>
                                <td style={rowCellStyle}>{item.product_count ?? '—'}</td>
                                <td style={{ ...rowCellStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                    {item.grand_total != null
                                        ? Number(item.grand_total).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                        : '—'}
                                </td>
                                <td style={rowCellStyle}>
                                    {item.created_at ? formatDateLocal(item.created_at) : '—'}
                                </td>
                                <td style={rowCellStyle}>{item.rfq_status}</td>
                                <td style={rowCellStyle}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <button className="btn btn-outline-dark btn-sm"
                                            style={{ fontSize: 11 }}
                                            onClick={() => handleOpenView(item.rfq_id)}>
                                            View
                                        </button>
                                        {canCreate && (
                                        <button
                                            title="Delete"
                                            onClick={() => handleDeleteRFQ(item.rfq_id)}
                                            style={{
                                                background: "#fff",
                                                border: "1px solid #f5c2c7",
                                                borderRadius: "4px",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                padding: "4px 8px",
                                                cursor: "pointer"
                                            }}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z"/>
                                                <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z"/>
                                            </svg>
                                        </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                            );
                        }) : (
                            <tr><td colSpan="10" className="text-center p-3 text-muted">No Records Found</td></tr>
                        )}
                    </tbody>
                </table>
                <div className="d-flex align-items-center justify-content-between pt-3">
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                        Page <b>{page}</b> of <b>{totalPages}</b> • Total {filteredData.length}
                    </div>
                    <div className="d-flex gap-2">
                        <button className="btn btn-outline-secondary btn-sm"
                            disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                        <button className="btn btn-outline-secondary btn-sm"
                            disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                    </div>
                </div>
            </div>
            </>
            )}

            {showModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "1300px", width: "95%", maxHeight: "90vh" }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600, fontSize: '18px', color: '#1e293b' }}>
                                {modalMode === 'create' ? "Create" : modalMode === 'edit' ? `Edit: ${rfqId}` : `RFQ: ${rfqId}`}
                            </div>
                            <button style={styles.iconBtn} onClick={handleCloseModal}>✕</button>
                        </div>

                        <div style={styles.modalBody}>
                            {/* ── Reference RFQ ── */}
                            {modalMode === 'create' && (
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label style={{ ...styles.compactLabel, display: 'block' }}>
                                            Reference RFQ <span style={{ fontWeight: 400, color: '#64748b', fontSize: '12px' }}></span>
                                        </label>
                                        <Select
                                            options={refRfqList}
                                            value={selectedRefRfq}
                                            onChange={handleRefRfqSelect}
                                            isClearable
                                            placeholder="Select an existing RFQ to copy from..."
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={{
                                                ...dropdownOptionStyle,
                                                control: (b) => ({ ...b, minHeight: 34, fontSize: '13px', borderColor: '#6366f1', boxShadow: selectedRefRfq ? '0 0 0 2px rgba(99,102,241,0.2)' : 'none' }),
                                                menuPortal: (b) => ({ ...b, zIndex: 99999 })
                                            }}
                                        />
                                    </div>
                                    {/* {selectedRefRfq && (
                                        <div className="col-md-6 d-flex align-items-end">
                                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '6px 12px', fontSize: 12, color: '#166534' }}>
                                                ✓ Fields populated from <strong>{selectedRefRfq.value}</strong>. You can freely edit or add/remove products below.
                                            </div>
                                        </div>
                                    )} */}
                                </div>
                            )}

                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <label style={styles.compactLabel}>Sales Organization <span style={{ color: 'red' }}>*</span></label>
                                    {modalMode === 'view' && !isFieldVisible('sales_org') ? (
                                        <MaskedValue />
                                    ) : (
                                    <Select
                                        options={salesOrgList.map(item => ({ value: item.code || item.name || item.sales_organization, label: item.code || item.name || item.sales_organization }))}
                                        value={salesOrganization ? { value: salesOrganization, label: salesOrganization } : null}
                                        onChange={opt => setSalesOrganization(opt ? opt.value : "")}
                                        isDisabled={modalMode !== 'create'}
                                        placeholder="Select"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={dropdownOptionStyle}
                                    />
                                    )}
                                </div>
                                <div className="col-md-4">
                                    <label style={styles.compactLabel}>Customer Segment <span style={{ color: 'red' }}>*</span></label>
                                    {modalMode === 'view' && !isFieldVisible('customer_segment') ? (
                                        <MaskedValue />
                                    ) : (
                                    <Select
                                        options={customerSegmentList.map(item => ({ value: item.name || item.customer_segment || item, label: item.name || item.customer_segment || item }))}
                                        value={customerSegment ? { value: customerSegment, label: customerSegment } : null}
                                        onChange={opt => setCustomerSegment(opt ? opt.value : "")}
                                        isDisabled={modalMode !== 'create'}
                                        placeholder="Select"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={dropdownOptionStyle}
                                    />
                                    )}
                                </div>
                                <div className="col-md-4">
                                    <label style={styles.compactLabel}>Distribution Channel <span style={{ color: 'red' }}>*</span></label>
                                    {modalMode === 'view' && !isFieldVisible('distribution_channel') ? (
                                        <MaskedValue />
                                    ) : (
                                    <Select
                                        options={channelList.map(item => ({ value: item.code || item.name || item.distribution_channel, label: item.code || item.name || item.distribution_channel }))}
                                        value={dChannel ? { value: dChannel, label: dChannel } : null}
                                        onChange={opt => setDCHannel(opt ? opt.value : "")}
                                        isDisabled={modalMode !== 'create'}
                                        placeholder="Select"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={dropdownOptionStyle}
                                    />
                                    )}
                                </div>
                            </div>

                            {/* ---- Created By / Created Date / Approved By / Approved Date ---- */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Created By</label>
                                    {modalMode === 'view' && !isFieldVisible('created_by') ? (
                                        <MaskedValue />
                                    ) : (
                                    <input
                                        type="text"
                                        className="form-control"
                                        style={{ ...styles.compactInput, background: '#f8fafc', color: '#374151', cursor: 'default' }}
                                        value={createdBy}
                                        readOnly
                                    />
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Created Date</label>
                                    {modalMode === 'view' && !isFieldVisible('created_date') ? (
                                        <MaskedValue />
                                    ) : (
                                    <input
                                        type="text"
                                        className="form-control"
                                        style={{ ...styles.compactInput, background: '#f8fafc', color: '#374151', cursor: 'default' }}
                                        value={createdDate}
                                        readOnly
                                    />
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Approved By</label>
                                    {modalMode === 'view' && !isFieldVisible('approved_by') ? (
                                        <MaskedValue />
                                    ) : (
                                    <input
                                        type="text"
                                        className="form-control"
                                        style={{ ...styles.compactInput, background: '#f8fafc', color: '#374151', cursor: 'default' }}
                                        value={approvedBy || '—'}
                                        readOnly
                                    />
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Approved Date</label>
                                    {modalMode === 'view' && !isFieldVisible('approved_date') ? (
                                        <MaskedValue />
                                    ) : (
                                    <input
                                        type="text"
                                        className="form-control"
                                        style={{ ...styles.compactInput, background: '#f8fafc', color: '#374151', cursor: 'default' }}
                                        value={approvedDate || '—'}
                                        readOnly
                                    />
                                    )}
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <label style={styles.compactLabel}>Markup (%)</label>
                                    {modalMode === 'view' && !isFieldVisible('markup_percent') ? (
                                        <MaskedValue />
                                    ) : (
                                    <input
                                        type="number"
                                        className="form-control no-spinner"
                                        style={styles.compactInput}
                                        placeholder="e.g. 5"
                                        value={markup}
                                        onChange={(e) => handleHeaderMarkupChange(e.target.value)}
                                        disabled={modalMode === 'view' || (modalMode === 'edit' && !isFieldEditable('markup_percent'))}
                                    />
                                    )}
                                </div>
                                <div className="col-md-4">
                                    <label style={styles.compactLabel}>Customer Inspection</label>
                                    {modalMode === 'view' && !isFieldVisible('customer_inspection') ? (
                                        <MaskedValue />
                                    ) : (
                                    <Select
                                        options={[{ value: 'No', label: 'No' }, { value: 'Yes', label: 'Yes' }]}
                                        value={{ value: customerInspection, label: customerInspection }}
                                        onChange={opt => setCustomerInspection(opt ? opt.value : "No")}
                                        isDisabled={modalMode === 'view' || (modalMode === 'edit' && !isFieldEditable('customer_inspection'))}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={dropdownOptionStyle}
                                    />
                                    )}
                                </div>
                                <div className="col-md-4">
                                    <label style={styles.compactLabel}>Validity <span style={{ color: 'red' }}>*</span></label>
                                    {modalMode === 'view' && !isFieldVisible('validity') ? (
                                        <MaskedValue />
                                    ) : (modalMode === 'view' && validityDate) ? (
                                        <div className="form-control" style={{ ...styles.compactInput, background: '#e9ecef', fontWeight: 'bold' }}>
                                            {formatDateLocal(validityDate)}
                                        </div>
                                    ) : (
                                        <div className="d-flex gap-2">
                                            <style>{`
                                                .no-spinner::-webkit-inner-spin-button,
                                                .no-spinner::-webkit-outer-spin-button {
                                                    -webkit-appearance: none;
                                                    margin: 0;
                                                }
                                                .no-spinner {
                                                    -moz-appearance: textfield;
                                                }
                                            `}</style>
                                            <input
                                                type="number"
                                                className="form-control no-spinner"
                                                style={{ ...styles.compactInput, width: '100px' }}
                                                placeholder="Number"
                                                value={validityNumber}
                                                onChange={(e) => setValidityNumber(e.target.value)}
                                                disabled={modalMode === 'view' || (modalMode === 'edit' && !isFieldEditable('validity'))}
                                            />
                                            <div style={{ width: '130px' }}>
                                                <Select
                                                    options={[{ value: 'days', label: 'Days' }, { value: 'months', label: 'Months' }, { value: 'years', label: 'Years' }]}
                                                    value={{ value: validityUnit, label: validityUnit.charAt(0).toUpperCase() + validityUnit.slice(1) }}
                                                    onChange={opt => setValidityUnit(opt ? opt.value : "days")}
                                                    isDisabled={modalMode === 'view' || (modalMode === 'edit' && !isFieldEditable('validity'))}
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    styles={dropdownOptionStyle}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* REPLACE YOUR CUSTOMER / INQUIRY / UPLOAD SECTION WITH THIS: */}
                            <div className="p-3 bg-light rounded border mb-3">
                                <div className="row g-3 align-items-start">

                                    {/* 1. Customer Selection — can be picked first, or picking Enquiry ID
                                           below fills this in automatically */}
                                    <div className="col-md-4">
                                        <label style={{ ...styles.compactLabel, display: 'block', marginBottom: '4px' }}>Customer <span style={{ color: 'red' }}>*</span></label>
                                        {modalMode === 'view' && !isFieldVisible('customer') ? (
                                            <MaskedValue height={38} />
                                        ) : (
                                        <AsyncSelect
                                            cacheOptions
                                            loadOptions={loadCustomerOptions}
                                            defaultOptions
                                            value={selectedCustomer}
                                            isDisabled={modalMode !== 'create'}
                                            onChange={handleCustomerChange}
                                            placeholder="Select Customer"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={{
                                                ...dropdownOptionStyle,
                                                control: (p) => ({ ...p, minHeight: 38, fontSize: '14px', borderRadius: '0.375rem', borderColor: '#ced4da', boxShadow: 'none' }),
                                                menu: (base) => ({ ...dropdownOptionStyle.menu(base), zIndex: 99999 }),
                                                menuPortal: (base) => ({ ...base, zIndex: 99999 })
                                            }}
                                        />
                                        )}
                                    </div>

                                    {/* 2. Enquiry ID — can be selected directly (loads its Customer,
                                           products, etc.), or gets re-scoped once a Customer is chosen above */}
                                    <div className="col-md-4">
                                        <label style={{ ...styles.compactLabel, display: 'block', marginBottom: '4px' }}>Enquiry ID</label>
                                        {modalMode === 'view' && !isFieldVisible('enquiry_id') ? (
                                            <MaskedValue height={38} />
                                        ) : modalMode === 'view' ? (
                                            <div className="form-control" style={{ minHeight: 38, fontSize: '14px', padding: '6px 10px', background: '#e9ecef', display: 'flex', alignItems: 'center' }}>
                                                {selectedInquiryId?.label || "None"}
                                            </div>
                                        ) : (
                                            <Select
                                                options={inquiryDropdownList}
                                                value={selectedInquiryId}
                                                onChange={handleInquirySelect}
                                                isDisabled={modalMode !== 'create'}
                                                placeholder="Search Enquiry ID..."
                                                isClearable
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={{
                                                    ...dropdownOptionStyle,
                                                    control: (p) => ({ ...p, minHeight: 38, fontSize: '14px', borderRadius: '0.375rem', borderColor: '#ced4da', boxShadow: 'none' }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 99999 })
                                                }}
                                            />
                                        )}
                                    </div>

                                    {/* 3. Quotation Rev No. — free-text field the user types in while
                                           creating the quotation, and can update again any time in
                                           Edit mode. Sits in the same row, right after Enquiry ID. */}
                                    <div className="col-md-4">
                                        <label style={{ ...styles.compactLabel, display: 'block', marginBottom: '4px' }}>Quotation Rev No.</label>
                                        {modalMode === 'view' && !isFieldVisible('quotation_rev_no') ? (
                                            <MaskedValue height={38} />
                                        ) : (
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={{ ...styles.compactInput, height: 38 }}
                                                placeholder="Enter Rev No."
                                                value={quotationRevNo}
                                                onChange={(e) => setQuotationRevNo(e.target.value)}
                                                disabled={modalMode === 'view' || (modalMode === 'edit' && !isFieldEditable('quotation_rev_no'))}
                                            />
                                        )}
                                    </div>

                                    {/* 🆕 EXPORT — Customer Country, only shown when the selected
                                           Enquiry's type is "Export"; auto-filled from that Enquiry
                                           and read-only here (source of truth is the Enquiry itself). */}
                                    {isExportQuotation && (
                                        <div className="col-md-4">
                                            <label style={{ ...styles.compactLabel, display: 'block', marginBottom: '4px' }}>Customer Country <span style={{ color: 'red' }}>*</span></label>
                                            {modalMode === 'view' && !isFieldVisible('customer_country') ? (
                                                <MaskedValue height={38} />
                                            ) : (
                                                <Select
                                                    options={countryOptions.map(c => ({ value: c.name || c.country || c, label: c.name || c.country || c }))}
                                                    value={customerCountry ? { value: customerCountry, label: customerCountry } : null}
                                                    isDisabled
                                                    placeholder="Auto-filled from Enquiry"
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    styles={{
                                                        ...dropdownOptionStyle,
                                                        control: (p) => ({ ...p, minHeight: 38, fontSize: '14px', borderRadius: '0.375rem', borderColor: '#ced4da', boxShadow: 'none' }),
                                                        menuPortal: (base) => ({ ...base, zIndex: 99999 })
                                                    }}
                                                />
                                            )}
                                        </div>
                                    )}

                                    {/* 4. Excel Upload Button */}
                                    {modalMode === 'create' && (
                                        <div className="col-md-4">
                                            {/* <label style={{ ...styles.compactLabel, display: 'block', marginBottom: '4px' }}>Upload Sales File</label>
                                            <button
                                                className="btn btn-outline-dark btn-sm"
                                                style={{ height: 38, display: 'flex', alignItems: 'center', gap: 6 }}
                                                onClick={() => setShowExcelModal(true)}
                                            >
                                                📊 Excel Upload
                                            </button> */}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={styles.tabs}>
                                {(() => {
                                    // Checklist tab removed entirely
                                    let tabs = isReviewOnly ? [] : ['basic', 'product'];

                                    // Discount Review tab — see isDiscountReviewVisible above:
                                    // admin-only, only once the RFQ is submitted, and only when
                                    // at least one line is flagged. Shown before the Review /
                                    // Review for Customer tabs.
                                    if (isDiscountReviewVisible) {
                                        tabs.push('discount_review');
                                    }

                                    // Review tab (internal) only shows when status is NOT Draft and NOT create mode
                                    const showReview = isRfqSubmitted;
                                    if (showReview) {
                                        tabs.push('feasibility');
                                    }

                                    // Review for Customer tab only shows once the RFQ has actually been
                                    // submitted to the customer (i.e. rfq_status carries "Customer" in it:
                                    // 'Submitted to Customer', 'Approved by Customer', 'Rejected by Customer',
                                    // 'Partially Approved by Customer', 'Partially Rejected by Customer').
                                    // Before that point, the 2nd review stage doesn't exist yet.
                                    const showCustomerReview = modalMode !== 'create' && (selectedRfq?.rfq_status || '').includes('Customer');
                                    if (showCustomerReview) {
                                        tabs.push('feasibility_customer');
                                    }

                                    // A Review-only role never gets Basic/Product/Docs/Change Log —
                                    // just whichever review tab(s) apply to this record's status.
                                    if (!isReviewOnly) {
                                        tabs.push('docs');

                                        if (modalMode !== 'create') {
                                            tabs.push('change_log');
                                        }
                                    }

                                    return tabs.map(t => (
                                        <button
                                            key={t}
                                            className={`btn ${activeTab === t ? "btn-secondary" : "btn-light"}`}
                                            onClick={() => setActiveTab(t)}
                                            style={styles.compactTabBtn}
                                        >
                                            {t === 'change_log' ? 'Change Log' : t === 'feasibility' ? 'Review' : t === 'feasibility_customer' ? 'Review for Customer' : t === 'discount_review' ? 'Discount Review' : t.charAt(0).toUpperCase() + t.slice(1)}
                                        </button>
                                    ));
                                })()}
                            </div>

                            {activeTab === "basic" && !isReviewOnly && (
                                <div className="row g-3">
                                    {/* Payment Terms — dropdown driven by the Payment Terms
                                        configuration (Product Configuration screen) instead
                                        of a hardcoded list. */}
                                    <div className="col-md-8">
                                        <label style={styles.compactLabel}>Payment Terms <span style={{ color: 'red' }}>*</span></label>
                                        {modalMode === 'view' && !isFieldVisible('payment_terms') ? (
                                            <MaskedValue />
                                        ) : (
                                        <select
                                            className="form-control"
                                            style={{ ...styles.compactInput }}
                                            value={paymentTerms}
                                            onChange={(e) => setPaymentTerms(e.target.value)}
                                            disabled={modalMode === 'view'}
                                        >
                                            <option value="">-- Select payment terms --</option>
                                            {/* Auto-filled value from the customer record that doesn't match
                                                any of the configured options — shown so it's visible instead
                                                of silently blank. Picking a configured option below replaces it. */}
                                            {paymentTerms && !paymentTermsOptions.includes(paymentTerms) && (
                                                <option value={paymentTerms}>{paymentTerms}</option>
                                            )}
                                            {paymentTermsOptions.map((opt, i) => (
                                                <option key={i} value={opt}>{i + 1}. {opt}</option>
                                            ))}
                                        </select>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === "product" && !isReviewOnly && (
                                <div>
                                    {(modalMode === 'create' || modalMode === 'edit') ? (
                                        <div>
                                            {/* Add Product button row */}
                                            <div className="d-flex justify-content-end mb-2">
                                                <button className="btn btn-sm btn-dark" onClick={modalMode === 'create' ? handleAddRowCreate : handleAddRowEdit}>+ Add Product</button>
                                            </div>
                                            {/* Inline filter row — always visible, no modal */}
                                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}></span>
                                                    {[
                                                        { key: 'category', opts: filterMasterData.categories, label: 'Category' },
                                                        { key: 'cableType', opts: filterMasterData.cableTypes, label: 'Cable Type' },
                                                        { key: 'code', opts: filterMasterData.codes, label: 'Code' },
                                                        { key: 'color', opts: filterMasterData.colours, label: 'Color' },
                                                        { key: 'noOfCores', opts: filterMasterData.cores, label: 'Cores' },
                                                        { key: 'nominalCrossSection', opts: filterMasterData.nominalCrossSections, label: 'Cross Section' },
                                                        { key: 'referenceOfStandard', opts: filterMasterData.referenceOdStandards, label: 'Std. Ref' },
                                                        { key: 'voltage', opts: filterMasterData.voltages, label: 'Voltage' },
                                                        { key: 'construction', opts: filterMasterData.constructions, label: 'Construction' },
                                                    ].map(({ key, opts, label }) => (
                                                        <div
                                                            key={key}
                                                            title={
                                                                globalFilters[key] && globalFilters[key].length > 0
                                                                    ? globalFilters[key].map(o => o.label).join(', ')
                                                                    : undefined
                                                            }
                                                            style={{ minWidth: 120, flex: '1 1 110px', maxWidth: 180, display: 'flex', flexDirection: 'column', gap: 3 }}
                                                        >
                                                            <span style={{
                                                                fontSize: 11,
                                                                fontWeight: 600,
                                                                color: '#64748b',
                                                                whiteSpace: 'nowrap',
                                                                letterSpacing: '0.02em',
                                                                paddingLeft: 2
                                                            }}>
                                                                {label}
                                                            </span>
                                                            <Select
                                                                options={getFilterOptions(opts)}
                                                                value={globalFilters[key] && globalFilters[key].length > 0 ? globalFilters[key] : null}
                                                                isMulti
                                                                closeMenuOnSelect={false}
                                                                hideSelectedOptions={false}
                                                                formatOptionLabel={(opt) => (
                                                                    <span title={opt.label}>{opt.label}</span>
                                                                )}
                                                                onChange={selectedOpts => {
                                                                    const selected = selectedOpts || [];
                                                                    const updated = { ...globalFilters, [key]: selected };
                                                                    setGlobalFilters(updated);
                                                                    const anyActive = Object.values(updated).some(v => Array.isArray(v) ? v.length > 0 : v !== '');
                                                                    if (!anyActive) { setFilteredProductList(null); return; }
                                                                    const matchesFilter = (productVal, selectedVals) => {
                                                                        if (!selectedVals || selectedVals.length === 0) return true;
                                                                        return selectedVals.some(s => String(productVal || '').trim().toLowerCase() === s.value.toLowerCase());
                                                                    };
                                                                    setFilteredProductList(productList.filter(p =>
                                                                        matchesFilter(p.construction, updated.construction) &&
                                                                        matchesFilter(p.code, updated.code) &&
                                                                        matchesFilter(p.cableType, updated.cableType) &&
                                                                        matchesFilter(p.category, updated.category) &&
                                                                        matchesFilter(p.noOfCores, updated.noOfCores) &&
                                                                        matchesFilter(p.color, updated.color) &&
                                                                        matchesFilter(p.nominalCrossSection, updated.nominalCrossSection) &&
                                                                        matchesFilter(p.referenceOfStandard, updated.referenceOfStandard) &&
                                                                        matchesFilter(p.voltage, updated.voltage)
                                                                    ));
                                                                }}
                                                                isClearable
                                                                placeholder={label}
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                                styles={{
                                                                    control: (base) => ({ ...base, minHeight: 30, fontSize: '12px' }),
                                                                    valueContainer: (base) => ({ ...base, padding: '2px 6px', flexWrap: 'wrap' }),
                                                                    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: '12px' }),
                                                                    indicatorsContainer: (base) => ({ ...base, alignSelf: 'flex-start', paddingTop: 2 }),
                                                                    dropdownIndicator: (base) => ({ ...base, padding: 2 }),
                                                                    clearIndicator: (base) => ({ ...base, padding: 2 }),
                                                                    placeholder: (base) => ({ ...base, fontSize: '12px', color: '#94a3b8', fontWeight: 400 }),
                                                                    option: (base, state) => ({
                                                                        ...base,
                                                                        fontSize: '12px',
                                                                        fontWeight: 400,
                                                                        color: state.isSelected ? '#fff' : '#334155',
                                                                        backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : '#fff',
                                                                        padding: '6px 10px',
                                                                    }),
                                                                    multiValue: (base) => ({ ...base, fontSize: '11px', backgroundColor: '#e2e8f0', borderRadius: 4 }),
                                                                    multiValueLabel: (base) => ({ ...base, fontSize: '11px', color: '#334155', padding: '1px 4px' }),
                                                                    multiValueRemove: (base) => ({ ...base, color: '#64748b', ':hover': { backgroundColor: '#cbd5e1', color: '#1e293b' } }),
                                                                    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                                                                    menu: (base) => ({ ...base, fontSize: '12px' }),
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                    {filteredProductList && (
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary"
                                                            style={{ fontSize: 11, whiteSpace: 'nowrap', height: 30, padding: '0 10px' }}
                                                            onClick={clearGlobalFilters}
                                                        >
                                                            ✕ Clear ({filteredProductList.length})
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                    <div style={{ ...styles.tableContainer, padding: 0, overflowX: 'auto' }}>
                                        <table className="table" style={styles.erpTable}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#f8fafc' }}>
                                                    <th style={{ ...styles.erpTh, width: '40px' }}>Item</th>
                                                    <th style={{ ...styles.erpTh, minWidth: '150px' }}>Product Code</th>
                                                    <th style={{ ...styles.erpTh, minWidth: '220px', whiteSpace: 'nowrap' }}>Description</th>
                                                    <th style={styles.erpTh}>Fast Entry</th>
                                                    <th style={styles.erpTh}>UOM</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Qty</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Tol.Range</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Currency</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Unit Price</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Spl. Price</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Total Price</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Disc (%)</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Markup (%)</th>

                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Net Price</th>

                                                    {modalMode !== 'create' && (
                                                        <>
                                                            {/* 🆕 One column per tax type actually configured across
                                                                this quotation's lines (liveTotals.taxColumns, computed
                                                                in the useEffect above) — 3 for a normal domestic
                                                                quotation, more for Export or any HSN with extra duties,
                                                                instead of a fixed CGST/SGST/IGST set. */}
                                                            {(liveTotals.taxColumns && liveTotals.taxColumns.length ? liveTotals.taxColumns : ['CGST', 'SGST', 'IGST']).map((taxType) => (
                                                                <th key={taxType} style={{ ...styles.erpTh, textAlign: 'right' }}>{taxType}</th>
                                                            ))}
                                                            <th style={{ ...styles.erpTh, textAlign: 'right' }}>Total GST</th>
                                                            <th style={{ ...styles.erpTh, textAlign: 'right' }}>Grand Total</th>
                                                        </>
                                                    )}

                                                    <th style={{ ...styles.erpTh, minWidth: '150px' }}>Req. Date <span style={{ color: 'red' }}>*</span></th>
                                                    <th style={{ ...styles.erpTh, minWidth: '100px', textAlign: 'right' }}>Min. Order Qty</th>

                                                    {modalMode !== 'create' && <th style={styles.erpTh}></th>}
                                                    {(modalMode === 'create' || modalMode === 'edit') && <th style={styles.erpTh}></th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    const rawTableData = modalMode === 'create' ? salesData : selectedRfq?.productDetails || [];

                                                    // Once the RFQ has reached a customer-approval stage ('Approved by
                                                    // Customer' or 'Partially Approved by Customer'), the Product tab
                                                    // should only list items the customer actually approved. This is
                                                    // VIEW-mode only — in Edit mode, row index is how saves are wired
                                                    // back to specific line items (handleFieldChangeEdit(idx, ...)),
                                                    // so hiding rows there would misalign those saves onto the wrong
                                                    // items. Read-only view has no such risk.
                                                    const isCustomerApprovedStage = modalMode === 'view'
                                                        && /Approved by Customer/i.test(selectedRfq?.rfq_status || '');

                                                    // Keep each row's original array index alongside it (rather than
                                                    // filtering the plain array) so liveRowCalcs[idx] below still
                                                    // points at the correct row's GST/price figures after filtering.
                                                    const indexedTableData = rawTableData.map((row, originalIdx) => ({ row, originalIdx }));
                                                    const tableData = isCustomerApprovedStage
                                                        ? indexedTableData.filter(({ row }) => (row.customer_approval_status || '').trim().toLowerCase() === 'approved')
                                                        : indexedTableData;

                                                    // Totals now come from the live useEffect above (markup applied on
                                                    // Unit Price, not on Net Price). Kept as `totals` so nothing below
                                                    // this line needs to change.
                                                    const totals = liveTotals;

                                                    return (
                                                        <>
                                                            {tableData.map(({ row, originalIdx }, displayIdx) => {
                                                                const idx = originalIdx; // preserve original index for liveRowCalcs / handlers
                                                                const code = row.product_code || "";
                                                                const desc =
                                                                    row.Description ||
                                                                    row.description ||
                                                                    "";

                                                                const pClass =
                                                                    row.Product_Class ||
                                                                    row.product_class ||
                                                                    "";

                                                                const uom =
                                                                    row.UOM ||
                                                                    row.uom ||
                                                                    "";
                                                                // USE THE HELPER FUNCTION HERE
                                                                // `date` stays "YYYY-MM-DD" because the native <input type="date">
                                                                // requires that exact format; the read-only view uses formatDateLocal()
                                                                // separately to show DD-MM-YYYY.
                                                                const rawReqDate = row.Requirement_Day || row.requirement_date || '';
                                                                const date = rawReqDate ? String(rawReqDate).substring(0, 10) : '';
                                                                const moq = row.Min_Order_Qty ?? row.minimum_order_quantity ?? '';
                                                                const disc = modalMode === 'create' ? (discounts[idx] ?? "") : row.discount;

                                                                // Raw entered unit price (still the value bound to the
                                                                // editable Unit Price input below — unaffected by markup,
                                                                // so typing doesn't compound on every render).
                                                                const unitPrice = parseFloat(row.unit_price || row.Standard_Price || 0);
                                                                const quantity = parseFloat(row.quantity || row.Quantity || 0);

                                                                // ── Live calculations (from useEffect above) ──
                                                                // Markup is applied on Unit Price -> newUnitPrice, and every
                                                                // figure below (Total Price, Discounted Price, Net Price,
                                                                // GST, Grand Total) is derived from that new unit price.
                                                                const calc = liveRowCalcs[idx] || {};
                                                                const newUnitPrice = calc.newUnitPrice ?? unitPrice;
                                                                // 🆕 Spl. Price — the editable base Markup (%)/Discount (%)
                                                                // actually apply to. Starts out equal to Unit Price (raw)
                                                                // until the user overrides it. calc.splBase (from the live
                                                                // useEffect above) already resolves the right value for
                                                                // both Create mode (splPrices[idx]) and Edit/View mode
                                                                // (row.spl_price, with the legacy-row fallback).
                                                                const splPriceDisplay = calc.splBase ?? unitPrice;
                                                                const finalSplPrice = calc.finalUnitPrice ?? splPriceDisplay;
                                                                const grossPrice = calc.grossPrice ?? (unitPrice * quantity);
                                                                const discountedPriceRow = calc.discountedPriceRow ?? 0;
                                                                const displayNetPrice = calc.displayNetPrice ?? 0;
                                                                const displayDiscPrice = discountedPriceRow;

                                                                const rowTaxBreakdown = calc.taxBreakdown || {};
                                                                const totalGstRow = calc.totalGstRow ?? 0;
                                                                const grandTotalRow = calc.grandTotalRow ?? 0;
                                                                // Same column set as the header (falls back to the
                                                                // classic 3 while liveTotals hasn't resolved yet).
                                                                const effectiveTaxColumns = (liveTotals.taxColumns && liveTotals.taxColumns.length)
                                                                    ? liveTotals.taxColumns
                                                                    : ['CGST', 'SGST', 'IGST'];

                                                                // ── Currency: header-level field, same for all rows ──
                                                                const rowCurrency = row.Currency || row.currency || currency || "";

                                                                const isEditable = modalMode === 'create' || modalMode === 'edit';
                                                                // A Change-role's per-field lock only applies once the RFQ
                                                                // already exists (edit mode) — Create is all-or-nothing per
                                                                // Role.js, so nothing is locked while creating.
                                                                const isFieldLocked = (fieldId) => modalMode === 'edit' && !isFieldEditable(fieldId);

                                                                return (
                                                                    <tr key={`${idx}-${row.product_code}-${row.Description}`}>
                                                                        <td style={styles.erpTd}>
                                                                            {modalMode === 'view' && !isFieldVisible('item_no') ? <MaskedValue /> : displayIdx + 1}
                                                                        </td>

                                                                        <td style={{ ...styles.erpTd, overflow: 'visible', padding: '4px' }}>
                                                                            {isEditable ? (
                                                                                <CreatableSelect
                                                                                    classNamePrefix="react-select"

                                                                                    options={filteredProductList !== null ? filteredProductList : productList}

                                                                                    isDisabled={isFieldLocked('product_code')}

                                                                                    value={
                                                                                        code
                                                                                            ? {
                                                                                                value: code,
                                                                                                label: desc ? `${code} - ${desc}` : code
                                                                                            }
                                                                                            : null
                                                                                    }

                                                                                    onChange={(selectedOption) => {

                                                                                        const val = selectedOption?.value || "";

                                                                                        if (modalMode === 'create') {

                                                                                            handleProductCodeChangeCreate(
                                                                                                val,
                                                                                                idx
                                                                                            );

                                                                                        } else {

                                                                                            handleFieldChangeEdit(
                                                                                                idx,
                                                                                                'product_code',
                                                                                                val
                                                                                            );
                                                                                        }
                                                                                    }}

                                                                                    placeholder="Select or Type"

                                                                                    isSearchable

                                                                                    isClearable

                                                                                    backspaceRemovesValue={false}

                                                                                    closeMenuOnSelect={false}

                                                                                    blurInputOnSelect={false}

                                                                                    openMenuOnFocus

                                                                                    menuPortalTarget={document.body}

                                                                                    menuPosition="fixed"

                                                                                    styles={{

                                                                                        control: (base) => ({
                                                                                            ...base,
                                                                                            minHeight: '30px',
                                                                                            height: '30px',
                                                                                            fontSize: '13px',
                                                                                            minWidth: '140px'
                                                                                        }),

                                                                                        valueContainer: (base) => ({
                                                                                            ...base,
                                                                                            height: '30px',
                                                                                            padding: '0 8px'
                                                                                        }),

                                                                                        input: (base) => ({
                                                                                            ...base,
                                                                                            margin: 0,
                                                                                            padding: 0
                                                                                        }),

                                                                                        indicatorsContainer: (base) => ({
                                                                                            ...base,
                                                                                            height: '30px'
                                                                                        }),

                                                                                        dropdownIndicator: (base) => ({
                                                                                            ...base,
                                                                                            padding: 2
                                                                                        }),

                                                                                        menuPortal: (base) => ({
                                                                                            ...base,
                                                                                            zIndex: 99999
                                                                                        }),

                                                                                        // Option/menu styling matches the Category/Cores/Color filter dropdowns
                                                                                        option: (base, state) => ({
                                                                                            ...base,
                                                                                            fontSize: '13px',
                                                                                            fontWeight: 400,
                                                                                            color: state.isSelected ? '#fff' : '#334155',
                                                                                            backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : '#fff',
                                                                                            padding: '8px 12px',
                                                                                            cursor: 'pointer',
                                                                                        }),

                                                                                        menu: (base) => ({
                                                                                            ...base,
                                                                                            fontSize: '13px'
                                                                                        })
                                                                                    }}
                                                                                />
                                                                            ) : (modalMode === 'view' && !isFieldVisible('product_code') ? <MaskedValue /> : code)}
                                                                        </td>

                                                                        <td style={{ ...styles.erpTd, minWidth: '220px', maxWidth: '320px', whiteSpace: 'normal' }}>
                                                                            {modalMode === 'view' && !isFieldVisible('item_description') ? <MaskedValue /> : desc}
                                                                        </td>

                                                                        <td style={styles.erpTd}>
                                                                            {modalMode === 'view' && !isFieldVisible('fast_entry') ? <MaskedValue /> : (
                                                                                <>
                                                                                    {pClass}
                                                                                    <span style={{ cursor: 'pointer', marginLeft: 8, color: '#151616' }} onClick={() => handleOpenCharModal(idx, row, modalMode === 'create')}>▶</span>
                                                                                </>
                                                                            )}
                                                                        </td>

                                                                        <td style={styles.erpTd}>
                                                                            {modalMode === 'view' && !isFieldVisible('uom') ? <MaskedValue /> : uom}
                                                                        </td>
                                                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                            {modalMode === 'edit' ? (
                                                                                <input type="number" className="form-control" style={{ ...styles.compactInput, width: 60, backgroundColor: '#e9ecef', textAlign: 'right' }}
                                                                                    value={quantity} disabled={true} title="Edit Characteristics to change Quantity" />
                                                                            ) : (modalMode === 'view' && !isFieldVisible('qty') ? <MaskedValue /> : quantity)}
                                                                        </td>

                                                                        {/* Range — read-only, calculated from Over/Under Tolerances via Characteristics (▶) */}
                                                                        <td style={{ ...styles.erpTd, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                            {modalMode === 'view' && !isFieldVisible('tol_range') ? <MaskedValue /> : (
                                                                                <span style={{ fontSize: 12, fontWeight: 600, color: row.range ? '#16a34a' : '#94a3b8', whiteSpace: 'nowrap' }}>
                                                                                    {row.range || '—'}
                                                                                </span>
                                                                            )}
                                                                        </td>

                                                                        <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                                                            {modalMode === 'view' && !isFieldVisible('currency') ? <MaskedValue /> : rowCurrency}
                                                                        </td>

                                                                        {/* Unit Price — now a plain, read-only reference value
                                                                            (never recalculated by Markup/Discount). Spl. Price,
                                                                            right beside it, is the editable base those apply to. */}
                                                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                            {isEditable ? (
                                                                                <input type="number" className="form-control" style={{ ...styles.compactInput, width: 80, backgroundColor: '#e9ecef', textAlign: 'right' }}
                                                                                    value={unitPrice} disabled={true} title="Unit Price is read-only" />
                                                                            ) : (
                                                                                modalMode === 'view' && !isFieldVisible('unit_price') ? <MaskedValue /> : formatCurrency(unitPrice)
                                                                            )}
                                                                        </td>

                                                                        {/* 🆕 Spl. Price — the price per unit AFTER Markup (%) and Discount (%).
                                                                            It recalculates whenever markup/discount change, and it can also be typed
                                                                            directly: the typed figure is treated as the final unit price and the
                                                                            pre-markup base (what gets saved as spl_price) is worked back from it.
                                                                            Net Price = Spl. Price x Qty. */}
                                                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                            {isEditable ? (() => {
                                                                                const splFactor = (1 + (calc.rowMarkupNum || 0) / 100) * (1 - (calc.discountVal || 0) / 100);
                                                                                const splShown = Number((finalSplPrice || 0).toFixed(2));
                                                                                const splEditingThis = splEdit && splEdit.idx === idx;
                                                                                return (
                                                                                    <input type="number" className="form-control no-spinner" style={{ ...styles.compactInput, width: 80, textAlign: 'right', MozAppearance: 'textfield' }}
                                                                                        disabled={isFieldLocked('unit_price')}
                                                                                        title="Spl. Price (after Markup and Discount). Type a price to set it directly."
                                                                                        value={splEditingThis ? splEdit.text : splShown}
                                                                                        onFocus={() => setSplEdit({ idx, text: String(splShown) })}
                                                                                        onBlur={() => setSplEdit(null)}
                                                                                        onChange={(e) => {
                                                                                            const text = e.target.value;
                                                                                            setSplEdit({ idx, text });
                                                                                            const typed = parseFloat(text);
                                                                                            // Work the pre-markup base back out of the typed final price
                                                                                            // (kept to 6 decimals so Net Price doesn't drift on big quantities).
                                                                                            const baseVal = (text === "" || isNaN(typed))
                                                                                                ? ""
                                                                                                : String(Math.round((splFactor > 0 ? typed / splFactor : typed) * 1e6) / 1e6);
                                                                                            if (modalMode === 'create') {
                                                                                                const updatedSplPrices = [...splPrices];
                                                                                                updatedSplPrices[idx] = baseVal;
                                                                                                setSplPrices(updatedSplPrices);
                                                                                            } else {
                                                                                                handleFieldChangeEdit(idx, 'spl_price', baseVal === "" ? 0 : baseVal);
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                );
                                                                            })() : (
                                                                                modalMode === 'view' && !isFieldVisible('unit_price') ? <MaskedValue /> : formatCurrency(finalSplPrice)
                                                                            )}
                                                                        </td>

                                                                        <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 500 }}>
                                                                            {modalMode === 'view' && !isFieldVisible('total_price') ? <MaskedValue /> : (
                                                                                modalMode === 'create'
                                                                                    ? discountedPriceRow.toLocaleString('en-IN', { maximumFractionDigits: 2 })
                                                                                    : grossPrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })
                                                                            )}
                                                                        </td>

                                                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                            {isEditable ? (
                                                                                <input type="number" className="form-control no-spinner" style={{ ...styles.compactInput, width: 60, textAlign: 'right', MozAppearance: 'textfield' }}
                                                                                    disabled={isFieldLocked('discount_percent')}
                                                                                    value={disc}
                                                                                    onChange={(e) => {
                                                                                        const newVal = e.target.value;
                                                                                        const effectiveRowMarkup = (row.markup !== undefined && row.markup !== null && row.markup !== "")
                                                                                            ? row.markup
                                                                                            : markup;
                                                                                        if (modalMode === 'create') {
                                                                                            const updatedDiscounts = [...discounts];
                                                                                            updatedDiscounts[idx] = newVal === "" ? "" : newVal;
                                                                                            setDiscounts(updatedDiscounts);
                                                                                            checkDiscountVsMarkup(row.product_code, newVal, effectiveRowMarkup);
                                                                                        } else {
                                                                                            handleFieldChangeEdit(idx, 'discount', newVal === "" ? 0 : newVal);
                                                                                            checkDiscountVsMarkup(row.product_code, newVal, effectiveRowMarkup);
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            ) : (modalMode === 'view' && !isFieldVisible('discount_percent') ? <MaskedValue /> : disc)}
                                                                        </td>

                                                                        {/* Markup (%) — defaults from the header Markup (%)
                                                                            field, but can be overridden per product row. A
                                                                            change to the header field re-broadcasts and
                                                                            overwrites every row's value again. */}
                                                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                            {isEditable ? (
                                                                                <input type="number" className="form-control no-spinner" style={{ ...styles.compactInput, width: 60, textAlign: 'right', MozAppearance: 'textfield' }}
                                                                                    disabled={isFieldLocked('line_markup_percent')}
                                                                                    value={(row.markup !== undefined && row.markup !== null && row.markup !== "") ? row.markup : (markup || 0)}
                                                                                    onChange={(e) => {
                                                                                        const newVal = e.target.value;
                                                                                        if (modalMode === 'create') {
                                                                                            setSalesData(prev => prev.map((r, i) => i === idx ? { ...r, markup: newVal } : r));
                                                                                        } else {
                                                                                            handleFieldChangeEdit(idx, 'markup', newVal);
                                                                                        }
                                                                                        checkDiscountVsMarkup(row.product_code, disc, newVal);
                                                                                    }}
                                                                                />
                                                                            ) : (modalMode === 'view' && !isFieldVisible('line_markup_percent') ? <MaskedValue /> : ((row.markup !== undefined && row.markup !== null && row.markup !== "") ? row.markup : (markup ?? '—')))}
                                                                        </td>


                                                                        {/* Net Price — live, in every mode, so
                                                                            Discount (%) has somewhere visible to land immediately,
                                                                            the same way Markup (%) already updates Unit Price live. */}
                                                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                            {modalMode === 'view' && !isFieldVisible('net_price') ? <MaskedValue /> : formatCurrency(displayNetPrice)}
                                                                        </td>

                                                                        {/* --- GST CELLS (Only in View/Edit Mode — rate isn't resolved until Save) --- */}
                                                                        {/* 🆕 One cell per column in effectiveTaxColumns — this
                                                                            row's amount for that tax type (from row.taxes /
                                                                            rowTaxBreakdown), or 0 if this line's HSN doesn't
                                                                            use that particular type at all. */}
                                                                        {modalMode !== 'create' && (
                                                                            <>
                                                                                {effectiveTaxColumns.map((taxType) => (
                                                                                    <td key={taxType} style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                                        {modalMode === 'view' && !isFieldVisible(taxType.toLowerCase()) ? <MaskedValue /> : formatCurrency(rowTaxBreakdown[taxType] || 0)}
                                                                                    </td>
                                                                                ))}
                                                                                <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                                    {modalMode === 'view' && !isFieldVisible('total_gst') ? <MaskedValue /> : formatCurrency(totalGstRow)}
                                                                                </td>
                                                                                <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                                    {modalMode === 'view' && !isFieldVisible('grand_total') ? <MaskedValue /> : formatCurrency(grandTotalRow)}
                                                                                </td>
                                                                            </>
                                                                        )}

                                                                        <td style={styles.erpTd}>
                                                                            {isEditable ? (
                                                                                <DatePicker
                                                                                    className="form-control"
                                                                                    wrapperClassName="w-100"
                                                                                    style={{ ...styles.compactInput, width: '140px', minWidth: '140px' }}
                                                                                    dateFormat="dd/MM/yyyy"
                                                                                    placeholderText="dd/mm/yyyy"
                                                                                    disabled={isFieldLocked('req_date')}
                                                                                    selected={date ? new Date(date) : null}
                                                                                    onChange={(selectedDate) => {
                                                                                        let newVal = '';
                                                                                        if (selectedDate) {
                                                                                            const yyyy = selectedDate.getFullYear();
                                                                                            const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
                                                                                            const dd = String(selectedDate.getDate()).padStart(2, '0');
                                                                                            newVal = `${yyyy}-${mm}-${dd}`;
                                                                                        }
                                                                                        if (modalMode === 'create') {
                                                                                            const updatedData = [...salesData];
                                                                                            updatedData[idx] = {

                                                                                                ...updatedData[idx],

                                                                                                Requirement_Day: newVal,

                                                                                                requirement_date: newVal
                                                                                            }; setSalesData(updatedData);
                                                                                        } else {
                                                                                            handleFieldChangeEdit(idx, 'requirement_date', newVal);
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            ) : (modalMode === 'view' && !isFieldVisible('req_date') ? <MaskedValue /> : (date ? formatDateLocal(date) : '-'))}
                                                                        </td>

                                                                        <td style={styles.erpTd}>
                                                                            {isEditable ? (
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    className="form-control"
                                                                                    style={{ ...styles.compactInput, minWidth: '90px', textAlign: 'right' }}
                                                                                    disabled={isFieldLocked('min_order_qty')}
                                                                                    value={moq}
                                                                                    onChange={(e) => {
                                                                                        const newVal = e.target.value;
                                                                                        if (modalMode === 'create') {
                                                                                            const updatedData = [...salesData];
                                                                                            updatedData[idx] = {
                                                                                                ...updatedData[idx],
                                                                                                Min_Order_Qty: newVal,
                                                                                                minimum_order_quantity: newVal
                                                                                            };
                                                                                            setSalesData(updatedData);
                                                                                        } else {
                                                                                            handleFieldChangeEdit(idx, 'minimum_order_quantity', newVal);
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            ) : (modalMode === 'view' && !isFieldVisible('min_order_qty') ? <MaskedValue /> : (moq !== '' ? moq : '-'))}
                                                                        </td>

                                                                        {modalMode !== 'create' && (
                                                                            <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                                                                <button className="btn btn-sm btn-outline-dark" style={{ padding: '2px 8px', fontSize: '11px' }} onClick={() => handleViewProductDetails(row.id)}>View</button>
                                                                            </td>
                                                                        )}

                                                                        {isEditable && (
                                                                            <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                                                                <button
                                                                                    onClick={() => handleRemoveRow(idx)}
                                                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4, borderRadius: 4, transition: 'background 0.15s' }}
                                                                                    title="Remove row"
                                                                                    onMouseEnter={e => e.currentTarget.style.background = '#fee2e2'}
                                                                                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                                                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                                                                                        <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
                                                                                    </svg>
                                                                                </button>
                                                                            </td>
                                                                        )}
                                                                    </tr>
                                                                );
                                                            })}

                                                            {/* --- SUMMARY ROW --- */}
                                                            {tableData.length > 0 && (
                                                                <tr style={{ backgroundColor: '#f1f5f9', borderTop: '2px solid #e2e8f0', fontWeight: '700' }}>
                                                                    <td colSpan={9} style={{ ...styles.erpTd, textAlign: 'right', color: '#64748b' }}>TOTAL:</td>

                                                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                        {modalMode === 'create'
                                                                            ? formatCurrency(totals.discounted)
                                                                            : formatCurrency(totals.gross)
                                                                        }
                                                                    </td>

                                                                    {/* Removed the '-' here, keeping the cell for alignment (Disc %) */}
                                                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}></td>
                                                                    {/* Markup (%) — no meaningful total, kept blank for alignment */}
                                                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}></td>

                                                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.net)}</td>

                                                                    {/* GST Totals (Only in View/Edit Mode) */}
                                                                    {/* 🆕 Same dynamic column set as the header/rows above,
                                                                        so a TOTALS cell always lines up under its own column. */}
                                                                    {modalMode !== 'create' && (
                                                                        <>
                                                                            {(liveTotals.taxColumns && liveTotals.taxColumns.length ? liveTotals.taxColumns : ['CGST', 'SGST', 'IGST']).map((taxType) => (
                                                                                <td key={taxType} style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.taxByType?.[taxType] || 0)}</td>
                                                                            ))}
                                                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.totalGst)}</td>
                                                                            <td style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a', fontSize: '14px' }}>{formatCurrency(totals.grand)}</td>
                                                                        </>
                                                                    )}

                                                    <td colSpan={modalMode === 'edit' ? 4 : 3}></td>
                                                                </tr>
                                                            )}

                                                            {tableData.length === 0 && <tr><td colSpan="17" className="text-center p-3 text-muted">No items added</td></tr>}
                                                        </>
                                                    );
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {activeTab === "docs" && !isReviewOnly && (
                                <div
                                    style={{
                                        background: "#fff",
                                        borderRadius: 8,
                                        padding: 16,
                                        border: "1px solid #e2e8f0",
                                        boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
                                    }}
                                >

                                    {/* QUOTATION TEMPLATE */}
                                    {rfqId && (modalMode !== 'view' || isFieldVisible('quotation_pdf')) && (
                                        <div
                                            style={{
                                                marginBottom: "16px",
                                                padding: "15px",
                                                border: "1px solid #d1d5db",
                                                borderRadius: "8px",
                                                background: "#f8fafc"
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: "15px",
                                                    fontWeight: "600",
                                                    color: "#000",
                                                    marginBottom: "12px"
                                                }}
                                            >
                                                {rfqId}_Quotation.pdf
                                            </div>

                                            {/* ACTION BUTTONS */}
                                            <div style={{ display: "flex", gap: "8px", marginBottom: "4px" }}>

                                                {/* VIEW — opens popup modal */}
                                                <button
                                                    onClick={() => setShowQuotationModal(true)}
                                                    style={{
                                                        padding: "6px 14px",
                                                        fontSize: "13px",
                                                        fontWeight: "500",
                                                        background: "#000",
                                                        color: "#fff",
                                                        border: "none",
                                                        borderRadius: "6px",
                                                        cursor: "pointer"
                                                    }}
                                                >
                                                    View
                                                </button>

                                                {/* DOWNLOAD — hidden iframe, no tab switch — uses the real QuotationView so it exactly matches what's shown in View */}
                                                <button
                                                    onClick={() => {
                                                        // NOTE: display:none takes the iframe out of the render
                                                        // tree entirely, so html2canvas (which runs inside this
                                                        // iframe via QuotationView) captures nothing and the PDF
                                                        // comes out blank. Position it off-screen instead so it
                                                        // stays laid out/painted but invisible to the user.
                                                        const iframe = document.createElement("iframe");
                                                        iframe.style.position = "fixed";
                                                        iframe.style.top = "0";
                                                        iframe.style.left = "-10000px";
                                                        iframe.style.width = "1050px";
                                                        iframe.style.height = "2000px";
                                                        iframe.style.border = "none";
                                                        iframe.style.visibility = "hidden";
                                                        iframe.src = "/quotation/" + rfqId + "?download=true";
                                                        document.body.appendChild(iframe);
                                                        setTimeout(() => {
                                                            document.body.removeChild(iframe);
                                                        }, 10000);
                                                    }}
                                                    style={{
                                                        padding: "6px 14px",
                                                        fontSize: "13px",
                                                        fontWeight: "500",
                                                        background: "#000",
                                                        color: "#fff",
                                                        border: "none",
                                                        borderRadius: "6px",
                                                        cursor: "pointer"
                                                    }}
                                                >
                                                    Download
                                                </button>

                                                {/* PRINT — hidden iframe, no tab switch */}
                                                <button
                                                    onClick={() => {
                                                        const iframe = document.createElement("iframe");
                                                        iframe.style.position = "fixed";
                                                        iframe.style.top = "0";
                                                        iframe.style.left = "-10000px";
                                                        iframe.style.width = "1050px";
                                                        iframe.style.height = "2000px";
                                                        iframe.style.border = "none";
                                                        iframe.style.visibility = "hidden";
                                                        iframe.src = "/quotation/" + rfqId + "?print=true";
                                                        document.body.appendChild(iframe);
                                                        setTimeout(() => {
                                                            document.body.removeChild(iframe);
                                                        }, 10000);
                                                    }}
                                                    style={{
                                                        padding: "6px 14px",
                                                        fontSize: "13px",
                                                        fontWeight: "500",
                                                        background: "#000",
                                                        color: "#fff",
                                                        border: "none",
                                                        borderRadius: "6px",
                                                        cursor: "pointer"
                                                    }}
                                                >
                                                    Print
                                                </button>
                                            </div>

                                            {/* VIEW MODAL */}
                                            {showQuotationModal && (
                                                <div
                                                    style={{
                                                        position: "fixed",
                                                        inset: 0,
                                                        background: "rgba(0,0,0,0.5)",
                                                        zIndex: 9999,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center"
                                                    }}
                                                    onClick={(e) => {
                                                        if (e.target === e.currentTarget) setShowQuotationModal(false);
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: "90%",
                                                            height: "90%",
                                                            background: "#fff",
                                                            borderRadius: "8px",
                                                            position: "relative",
                                                            display: "flex",
                                                            flexDirection: "column"
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "space-between",
                                                                padding: "10px 16px",
                                                                borderBottom: "1px solid #e2e8f0"
                                                            }}
                                                        >
                                                            <span style={{ fontWeight: "600", fontSize: "14px" }}>
                                                                {rfqId}_Quotation.pdf
                                                            </span>
                                                            <button
                                                                onClick={() => setShowQuotationModal(false)}
                                                                style={{
                                                                    background: "none",
                                                                    border: "none",
                                                                    fontSize: "18px",
                                                                    cursor: "pointer",
                                                                    color: "#64748b",
                                                                    lineHeight: 1
                                                                }}
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>

                                                        <iframe
                                                            src={"/quotation/" + rfqId}
                                                            title="Quotation"
                                                            width="100%"
                                                            style={{ flex: 1, border: "none", borderRadius: "0 0 8px 8px" }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* GTP DATASHEET(S) — only shown when status is "Submitted to Customer" */}
                                    {(modalMode !== 'view' || isFieldVisible('gtp_datasheet') || isFieldVisible('quotation_pdf')) && gtpDocs.length > 0 && gtpDocs.map((gtp, gtpIdx) => (
                                        <div
                                            key={gtpIdx}
                                            style={{ marginBottom: "16px", padding: "15px", border: "1px solid #d1d5db", borderRadius: "8px", background: "#f8fafc" }}
                                        >
                                            <div style={{ fontSize: "15px", fontWeight: "600", color: "#000", marginBottom: "12px" }}>
                                                {gtpFileName(gtp)}
                                            </div>
                                            <div style={{ display: "flex", gap: "8px" }}>
                                                <button
                                                    onClick={() => setShowGtpPreview(gtp)}
                                                    style={{ padding: "6px 14px", fontSize: "13px", fontWeight: "500", background: "#000", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" }}
                                                >
                                                    View
                                                </button>
                                                <button
                                                    onClick={() => { const doc = buildGtpDatasheetPdf(gtp, { preparedBy: createdBy, approvedBy }); doc.save(gtpFileName(gtp)); }}
                                                    style={{ padding: "6px 14px", fontSize: "13px", fontWeight: "500", background: "#000", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" }}
                                                >
                                                    Download
                                                </button>
                                                <button
                                                    onClick={() => { const doc = buildGtpDatasheetPdf(gtp, { preparedBy: createdBy, approvedBy }); doc.autoPrint(); window.open(doc.output("bloburl", { filename: gtpFileName(gtp) }), "_blank"); }}
                                                    style={{ padding: "6px 14px", fontSize: "13px", fontWeight: "500", background: "#000", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" }}
                                                >
                                                    Print
                                                </button>
                                            </div>
{/* INLINE VIEW POPUP */}
{showGtpPreview?.productCode === gtp.productCode && (
    <div
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10050, display: "flex", alignItems: "center", justifyContent: "center" }}
        onClick={(e) => { if (e.target === e.currentTarget) setShowGtpPreview(null); }}
    >
        <div style={{ width: "90%", maxWidth: "950px", maxHeight: "90vh", background: "#fff", borderRadius: "10px", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 15px 40px rgba(0,0,0,0.25)" }}>
            <div style={{ padding: "12px 18px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc" }}>
                <strong>{gtpFileName(gtp)}</strong>
                <button onClick={() => setShowGtpPreview(null)} style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "24px", background: "#fff" }}>

                {/* Header — logo left, text centered */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                    <img src={sbeeLogo} alt="SBEE" style={{ height: "40px", width: "60px", objectFit: "contain" }} />
                    <div style={{ flex: 1, textAlign: "center" }}>
                        <div style={{ fontSize: "15px", fontWeight: 700, fontFamily: "'Good Times', 'Segoe UI', Calibri, Arial, sans-serif" }}>SBEE CABLES INDIA LTD</div>
                        <div style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", marginTop: "2px" }}>
                            {`GUARANTEED TECHNICAL PARTICULARS FOR ${(gtp.productType || "").toUpperCase()}`}
                        </div>
                    </div>
                </div>

                {/* Product Code / Description bordered bar */}
                <div style={{ border: "1px solid #333", padding: "5px 10px", fontSize: "13px", marginBottom: "0", textAlign: "center" }}>
                    <strong>Product Code:</strong> {gtp.productCode || "-"} &nbsp;&nbsp;
                    <strong>Description:</strong> {gtp.productDescription || "-"}
                </div>

                {/* Characteristics Table */}
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", border: "1px solid #333" }}>
                    <thead>
                     <tr>
  {["S.No", "Parameters", "UOM", "Specified Values"].map(h => (
    <th
      key={h}
      style={{
        border: "1px solid #333",
        padding: "7px 10px",
        background: "#dcdcdc",
        fontWeight: 700,
        textAlign: h === "Specified Values" ? "center" : "left"
      }}
    >
      {h}
    </th>
  ))}
</tr>
                    </thead>
                    <tbody>
                        {(() => {
                            let lastH = null, sectionCounter = 1, subCounter = 0;
                            const rows = [];

                            // GENERAL section (fixed rows)
                            rows.push(
                                <tr key="gen-header">
                                    <td style={{ border: "1px solid #333", padding: "7px 10px", fontWeight: 700 }}>1</td>
                                    <td colSpan={3} style={{ border: "1px solid #333", padding: "7px 10px", fontWeight: 700 }}>GENERAL</td>
                                </tr>
                            );
                            rows.push(
                                <tr key="gen-1">
                                    <td style={{ border: "1px solid #333", padding: "6px 10px" }}>1.1</td>
                                    <td style={{ border: "1px solid #333", padding: "6px 10px" }}>Name & Address of the manufacturer</td>
                                    <td style={{ border: "1px solid #333", padding: "6px 10px" }}></td>
                                    <td style={{ border: "1px solid #333", padding: "6px 10px" }}>{manufacturerAddress}</td>
                                </tr>
                            );
                            rows.push(
                                <tr key="gen-2">
                                    <td style={{ border: "1px solid #333", padding: "6px 10px" }}>1.2</td>
                                    <td style={{ border: "1px solid #333", padding: "6px 10px" }}>Trade mark / Brand Name</td>
                                    <td style={{ border: "1px solid #333", padding: "6px 10px" }}></td>
                                    <td style={{ border: "1px solid #333", padding: "6px 10px", textAlign: "center" }}>
                                        <img src={sbeeLogo} alt="SBEE" style={{ height: "22px" }} />
                                    </td>
                                </tr>
                            );

                            // Dynamic characteristics — only rows that actually have a
                            // specified value are shown; blanks are dropped.
                            const gtpCharsWithValues = (gtp.characteristics || []).filter(
                                (item) => item.standardValue !== undefined && item.standardValue !== null && String(item.standardValue).trim() !== "" && isSalesCharacteristic(item)
                            );
                            gtpCharsWithValues.forEach((item, i) => {
                                if (item.processName !== lastH) {
                                    sectionCounter += 1; subCounter = 0;
                                    rows.push(
                                        <tr key={`gh-${i}`}>
                                            <td style={{ border: "1px solid #333", padding: "7px 10px", fontWeight: 700 }}>{sectionCounter}</td>
                                            <td colSpan={3} style={{ border: "1px solid #333", padding: "7px 10px", fontWeight: 700, textTransform: "uppercase" }}>
                                                {item.processName}
                                            </td>
                                        </tr>
                                    );
                                    lastH = item.processName;
                                }
                                subCounter++;
                                rows.push(
                                    <tr key={`gr-${i}`}>
                                        <td style={{ border: "1px solid #333", padding: "6px 10px", textAlign: "center" }}>{sectionCounter}.{subCounter}</td>
                                        <td style={{ border: "1px solid #333", padding: "6px 10px" }}>{item.characteristic}</td>
                                        <td style={{ border: "1px solid #333", padding: "6px 10px", textAlign: "center" }}>{item.uom || "-"}</td>
                                        <td style={{ border: "1px solid #333", padding: "6px 10px", textAlign: "center" }}>{item.standardValue || ""}</td>
                                    </tr>
                                );
                            });
                            return rows;
                        })()}
                    </tbody>
                </table>

                {/* Footer */}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "50px", fontSize: "13px" }}>
                    <div>
                        <strong>Prepared By:</strong>
                        <span style={{ display: "inline-block", borderBottom: "1px solid #333", width: "150px", marginLeft: "8px" }}>
                            {createdBy || ""}
                        </span>
                    </div>
                    <div>
                        <strong>Approved By:</strong>
                        <span style={{ display: "inline-block", borderBottom: "1px solid #333", width: "150px", marginLeft: "8px" }}>
                            {approvedBy || ""}
                        </span>
                    </div>
                </div>

            </div>
        </div>
    </div>
)}
                                        </div>
                                    ))}

                                    {/* UPLOAD AREA + FILE LIST — hidden entirely for a Display role with Uploaded Files unchecked */}
                                    {(modalMode !== 'view' || isFieldVisible('uploaded_files')) && (
                                        <>
                                    <div style={{ marginBottom: 16 }}>
                                        <label
                                            htmlFor="docFileInput"
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                border: "2px dashed #cbd5e1",
                                                borderRadius: 8,
                                                padding: "18px 12px",
                                                cursor: "pointer",
                                                background: "#f8fafc"
                                            }}
                                        >    
                                            <span style={{ fontSize: 22 }}>📁</span>
                                            <span style={{ fontSize: 13, color: "#475569", fontWeight: 500 }}>
                                                Click to attach documents
                                            </span>
                                            <span style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                                                PDF, Word, Excel, Images, etc. (max 50 KB per file)
                                            </span>
                                            <input
                                                id="docFileInput"
                                                type="file"
                                                ref={docFileInputRef}
                                                multiple
                                                style={{ display: "none" }}
                                                onChange={async (e) => {
                                                    const rawFiles = Array.from(e.target.files);
                                                    e.target.value = "";
                                                    if (!rawFiles.length) return;

                                                    const files = filterFilesWithinSizeLimit(rawFiles);
                                                    if (!files.length) return;

                                                    const targetId = rfqId || "DRAFT";
                                                    const formData = new FormData();
                                                    files.forEach(f => formData.append("docs", f));

                                                    try {
                                                        const res = await fetch(
                                                            ORDER_API + "/quotation/upload-docs/" + targetId,{ headers: authHeaders(), method: "POST", body: formData });
                                                        if (!res.ok) throw new Error(await res.text());

                                                        const result = await res.json();
                                                        const saved = result.files.map(f => ({
                                                            name: f.originalname,
                                                            filename: f.filename,
                                                            googleFileId: f.googleFileId,
                                                            size: f.size,
                                                            type: f.mimetype,
                                                            uploadedAt: formatChangeLogDateTime(new Date()),
                                                            rfqId: targetId
                                                        }));

                                                        setDocFiles(prev => [...prev, ...saved]);
                                                        toast.success(saved.length + " file(s) uploaded successfully");

                                                        if (rfqId) {
                                                            setTimeout(() => {
                                                            }, 500);
                                                        }
                                                    } catch (err) {
                                                        toast.error("File upload failed: " + err.message);
                                                    }
                                                }}
                                            />
                                        </label>
                                    </div>

                                    {/* FILE LIST */}
                                    {docFiles.length === 0 ? (
                                        <div style={{ textAlign: "center", padding: "28px 0", color: "#94a3b8", fontSize: 13 }}>
                                            <div style={{ fontSize: 32, marginBottom: 8 }}></div>
                                            No documents attached yet.
                                        </div>
                                    ) : (
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                            <thead>
                                                <tr>
                                                    <th style={styles.erpTh}>#</th>
                                                    <th style={styles.erpTh}>File Name</th>
                                                    <th style={styles.erpTh}>Type</th>
                                                    <th style={styles.erpTh}>Size</th>
                                                    <th style={styles.erpTh}>Uploaded At</th>
                                                    <th style={styles.erpTh}>Download</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {docFiles.map((doc, idx) => (
                                                    <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                                        <td style={styles.erpTd}>{idx + 1}</td>
                                                        <td style={styles.erpTd}>
                                                            <span style={{ marginRight: 6 }}>{getFileIcon(doc.name, doc.type)}</span>
                                                            <span style={{ color: '#1e293b', fontWeight: 500 }}>{doc.name}</span>
                                                        </td>
                                                        <td style={styles.erpTd}>
                                                            <span style={{
                                                                background: '#f1f5f9', borderRadius: 4, padding: '2px 7px',
                                                                fontSize: 11, color: '#475569', fontWeight: 600
                                                            }}>
                                                                {doc.type
                                                                    ? doc.type.split('/').pop().toUpperCase()
                                                                    : (doc.name?.split('.').pop() || '—').toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td style={styles.erpTd}>
                                                            {doc.size
                                                                ? doc.size > 1024 * 1024
                                                                    ? `${(doc.size / (1024 * 1024)).toFixed(2)} MB`
                                                                    : `${(doc.size / 1024).toFixed(1)} KB`
                                                                : '—'}
                                                        </td>
                                                        <td style={styles.erpTd}>{doc.uploadedAt || '—'}</td>
                                                        <td style={styles.erpTd}>
                                                            <button
                                                                onClick={() => {
                                                                    const link = document.createElement("a");
                                                                    link.href = ORDER_API + "/quotation/" + (doc.rfqId || rfqId) + "/download-doc/" + doc.googleFileId;
                                                                    link.download = doc.name;
                                                                    document.body.appendChild(link);
                                                                    link.click();
                                                                    document.body.removeChild(link);
                                                                }}
                                                                style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                                                    color: '#2563eb', fontWeight: 600, fontSize: 12,
                                                                    textDecoration: 'none', padding: '3px 10px',
                                                                    border: '1px solid #bfdbfe', borderRadius: 5,
                                                                    background: '#eff6ff', cursor: 'pointer'
                                                                }}
                                                            >
                                                                ⬇ Download
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                        </>
                                    )}
                                </div>
                            )}

                            {activeTab === "change_log" && !isReviewOnly && (
                                <div style={styles.tableContainer}>
                                    <table className="table" style={styles.erpTable}>
                                        <thead>
                                            <tr>
                                                <th style={styles.erpTh}>Date & Time</th>
                                                <th style={styles.erpTh}>User</th>
                                                <th style={styles.erpTh}>Action</th>
                                                <th style={styles.erpTh}>Status</th>
                                                <th style={styles.erpTh}>Field</th>
                                                <th style={styles.erpTh}>Previous Value</th>
                                                <th style={styles.erpTh}>Modified Value</th>
                                                <th style={styles.erpTh}>Comment</th> {/* NEW COLUMN HEADER */}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {changeLogs.length > 0 ? changeLogs.map((log) => {
                                                const dateVal = log.created_at;
                                                const userVal = log.created_by || log.updated_by;

                                                return (
                                                    <tr key={log.id}>
                                                        <td style={styles.erpTd}>
                                                            {formatChangeLogDateTime(dateVal)}
                                                        </td>
                                                        <td style={styles.erpTd}>{userVal || "Unknown"}</td>
                                                        <td style={styles.erpTd}>{log.action_type}</td>
                                                        <td style={styles.erpTd}>{log.doc_status}</td>
                                                        <td style={styles.erpTd}>
                                                            {log.field_name
                                                                ? log.field_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                                                : '-'}
                                                        </td>
                                                        <td style={{ ...styles.erpTd, maxWidth: '150px', wordWrap: 'break-word' }}>
                                                            {log.old_value || '-'}
                                                        </td>
                                                        <td style={{ ...styles.erpTd, fontWeight: 600, maxWidth: '150px', wordWrap: 'break-word' }}>
                                                            {log.new_value || '-'}
                                                        </td>
                                                        {/* NEW COLUMN DATA */}
                                                        <td style={{ ...styles.erpTd, maxWidth: '200px' }}>
                                                            {log.comment || '-'}
                                                        </td>
                                                    </tr>
                                                );
                                            }) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center text-muted p-3"> {/* Updated colSpan to 8 */}
                                                        No change logs available
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}


                            {activeTab === "feasibility" && modalMode !== 'create' && (
                                <div className="border rounded p-2">
                                    <Bd productDetails={selectedRfq.productDetails} selectedRfq={selectedRfq} mode="internal" canReview={isAdmin || isReviewOnly || canApprove} onDecisionSubmitted={refreshSelectedRfq} />
                                </div>
                            )}

                            {activeTab === "feasibility_customer" && modalMode !== 'create' && (
                                <div className="border rounded p-2">
                                    <Bd productDetails={selectedRfq.productDetails} selectedRfq={selectedRfq} mode="customer" canReview={isAdmin || isReviewOnly || canApprove} onDecisionSubmitted={refreshSelectedRfq} />
                                </div>
                            )}

                            {activeTab === "discount_review" && isDiscountReviewVisible && (
                                <div className="border rounded p-2">
                                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                                        Showing only line items where Discount (%) exceeds Markup (%).
                                    </div>
                                    <Bd
                                        productDetails={discountReviewRows.map(r => r.row)}
                                        selectedRfq={selectedRfq}
                                        mode="discount"
                                        canReview={canApprove}
                                        onDecisionSubmitted={refreshSelectedRfq}
                                    />
                                </div>
                            )}

                            <CharacteristicsModal
                                modalVisible={modalVisible}
                                selectedCharacteristics={selectedCharacteristics}
                                // Logic: Editable ONLY if (Edit Mode) OR (Create Mode AND No RFQ ID generated yet)
                                isEditing={modalMode === 'edit' || (modalMode === 'create' && !rfqId)}
                                handleChange={(idx, val) => {
                                    // Security check: If view mode or ID generated, block changes
                                    if (modalMode === 'view' || (modalMode === 'create' && rfqId)) {
                                        return;
                                    }

                                    const charName = selectedCharacteristics[idx].characteristic.toLowerCase();
                                    if (charName === 'factor' || charName === 'length') {
                                        if (val !== "") {
                                            if (!/^\d*\.?\d*$/.test(val)) {
                                                toast.info("Only positive numbers allowed.", { autoClose: 2000, hideProgressBar: true });
                                                return;
                                            }
                                        }
                                    }

                                    else if (charName.includes('tolerance')) {
                                        if (val !== "") {
                                            if (!/^\d*\.?\d*$/.test(val)) {
                                                toast.info("Only numeric percentages allowed.", { autoClose: 2000, hideProgressBar: true });
                                                return;
                                            }
                                        }
                                    }

                                    const u = [...selectedCharacteristics];
                                    u[idx].value = val;
                                    setSelectedCharacteristics(u);
                                }}
                                handleToggleTolerance={handleToggleTolerance}
                                onAdd={handleAddDrumGroup}
                                onRemove={handleRemoveDrumGroup}
                                handleClose={() => handleSaveCharModal(selectedCharsRef.current)} />
                        </div>

                        <div style={styles.modalFooter}>
                            {modalMode === 'create' && (
                                <>
                                    <button
  className="btn btn-dark"
  onClick={handleCreateRFQId}
  disabled={loading || rfqId !== ""}
>
  Create
</button>
                                    {/* CHANGE HERE */}
                                    <button className="btn btn-dark" onClick={() => { setActionType(""); setShowCommentModal(true); }} disabled={!rfqId || isSubmitting}>Submit</button>
                                </>
                            )}
                          {modalMode === 'view' && (
    <>
        {!isDisplayOnly && !selectedRfq?.is_converted_to_so && (
            <button
                className="btn btn-dark"
                onClick={handleSwitchToEdit}
            >
                Edit
            </button>
        )}

        {selectedRfq?.is_converted_to_so && (
            <span style={{ marginRight: 8, color: '#6c757d', fontSize: 13, alignSelf: 'center' }}>
                Converted to Sales Order {selectedRfq?.converted_so_id} — locked for editing.
            </span>
        )}

        {(selectedRfq?.rfq_status === 'Approved' || selectedRfq?.rfq_status === 'Partially Approved') && (
            <button className="btn btn-dark" onClick={handleOpenSubmitToCustomer}>
                Submit to Customer
            </button>
        )}

        {selectedRfq?.rfq_status === 'Draft' && (
            <button
                className="btn btn-dark"
                onClick={() => { setActionType(""); setShowCommentModal(true); }}
                disabled={isSubmitting}
            >
                Submit
            </button>
        )}
    </>
)}
                            {modalMode === 'edit' && (
                                <>
                                    <button className="btn btn-dark" onClick={handleSaveEdit}>Save</button>
                                    <button className="btn btn-outline-secondary" onClick={() => setModalMode('view')}>Cancel</button>
                                </>
                            )}
                        </div>

                    </div>
                </div>
            )}

            {showProductModal && productDetails && (
                <div style={styles.modalBackdrop}>
                    <div
                        style={{
                            ...styles.modal,
                            maxWidth: "950px",
                            width: "95%",
                            maxHeight: "85vh",
                            padding: "14px",
                            fontSize: "13px"
                        }}
                    >
                        <div style={{ padding: "12px" }}>
                            <table className="table table-bordered table-sm mb-3">
                                <thead style={{ background: "#f8fafc", fontWeight: 600 }}>
                                    <tr>
                                        <th>Product Code</th>
                                        <th>HSN Code</th>
                                        <th>Quantity</th>
                                        <th>Unit Price</th>
                                        <th>Discount (%)</th>
                                        <th>Total Price</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{productDetails.product_code}</td>
                                        <td>{productDetails.hsnCode}</td>
                                        <td>{productDetails.quantity}</td>
                                        <td>₹ {formatCurrency(productDetails.unit_price)}</td>
                                        <td>{productDetails.discount}</td>
                                        <td style={{ fontWeight: 600 }}>
                                            ₹ {formatCurrency(productDetails.totalPrice)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "8px 10px",
                                    borderTop: "1px solid #999",
                                    borderBottom: "1px solid #999",
                                    marginBottom: "10px",
                                    fontWeight: 600
                                }}
                            >
                                <span>Net Price :</span>
                                <span>₹ {formatCurrency(productDetails.netPrice)}</span>
                            </div>

                            <table
                                className="table table-sm"
                                style={{ width: "50%", marginLeft: "auto" }}
                            >
                                <tbody>
                                    {/* 🆕 One row per tax type actually configured for this
                                        line's HSN (productDetails.taxes, set in
                                        handleViewProductDetails above) instead of a fixed
                                        CGST/SGST/IGST set. */}
                                    {(productDetails.taxes || []).map((t, i) => (
                                        <tr key={t.type || i}>
                                            <td>{t.type} @ {t.percent}%</td>
                                            <td style={{ textAlign: "right" }}>
                                                ₹ {formatCurrency(t.value)}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr style={{ borderTop: "1px solid #000", fontWeight: 600 }}>
                                        <td>Total GST</td>
                                        <td style={{ textAlign: "right" }}>
                                            ₹ {formatCurrency(productDetails.totalGST)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "12px",
                                    marginTop: "8px",
                                    background: "#f1f5f9",
                                    fontWeight: 700,
                                    fontSize: "15px",
                                    borderRadius: "4px"
                                }}
                            >
                                <span>Grand Total :</span>
                                <span>₹ {formatCurrency(productDetails.finalPriceAfterGST)}</span>
                            </div>
                        </div>

                        <div style={{ ...styles.modalFooter, padding: "10px 14px" }}>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setShowProductModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Excel Upload Modal */}
            {showExcelModal && (
                <div style={{ ...styles.modalBackdrop, zIndex: 1060 }}>
                    <div style={{ background: '#fff', borderRadius: '12px', width: '90%', maxWidth: '480px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
                        {/* Header */}
                        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>📊 Excel Upload</div>
                            <button style={styles.iconBtn} onClick={() => setShowExcelModal(false)}>✕</button>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '24px' }}>

                            {/* Step 1 – Download Template */}
                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '16px', marginBottom: '20px' }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#166534', marginBottom: 6 }}>Step 1 — Download Template</div>
                                <p style={{ fontSize: 12, color: '#4b5563', margin: '0 0 12px 0' }}>
                                    Download the template file and fill in your product details. Make sure the column headers match exactly.
                                </p>
                                <button
                                    className="btn btn-sm"
                                    style={{ background: '#16a34a', color: '#fff', fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, border: 'none', borderRadius: 6, padding: '7px 16px' }}
                                    onClick={handleDownloadTemplate}
                                >
                                    ⬇️ Download Template
                                </button>
                            </div>

                            {/* Step 2 – Upload File */}
                            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '16px' }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#1d4ed8', marginBottom: 6 }}>Step 2 — Upload Filled File</div>
                                <p style={{ fontSize: 12, color: '#4b5563', margin: '0 0 12px 0' }}>
                                    Upload the completed Excel or CSV file to import your product line items.
                                </p>

                                {/* Drop zone */}
                                <label
                                    htmlFor="excelFileInput"
                                    style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                        border: '2px dashed #93c5fd', borderRadius: 8, padding: '20px 12px', cursor: 'pointer',
                                        background: salesFile ? '#dbeafe' : '#fff', transition: 'background 0.2s', marginBottom: 12
                                    }}
                                >
                                    <span style={{ fontSize: 28 }}>{salesFile ? '✅' : '📁'}</span>
                                    <span style={{ fontSize: 12, color: '#374151', marginTop: 6, fontWeight: salesFile ? 600 : 400 }}>
                                        {salesFile ? salesFile.name : 'Click to browse or drag & drop'}
                                    </span>
                                    <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>Supports .xls, .xlsx, .csv</span>
                                    <input
                                        id="excelFileInput"
                                        type="file"
                                        ref={salesFileInputRef}
                                        accept=".xls,.xlsx,.csv"
                                        style={{ display: 'none' }}
                                        onChange={(e) => setSalesFile(e.target.files[0])}
                                    />
                                </label>

                                {salesFile && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: '#374151', background: '#fff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '6px 10px' }}>
                                        <span>📄 {salesFile.name}</span>
                                        <button onClick={() => setSalesFile(null)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>✕</button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#f8fafc' }}>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowExcelModal(false)}>Cancel</button>
                            <button
                                className="btn btn-dark btn-sm px-4"
                                onClick={handleSalesFileSave}
                                disabled={!salesFile || excelUploading}
                            >
                                {excelUploading ? 'Uploading...' : 'Upload & Import'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 2. Comment Modal */}
            {showCommentModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "600px", width: "100%" }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600 }}></div>
                            <button style={styles.iconBtn} onClick={() => setShowCommentModal(false)}>✕</button>
                        </div>
                        <div style={styles.modalBody}>
                            <label style={{ ...styles.compactLabel, display: 'block' }}>Submission Comment</label>
                            <textarea
                                value={submitComment}
                                onChange={(e) => setSubmitComment(e.target.value)}
                                placeholder="Enter your comment here"
                                rows="4"
                                style={{
                                    width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", resize: "none"
                                }}
                            />
                        </div>
                        <div style={styles.modalFooter}>
                            <button
                                className="btn btn-outline-secondary"
                                disabled={isSubmitting}
                                onClick={() => setShowCommentModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-dark"
                                disabled={isSubmitting}
                                onClick={() => {
                                    // Dispatch off actionType (set when the triggering button was
                                    // clicked) instead of re-deriving from rfq_status — re-deriving
                                    // broke Submit to Customer for any status other than the exact
                                    // string "Approved" (e.g. "Partially Approved"), silently falling
                                    // through to handleSubmitNewRFQ() instead.
                                    if (actionType === "SUBMIT_TO_CUSTOMER") {
                                        handleCommentSubmit();
                                    }
                                    else if (actionType === "RESUBMIT_EDIT" && modalMode === 'edit' && selectedRfq?.rfq_status === 'Rejected') {
                                        executeUpdateRFQ(submitComment);
                                    }
                                    else {
                                        handleSubmitNewRFQ();
                                    }
                                }}
                            >
                                {isSubmitting ? "Submitting..." : "Submit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===================== DELETE CONFIRM MODAL ===================== */}
            {showDeleteConfirm && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    background: "rgba(0,0,0,0.5)",
                    zIndex: 1070,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                }}>
                    <div style={{
                        background: "#fff",
                        width: "420px",
                        borderRadius: 14,
                        padding: "36px 32px",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                        textAlign: "center",
                        position: "relative"
                    }}>
                        {/* CLOSE */}
                        <button
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setRfqToDelete(null);
                            }}
                            style={{
                                position: "absolute",
                                top: 16,
                                right: 18,
                                background: "transparent",
                                border: "none",
                                fontSize: 18,
                                cursor: "pointer",
                                color: "#94a3b8"
                            }}
                        >
                            ✖
                        </button>

                        {/* ICON BADGE */}
                        <div style={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            background: "#ef4444",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 20px",
                            fontSize: 28,
                            color: "#fff",
                            fontWeight: "bold"
                        }}>
                            🗑
                        </div>

                        {/* TITLE */}
                        <h4 style={{
                            margin: "0 0 12px",
                            fontSize: "19px",
                            fontWeight: 700,
                            color: "#1e293b"
                        }}>
                            Delete RFQ
                        </h4>

                        {/* MESSAGE */}
                        <p style={{
                            fontSize: 14,
                            color: "#64748b",
                            marginBottom: 28,
                            lineHeight: 1.5
                        }}>
                            Delete RFQ "{rfqToDelete}"? This will hide it from the list.
                        </p>

                        {/* ACTIONS */}
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 10
                        }}>
                            <button
                                onClick={confirmDeleteRFQ}
                                style={{
                                    padding: "12px 16px",
                                    borderRadius: 10,
                                    border: "none",
                                    background: "#ef4444",
                                    color: "#fff",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                    fontSize: 14,
                                    width: "100%"
                                }}
                            >
                                Yes, Delete RFQ
                            </button>

                            <button
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setRfqToDelete(null);
                                }}
                                style={{
                                    padding: "10px 16px",
                                    borderRadius: 10,
                                    border: "none",
                                    background: "transparent",
                                    color: "#334155",
                                    cursor: "pointer",
                                    fontWeight: 500,
                                    fontSize: 14,
                                    width: "100%"
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </Layout>
    );
}

const styles = {
    compactLabel: { fontSize: "12px", fontWeight: 600, marginBottom: "2px", color: '#334155' },
    compactInput: { fontSize: "13px", height: "34px", padding: "4px 8px" },
    compactTabBtn: { fontSize: "13px", padding: "4px 10px" },
    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },
    erpTh: { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
    erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
    filterCard: { background: '#ffffff', borderRadius: 8, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 18 },
    filterLabel: { fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#334155' },
    filterSelect: { height: 36, fontSize: 13 },
    pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    pageTitle: { fontSize: 20, fontWeight: 700, color: '#1e293b' },
    pageSubtitle: { fontSize: 12, color: '#64748b' },
    primaryBtn: { paddingLeft: 14, paddingRight: 14 },
    modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '20px' },
    modal: { background: '#fff', borderRadius: '8px', width: '90%', maxWidth: '1200px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    modalHeader: { padding: '15px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    iconBtn: { background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer' },
    modalBody: { padding: '20px', overflowY: 'auto', flex: 1 },
    modalFooter: { padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
    tabs: { display: 'flex', gap: 8, margin: '14px 0' },
};

export default Home;